import { ProactiveBlockedError } from '../features/proactive/types';
import { ColetaError } from '../features/structured-collection/types';

import { engineUrl, tenantSlugFixo } from './runtime-config';

import type {
  SendMessageBody,
  SendMessageEnvelope,
  ApiErrorBody,
  ConfirmIdentityResponse,
  ConversationContactRole,
  EstablishLinkRequest,
  EstablishLinkResponse,
  LinkCandidatesResponse,
  CreateOutboundCampaignRequest,
  CreateOutboundCampaignResult,
  OutboundCampaignStateResponse,
  ListOutboundCampaignsResponse,
  ConversationDetail,
  ConversationsFilters,
  DocumentSummary,
  ImportBaseMapping,
  ImportDefaultChannel,
  ImportResult,
  InboxCounts,
  ListConversationsResponse,
  ListMessagesResponse,
  LoginResponse,
  MeResponse,
  MessageItem,
} from './types';
import type { LivingSummaryBlocks, SuggestionChip } from './websocket/events';
import type {
  CreateActionRiskBody,
  PreviewActionRiskBody,
  PreviewResultView,
  ResolvedCatalogView,
  TenantOverrideView,
  UpdateActionRiskBody,
} from '../features/action-risk-categories/types';
import type {
  WalletContractWire,
  WalletEntriesPageWire,
  WalletSnapshotWire,
} from '../features/admin/ai-billing/types';
import type {
  ApplyAiProfileResponse,
  DynamismScaleResponse,
  DynamismScaleWire,
  FailoverConfigWire,
  ListAgentsResponse,
  ListAiProfilesResponse,
  ListProvidersResponse,
  SetAgentFallbackResponse,
  SingleAgentResponse,
} from '../features/admin/ai-config/types';
import type {
  AuditCascadeResultWire,
  AuditEventWire,
  AuditListFilters,
  AuditListResultWire,
  AuditTimelineFilters,
  AuditTimelineResultWire,
} from '../features/admin/audit/types';
import type {
  AssetKind as BrandingAssetKind,
  SerializedTenantBranding,
  UpdateTenantBrandingRequest,
} from '../features/admin/branding/types';
import type {
  OperationProfileId,
  OperationProfileStateWire,
  SerializedTenantConfig,
  UpdateTenantConfigRequest,
} from '../features/admin/config-tenant/types';
import type {
  ChannelConfigWire,
  ChannelHealthWire,
  ChannelTestWire,
  OnboardingAttemptWire,
  OnboardingAvailabilityWire,
  OnboardingPathWire,
  OnboardingStartWire,
  PopupResultWire,
  RemoteHealthWire,
} from '../features/admin/integrations/channels/types';
import type {
  CreateMcpConnectorInput,
  IntegrationMetricsWire,
  McpConnectorWire,
  ReconciliationFlagWire,
  ReconciliationRunResultWire,
  ResolveReconciliationFlagInput,
  RotateCredentialsInput,
  UpdateMcpConnectorInput,
} from '../features/admin/integrations/types';
import type {
  CreateKnowledgeEntryBody,
  KnowledgeEntryDto,
  ListKnowledgeEntriesResponse,
  UpdateKnowledgeEntryBody,
} from '../features/admin/knowledge-base/types';
import type { InvitePreviewWire, PasswordResetPreviewWire } from '../features/admin/users/types';
import type {
  BondTypeConfigView,
  UpdateBondTypeConfigBody,
} from '../features/bond-type-configs/types';
import type { ConductProfileResolution } from '../features/conduct/conduct-types';
import type { ConversationSupervision } from '../features/conversations/supervision/types';
import type {
  BroadcastBody,
  BroadcastResult,
  ContextPeekResponse,
  InitiateThreadBody,
  InitiateThreadResult,
  MarkReadResult,
  PinResult,
  PinSummary,
  PinTargetBody,
  PostMessageBody,
  PostMessageResult,
  RecipientScope,
  ThreadDetail,
  ThreadSummary,
  UnpinResult,
  UnreadCountResult,
} from '../features/direct-messages/types';
import type {
  AnonymizationWireItem as AnonymizationFullWireItem,
  AnonymizationPreviewPlan,
  CompleteAnonymizationRequest,
  CreateAnonymizationRequest,
  PreviewAnonymizationRequest,
} from '../features/dpo/anonimizacao/types';
import type {
  LgpdRequestWireItem,
  ListLgpdRequestsResponse,
  AnonymizationWireItem,
  LegalHoldWireItem,
  ListLegalHoldsResponse,
  AuditCascadeResponse,
} from '../features/dpo/centro-direitos/types';
import type {
  CreateGuardianshipRequest,
  GuardianshipWireItem,
  RevokeGuardianshipRequest,
} from '../features/dpo/guardianship/types';
import type {
  GenerateLgpdReportRequest,
  GeneratedLgpdReport,
  LgpdReportTypeDescriptor,
} from '../features/dpo/relatorios/types';
import type {
  CreateTemplateBody,
  TemplateDto,
  TemplateListParams,
  UpdateTemplateBody,
} from '../features/message-templates/types';
import type {
  CreatePersonaBody,
  PersonaDto,
  PersonaListParams,
  UpdatePersonaBody,
} from '../features/personas/types';
import type {
  ListPersonsResponse,
  PersonsListFilters,
  PersonDetail,
  ListPersonConversationsResponse,
  ListPersonAppointmentsResponse,
} from '../features/persons/types';
import type {
  ChecklistTemplateDto,
  ChecklistTemplateSummary,
  CreatePlaybookBody,
  PlaybookDto,
  PlaybookListParams,
  UpdatePlaybookBody,
} from '../features/playbooks/types';
import type {
  ProactiveTemplate,
  ProactiveContext,
  ContextOriginType,
  InitiateProactivePayload,
  InitiateProactiveResponse,
  RecipientStatus,
} from '../features/proactive/types';
import type {
  StructuredCollection,
  ColetaWritePayload,
  ColetaRouteError,
  ColetaSchema,
} from '../features/structured-collection/types';
import type {
  SupervisorDashboardState,
  KpisResponse,
  PriorityQueueResponse,
  WidgetLayoutResponse,
  WidgetLayoutEntry,
  SetWidgetPreferencePayload,
  ResetLayoutResponse,
  RefreshResponse,
  SnapshotsHistoryResponse,
  ConflictSeverity,
  ConflictAlertRaw,
  ConflictAlertChainHistoryEntry,
  McpBlockEventRaw,
  ConflictAlertMetricsDailyRaw,
  ConflictAlertListResponse,
  ConflictAlertHistoryResponse,
  MitigateConflictAlertBody,
  AcceptWithJustificationBody,
  MarkFalsePositiveBody,
  ResolveConflictAlertBody,
  ReopenConflictAlertBody,
  SerializedPendencyRaw,
  PendencyTeamMetricsRaw,
  PendencyHeatmapCellRaw,
  SlaDashboardResultRaw,
  SlaHeatmapCellRaw,
  SlaRankingEntryRaw,
  SlaDefinitionRaw,
  SlaBreachRaw,
  SlaTemporalPatternRaw,
  SlaSeverityRaw,
  QualityGlobalRaw,
  OperatorQualityRaw,
  PlaybookQualityRaw,
  AgentPerformanceRaw,
  AiModelStatRaw,
  TopConversationsCostResponse,
  ConversationCostByAgentResponse,
  TopAttendantAbandonedRaw,
  TopBondCriticalRaw,
  ResolutionBySwitchBucketRaw,
  SequenceFlagsRaw,
  BehavioralPatternRaw,
  BehavioralContestationRaw,
  PlaybookDriftRaw,
  P1Category,
  P1EscalationLevel,
  P1IncidentRaw,
  P1ListResponse,
  P1Status,
  P1TimelineMiniResultRaw,
  P1VolumeStatusRaw,
  CoAttendantListItem,
  CoAttendantRole,
  ConversationComposerStateWire,
  QualityIndicators,
  CoachingMessage,
  SupervisorSessionNote,
  DiscrepancyIndicatorWire,
  AvailablePlaybook,
  ConversationTakeoverResult,
  PlaybookChangeResult,
  OverrideListResponse,
  OverrideRequestRaw,
  OverrideStatus,
  OverrideType,
  DualApprovalRequestRaw,
  TemporaryFeatureFlagRaw,
  OverrideDualResult,
  InsightCategory,
  InsightLens,
  InsightStatus,
  InsightRaw,
  InsightListResponse,
  InsightAggregateMetrics,
  InsightActionType,
  InsightActionRaw,
  InsightFalseFeedbackReason,
  InsightGenerationJobRaw,
  SensitivityMode,
} from '../features/supervisor/types';
import type {
  AddMemberBody,
  CreateRoutingRuleBody,
  CreateTeamBody,
  RoutingRuleDto,
  TeamView,
  UpdateMemberBody,
  UpdateTeamBody,
} from '../features/team-config/types';
// Respostas Prontas (spec 2026-07-16 §3.1) — tipo canônico dos items da
// rajada; fonte única FE+Engine, NUNCA redefinir (CLAUDE.md raiz §5).
import type {
  CannedResponseItem,
  CannedResponseMetricsOverview,
  CannedResponseOperatorMetric,
  ConductAlertConfig,
} from '@lume/shared';
// ADR-155 Fase 4 (E1) — shape por-campo do envelope episódico (Coleta Roteada
// por Durabilidade). Espelha `CollectedFieldEnvelope` do dispatcher do engine.
import type { CollectedFieldEnvelope } from '@lume/shared/collected-data';

/**
 * Wrapper sobre fetch para comunicação com o Engine.
 *
 * Convenções:
 *   - Base URL via `engineUrl()` (`runtime-config.ts`: contêiner → build → dev http://localhost:3100).
 *   - `Authorization: Bearer <accessToken>` injetado quando store auth tem.
 *   - `X-Tenant-Slug` derivado de subdomínio (dev: `netcomet` default).
 *   - Em 401 com refresh token disponível: tenta refresh → retenta original
 *     uma vez. Em falha, lança e o `authStore` faz logout (`onUnauthorized`).
 *   - 403 dispara `onForbidden(body)` SEM destruir sessão (Item 88 — bug
 *     detectado em 18/Mai/2026 via sessão Claude for Chrome: Carlos navegava
 *     URL direta de conversa que não era dele; backend retornava 403 e
 *     frontend tratava como 401, forçando logout silencioso). 401 é "sessão
 *     inválida"; 403 é "sem permissão para este recurso específico".
 *
 * Não usa axios para evitar dep adicional — fetch nativo + tipos.
 */

// Lido na hora de subir (`/config.js` do contêiner), com o build e o dev como reserva.
const ENGINE_URL = engineUrl();

function resolveTenantSlug(): string {
  // Tenant fixado pelo ambiente (contêiner ou build), quando o endereço não o carrega —
  // ou, em dev, para escolher o tenant sem depender do subdomínio (`VITE_TENANT_SLUG`).
  const fixo = tenantSlugFixo();
  if (fixo !== undefined) return fixo;
  if (typeof window === 'undefined') return 'netcomet';
  const host = window.location.hostname;
  // localhost / 127.0.0.1 → tenant dev default
  if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost')) {
    return 'netcomet';
  }
  // <slug>.lume.app → extrai
  const parts = host.split('.');
  return parts[0] ?? 'netcomet';
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiErrorBody,
  ) {
    const msg = Array.isArray(body.message) ? body.message.join('; ') : body.message;
    super(msg ?? body.error ?? `HTTP ${status.toString()}`);
    this.name = 'ApiError';
  }
}

export interface AuthTokenSnapshot {
  accessToken: string | null;
  refreshToken: string | null;
}

export type TokenGetter = () => AuthTokenSnapshot;
export type TokenUpdater = (next: LoginResponse) => void;
export type LogoutCallback = () => void;
/**
 * Disparado em 403 — recurso fora do alcance do user, sessão preservada.
 * Recebe o corpo do ApiError para a UI poder customizar mensagem
 * (capability/reason ou permission/layer conforme guard que falhou).
 */
export type ForbiddenCallback = (detail: { status: 403; body: ApiErrorBody }) => void;

let tokenGetter: TokenGetter = () => ({ accessToken: null, refreshToken: null });
let tokenUpdater: TokenUpdater = () => {
  /* no-op até configureApiClient ligar ao store */
};
let onUnauthorized: LogoutCallback = () => {
  /* no-op até configureApiClient ligar ao store */
};
let onForbidden: ForbiddenCallback = () => {
  /* no-op até configureApiClient ligar à UI */
};

export function configureApiClient(opts: {
  getTokens: TokenGetter;
  setTokens: TokenUpdater;
  onUnauthorized: LogoutCallback;
  onForbidden?: ForbiddenCallback;
}): void {
  tokenGetter = opts.getTokens;
  tokenUpdater = opts.setTokens;
  onUnauthorized = opts.onUnauthorized;
  if (opts.onForbidden !== undefined) onForbidden = opts.onForbidden;
}

interface FetchOpts extends RequestInit {
  authenticated?: boolean;
  /**
   * Opt-out por chamada do toast global de 403 (`onForbidden`). Quando `true`,
   * um 403 ainda LANÇA o `ApiError` (o caller trata inline), mas NÃO dispara o
   * toast genérico "Sem acesso a este recurso". Default ausente = comportamento
   * atual (dispara o toast). Usado pela mensageria interna, que mapeia os
   * 403 de escopo (DM_TARGET_OUT_OF_SCOPE etc.) em banner inline honesto
   * (design-references/mensageria/CLAUDE_CODE_HANDOFF.md §4).
   */
  suppressForbiddenToast?: boolean;
}

async function rawFetch(path: string, init: FetchOpts = {}): Promise<Response> {
  // `suppressForbiddenToast` é flag interna do callJson — não vai ao fetch.
  const { authenticated, suppressForbiddenToast: _suppressForbiddenToast, ...rest } = init;
  const headers = new Headers(rest.headers);
  headers.set('Content-Type', 'application/json');
  headers.set('X-Tenant-Slug', resolveTenantSlug());
  if (authenticated !== false) {
    const token = tokenGetter().accessToken;
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  return fetch(`${ENGINE_URL}${path}`, { ...rest, headers });
}

async function parseError(res: Response): Promise<ApiError> {
  let body: ApiErrorBody = {};
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    // ignore — body não-JSON
  }
  return new ApiError(res.status, body);
}

async function callJson<T>(path: string, init: FetchOpts = {}): Promise<T> {
  let res = await rawFetch(path, init);
  if (res.status === 401 && tokenGetter().refreshToken) {
    // tenta refresh + retry uma vez
    try {
      await refreshTokens();
      res = await rawFetch(path, init);
    } catch {
      onUnauthorized();
      throw await parseError(res);
    }
  }
  if (!res.ok) {
    if (res.status === 401) {
      // 401 = sessão inválida / expirada → logout.
      onUnauthorized();
    } else if (res.status === 403) {
      // 403 = sem permissão para este recurso → toast + preservar sessão.
      // Lê body uma única vez aqui e propaga via ApiError pelo throw abaixo.
      // `suppressForbiddenToast` (opt-out por chamada): ainda LANÇA o ApiError
      // (caller trata inline), mas pula o toast genérico.
      const apiErr = await parseError(res);
      if (init.suppressForbiddenToast !== true) {
        onForbidden({ status: 403, body: apiErr.body });
      }
      throw apiErr;
    }
    throw await parseError(res);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Converte o 422 da Coleta Estruturada (`{ error, reason }`) em `ColetaError`
 * tipado. Demais erros (incl. 409 optimistic lock) passam intactos.
 * PR B (ADR-155 Fase 3): os 422 de ROTEAMENTO do dispatcher trazem também
 * `field` (primeiro culpado) + `allErrors` (todos, por-campo) — propagados.
 */
function mapColetaError(err: unknown): unknown {
  if (err instanceof ApiError && err.status === 422) {
    const code = err.body.error ?? 'unknown';
    const reason = err.body.reason ?? '';
    const field = typeof err.body.field === 'string' ? err.body.field : null;
    const allErrors = Array.isArray(err.body.allErrors)
      ? err.body.allErrors
          .filter(
            (e): e is ColetaRouteError =>
              typeof e === 'object' &&
              e !== null &&
              typeof (e as ColetaRouteError).field === 'string' &&
              typeof (e as ColetaRouteError).error === 'string',
          )
          .map((e) => ({ field: e.field, error: e.error, reason: e.reason ?? '' }))
      : null;
    return new ColetaError(code, reason, err.message, field, allErrors);
  }
  return err;
}

/**
 * Item 36 (PR-B2) — Variante de callJson para uploads multipart/form-data.
 *
 * NÃO seta `Content-Type` manualmente — o browser injeta
 * `multipart/form-data; boundary=<boundary>` automaticamente ao receber
 * um FormData como body. Setar o header explicitamente quebraria o boundary.
 *
 * Reutiliza a lógica de auth, refresh e tratamento de erros de `callJson`.
 */
async function callFormData<T>(path: string, formData: FormData): Promise<T> {
  const headers = new Headers();
  // X-Tenant-Slug é obrigatório; Authorization injetado se disponível.
  headers.set('X-Tenant-Slug', resolveTenantSlug());
  const token = tokenGetter().accessToken;
  if (token) headers.set('Authorization', `Bearer ${token}`);
  // Content-Type: NÃO setar — browser injeta multipart boundary automaticamente.

  const doFetch = (): Promise<Response> =>
    fetch(`${ENGINE_URL}${path}`, { method: 'POST', body: formData, headers });

  let res = await doFetch();

  if (res.status === 401 && tokenGetter().refreshToken) {
    try {
      await refreshTokens();
      // Após refresh, recria headers com novo token.
      const newToken = tokenGetter().accessToken;
      if (newToken) headers.set('Authorization', `Bearer ${newToken}`);
      res = await doFetch();
    } catch {
      onUnauthorized();
      throw await parseError(res);
    }
  }
  if (!res.ok) {
    if (res.status === 401) {
      onUnauthorized();
    } else if (res.status === 403) {
      const apiErr = await parseError(res);
      onForbidden({ status: 403, body: apiErr.body });
      throw apiErr;
    }
    throw await parseError(res);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function refreshTokens(): Promise<void> {
  const { refreshToken } = tokenGetter();
  if (!refreshToken) throw new Error('Sem refresh token disponível.');
  const res = await rawFetch('/v1/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refreshToken }),
    authenticated: false,
  });
  if (!res.ok) throw await parseError(res);
  const next = (await res.json()) as LoginResponse;
  tokenUpdater(next);
}

// ─── public API ───────────────────────────────────────────────────────

/** Filtros compartilhados entre listConversations e getConversationCounts. */
function buildConversationFiltersQuery(filters: ConversationsFilters): URLSearchParams {
  const search = new URLSearchParams();
  if (filters.status !== undefined) search.set('status', filters.status);
  if (filters.channel !== undefined) search.set('channel', filters.channel);
  if (filters.operationMode !== undefined) search.set('operationMode', filters.operationMode);
  if (filters.assignedToUserId !== undefined) {
    search.set('assignedToUserId', filters.assignedToUserId);
  }
  if (filters.search !== undefined && filters.search !== '') search.set('search', filters.search);
  // Filtros B3 (Brief 2B)
  if (filters.awaitingPartyType !== undefined) {
    search.set('awaitingPartyType', filters.awaitingPartyType);
  }
  if (filters.metaSessionType !== undefined) search.set('metaSessionType', filters.metaSessionType);
  if (filters.hasActiveSequence === true) search.set('hasActiveSequence', 'true');
  if (filters.hasConflictAlert === true) search.set('hasConflictAlert', 'true');
  if (filters.hasOverdueCommitment === true) search.set('hasOverdueCommitment', 'true');
  if (filters.teamQueue !== undefined) search.set('teamQueue', filters.teamQueue);
  return search;
}

export const api = {
  login: (params: { email: string; password: string }): Promise<LoginResponse> =>
    callJson<LoginResponse>('/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify(params),
      authenticated: false,
    }),

  logout: (): Promise<void> => callJson<void>('/v1/auth/logout', { method: 'POST' }),

  me: (): Promise<MeResponse> => callJson<MeResponse>('/v1/auth/me', { method: 'GET' }),
  /** «Esqueci minha senha» (Item 477) — públicos. `forgot` responde 200 sempre. */
  forgotPassword: (email: string): Promise<{ ok: true; emailConfigured: boolean }> =>
    callJson('/v1/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  previewPasswordReset: (secret: string): Promise<PasswordResetPreviewWire> =>
    callJson<PasswordResetPreviewWire>(`/v1/auth/reset-password/${encodeURIComponent(secret)}`, {
      method: 'GET',
    }),
  resetPassword: (body: { token: string; newPassword: string }): Promise<void> =>
    callJson<void>('/v1/auth/reset-password', { method: 'POST', body: JSON.stringify(body) }),
  /** Aceite de convite (04/Set/2026) — públicos: o segredo da URL é a credencial. */
  previewInvite: (secret: string): Promise<InvitePreviewWire> =>
    callJson<InvitePreviewWire>(`/v1/auth/invite/${encodeURIComponent(secret)}`, { method: 'GET' }),
  acceptInvite: (
    secret: string,
    body: { name: string; password: string },
  ): Promise<{ email: string; tenantSlug: string }> =>
    callJson(`/v1/auth/invite/${encodeURIComponent(secret)}/accept`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  listConversations: (filters: ConversationsFilters): Promise<ListConversationsResponse> => {
    const search = buildConversationFiltersQuery(filters);
    if (filters.sort !== undefined) search.set('sort', filters.sort);
    if (filters.cursor !== undefined) search.set('cursor', filters.cursor);
    if (filters.limit !== undefined) search.set('limit', filters.limit.toString());
    const qs = search.toString();
    return callJson<ListConversationsResponse>(`/v1/conversations${qs ? `?${qs}` : ''}`, {
      method: 'GET',
    });
  },

  // Contagens agregadas da Inbox (badges das abas + por status do filter
  // rail). Mesmos filtros da listagem, sem cursor/limit/sort — o backend
  // agrega no banco (badge nunca diverge da lista por paginação).
  getConversationCounts: (filters: ConversationsFilters): Promise<{ data: InboxCounts }> => {
    const qs = buildConversationFiltersQuery(filters).toString();
    return callJson<{ data: InboxCounts }>(`/v1/conversations/counts${qs ? `?${qs}` : ''}`, {
      method: 'GET',
    });
  },

  getConversation: (id: string): Promise<ConversationDetail> =>
    callJson<ConversationDetail>(`/v1/conversations/${id}`, { method: 'GET' }),

  // ── Brief 2B Tela 2 — Pessoas (módulo persons B2 / PR #303). Read-only. ──
  // Cap `person-read` (gate da tela). Bubbles no detalhe são gated server-side
  // por `confidentiality-bubbles-full` (chegam `[]` sem a cap).
  listPersons: (filters: PersonsListFilters = {}): Promise<ListPersonsResponse> => {
    const s = new URLSearchParams();
    if (filters.q !== undefined && filters.q !== '') s.set('q', filters.q);
    if (filters.cpf !== undefined && filters.cpf !== '') s.set('cpf', filters.cpf);
    if (filters.email !== undefined && filters.email !== '') s.set('email', filters.email);
    if (filters.phone !== undefined && filters.phone !== '') s.set('phone', filters.phone);
    if (filters.cursor !== undefined) s.set('cursor', filters.cursor);
    if (filters.limit !== undefined) s.set('limit', filters.limit.toString());
    const qs = s.toString();
    return callJson<ListPersonsResponse>(`/v1/persons${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  getPerson: (personId: string): Promise<PersonDetail> =>
    callJson<PersonDetail>(`/v1/persons/${encodeURIComponent(personId)}`, { method: 'GET' }),
  /** D (spec 2026-08-12 §3.5) — busca por conteúdo no histórico da pessoa. */
  searchPersonMessages: (
    personId: string,
    params: { q: string; limit?: number },
  ): Promise<{
    data: {
      messageId: string;
      conversationId: string;
      sentAt: string;
      excerpt: string;
      personDisplayName: string | null;
    }[];
    meta: { enabled: boolean };
  }> => {
    const s = new URLSearchParams();
    s.set('q', params.q);
    if (params.limit !== undefined) s.set('limit', params.limit.toString());
    return callJson(`/v1/persons/${encodeURIComponent(personId)}/messages-search?${s.toString()}`, {
      method: 'GET',
    });
  },
  listPersonConversations: (
    personId: string,
    params: { cursor?: string; limit?: number } = {},
  ): Promise<ListPersonConversationsResponse> => {
    const s = new URLSearchParams();
    if (params.cursor !== undefined) s.set('cursor', params.cursor);
    if (params.limit !== undefined) s.set('limit', params.limit.toString());
    const qs = s.toString();
    return callJson<ListPersonConversationsResponse>(
      `/v1/persons/${encodeURIComponent(personId)}/conversations${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    );
  },
  listPersonAppointments: (
    personId: string,
    params: { cursor?: string; limit?: number } = {},
  ): Promise<ListPersonAppointmentsResponse> => {
    const s = new URLSearchParams();
    if (params.cursor !== undefined) s.set('cursor', params.cursor);
    if (params.limit !== undefined) s.set('limit', params.limit.toString());
    const qs = s.toString();
    return callJson<ListPersonAppointmentsResponse>(
      `/v1/persons/${encodeURIComponent(personId)}/appointments${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    );
  },

  // ── Brief 2B Tela 8 — Iniciar Conversa Proativa (módulo proactive-conversations
  // B7 / PR #301). Cap-mãe `initiate-proactive-conversation` (gate da tela). ──
  listProactiveTemplates: (category?: string): Promise<ProactiveTemplate[]> => {
    const s = new URLSearchParams();
    if (category !== undefined && category !== '') s.set('category', category);
    const qs = s.toString();
    return callJson<ProactiveTemplate[]>(
      `/v1/proactive-conversations/templates${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    );
  },
  /**
   * R-G9 — «esta pessoa pediu para não ser procurada?». Mesma cap da tela
   * (`initiate-proactive-conversation`): quem pode iniciar pode saber se será recusado.
   */
  getProactiveRecipientStatus: (personId: string): Promise<RecipientStatus> => {
    const s = new URLSearchParams({ personId });
    return callJson<RecipientStatus>(
      `/v1/proactive-conversations/recipient-status?${s.toString()}`,
      { method: 'GET' },
    );
  },
  resolveProactiveContext: (
    originType: ContextOriginType,
    originId: string,
  ): Promise<ProactiveContext> => {
    const s = new URLSearchParams({ originType, originId });
    return callJson<ProactiveContext>(`/v1/proactive-conversations/context?${s.toString()}`, {
      method: 'GET',
    });
  },
  /**
   * POST iniciação. 201 → `{ conversationId }`. 422 → `ProactiveBlockedError`
   * tipado pelo `code` (o backend manda `{ code, reason, message }`).
   */
  initiateProactiveConversation: async (
    payload: InitiateProactivePayload,
  ): Promise<InitiateProactiveResponse> => {
    try {
      return await callJson<InitiateProactiveResponse>('/v1/proactive-conversations', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        const code = err.body.code ?? 'unknown';
        const reason = err.body.reason ?? '';
        throw new ProactiveBlockedError(code, reason, err.message);
      }
      throw err;
    }
  },

  // ── Brief 2B Tela 4 — Coleta Estruturada (módulo structured-collections B6 /
  // ADR-144). Cap `structured-collection` (gate da tela). If-Match nos 3 de
  // escrita (ADR-071). 422 `{ error, reason }` → ColetaError tipado. ──
  getActiveStructuredCollection: (conversationId: string): Promise<StructuredCollection> =>
    callJson<StructuredCollection>(
      `/v1/conversations/${encodeURIComponent(conversationId)}/structured-collection`,
      { method: 'GET' },
    ),
  // Item 363 F1 — leitura por id (qualquer status). Usado pelo poll do painel
  // em `awaiting_confirmation` (o GET ativo acima só devolve in_progress).
  getStructuredCollectionById: (id: string): Promise<StructuredCollection> =>
    callJson<StructuredCollection>(`/v1/structured-collections/${encodeURIComponent(id)}`, {
      method: 'GET',
    }),
  // PR B (ADR-155 D9) — schema da tela derivado do Playbook da conversa
  // (mesma máquina do dispatcher do finalize). `source:'default'` ⇒ o FE
  // renderiza o form legado fixo (D8).
  getStructuredCollectionSchema: (id: string): Promise<ColetaSchema> =>
    callJson<ColetaSchema>(`/v1/structured-collections/${encodeURIComponent(id)}/schema`, {
      method: 'GET',
    }),
  // Item 361 F1 — "Sugerir da conversa" (cap `ai-collection-extraction`, Team+).
  // Devolve candidatos com provenance validada; NÃO grava nada (o rascunho é
  // preenchido no FE e o auto-save normal persiste).
  extractCollectionSuggestions: (
    conversationId: string,
  ): Promise<{
    candidates: { key: string; value: string; sourceMessageId: string; sourceQuote: string }[];
    discardedCount: number;
    invocationId: string | null;
  }> =>
    callJson(
      `/v1/conversations/${encodeURIComponent(conversationId)}/structured-collection/extract-suggestions`,
      { method: 'POST' },
    ),
  createStructuredCollection: (
    conversationId: string,
    payload: ColetaWritePayload = {},
  ): Promise<StructuredCollection> =>
    callJson<StructuredCollection>(
      `/v1/conversations/${encodeURIComponent(conversationId)}/structured-collection`,
      { method: 'POST', body: JSON.stringify(payload) },
    ),
  updateStructuredCollection: async (
    id: string,
    version: number,
    payload: ColetaWritePayload,
  ): Promise<StructuredCollection> => {
    try {
      return await callJson<StructuredCollection>(
        `/v1/structured-collections/${encodeURIComponent(id)}`,
        {
          method: 'PATCH',
          headers: { 'If-Match': version.toString() },
          body: JSON.stringify(payload),
        },
      );
    } catch (err) {
      throw mapColetaError(err);
    }
  },
  finalizeStructuredCollection: async (
    id: string,
    version: number,
  ): Promise<StructuredCollection> => {
    try {
      return await callJson<StructuredCollection>(
        `/v1/structured-collections/${encodeURIComponent(id)}/finalize`,
        { method: 'POST', headers: { 'If-Match': version.toString() } },
      );
    } catch (err) {
      throw mapColetaError(err);
    }
  },
  cancelStructuredCollection: async (
    id: string,
    version: number,
  ): Promise<StructuredCollection> => {
    try {
      return await callJson<StructuredCollection>(
        `/v1/structured-collections/${encodeURIComponent(id)}/cancel`,
        { method: 'POST', headers: { 'If-Match': version.toString() } },
      );
    } catch (err) {
      throw mapColetaError(err);
    }
  },
  // Item 361 F2 — "Confirmar com o titular": trava o rascunho e dispara o
  // fluxo SOA de confirmação em 2 etapas no canal validado do titular.
  // 422 `no_validated_channel` → ColetaError (FE oferece validação documental).
  requestTitularConfirmation: async (
    id: string,
    version: number,
  ): Promise<StructuredCollection> => {
    try {
      return await callJson<StructuredCollection>(
        `/v1/structured-collections/${encodeURIComponent(id)}/request-titular-confirmation`,
        { method: 'POST', headers: { 'If-Match': version.toString() } },
      );
    } catch (err) {
      throw mapColetaError(err);
    }
  },

  listMessages: (
    conversationId: string,
    params: { cursor?: string; limit?: number; personScope?: boolean } = {},
  ): Promise<ListMessagesResponse> => {
    const search = new URLSearchParams();
    if (params.cursor !== undefined) search.set('cursor', params.cursor);
    if (params.limit !== undefined) search.set('limit', params.limit.toString());
    // B2 — transcript contínuo por pessoa (server decide; meta.personScope é a verdade).
    if (params.personScope === true) search.set('personScope', 'true');
    const qs = search.toString();
    return callJson<ListMessagesResponse>(
      `/v1/conversations/${conversationId}/messages${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    );
  },

  sendMessage: async (
    conversationId: string,
    body: SendMessageBody,
    idempotencyKey?: string,
  ): Promise<SendMessageEnvelope> => {
    // F1.3 (bloqueio honesto): o header `X-Protection-Acknowledged` foi
    // removido — o Engine nunca o leu e o bloqueio de proteção é HARD. Não há
    // override de envio pelo atendente.
    const headers: Record<string, string> = {};
    if (idempotencyKey !== undefined && idempotencyKey.length > 0) {
      headers['Idempotency-Key'] = idempotencyKey;
    }
    /**
     * Adapter canonical — backend pode retornar (canonical pós Agent A PR-A):
     *   - `{ message: MessageItem, validation?: ProtectionValidation }` (envelope)
     * OU (legacy pré-protection-agent):
     *   - `MessageItem` direto (campos id, conversationId, etc no root)
     *
     * Detecta envelope via presença de propriedade `.message` no root.
     */
    const raw = await callJson<MessageItem | SendMessageEnvelope>(
      `/v1/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        headers,
      },
    );
    // Type guard canonical — envelope tem `.message`; MessageItem tem `.id` no root.
    if (
      typeof raw === 'object' &&
      raw !== null &&
      'message' in raw &&
      typeof (raw as { message: unknown }).message === 'object'
    ) {
      return raw;
    }
    // Legacy shape — empacota canonical em envelope sem validation. (O guard
    // acima não narrowa a união para o TS; fora do envelope, raw É MessageItem.)
    return { message: raw as MessageItem };
  },

  // Fatia 7 — sugestão IA inline.

  /**
   * Task 2 (Perfil de Operação IA Fatia 2b) — dispara geração de sugestão IA
   * sob demanda (modo H_a, operador solicita manualmente). Cap `ai-suggestion-h-a`.
   *
   * POST 202: `{ suggestionId: string | null; blockedByConflict: boolean }`.
   * POST 409: guard falhou (sem atendente / não-H_a / agente disabled / sem msg
   *   do cliente) → ApiError lançada com `.message` legível.
   *
   * A sugestão em si chega via WS `ai.suggestion.ready` (o endpoint só dispara
   * o `generate()`). NÃO chama `onForbidden` em 409 (é erro de negócio, não
   * de permissão) — o `callJson` padrão já trata 403 separadamente.
   */
  generateAISuggestion: (
    conversationId: string,
  ): Promise<{
    suggestionId: string | null;
    blockedByConflict: boolean;
    /** Item 482 — motivo de não ter sugerido (sem conflito). Ausente em engine antigo. */
    reason?: string | null;
  }> =>
    callJson(`/v1/ai-suggestions/conversations/${encodeURIComponent(conversationId)}/generate`, {
      method: 'POST',
    }),

  recordSuggestionDecision: (
    suggestionId: string,
    body: {
      action: 'accepted' | 'edited' | 'rejected' | 'ignored' | 'regenerated';
      finalText?: string;
    },
  ): Promise<void> =>
    callJson<void>(`/v1/ai-suggestions/${suggestionId}/decision`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Task 57 — busca AISuggestion ativa canonical para popular estado inicial
   * do hook `useAISuggestion` no mount (pattern hybrid REST+WS canonical).
   * Retorna null quando não há suggestion ativa (não-consumida + não-expirada).
   */
  getActiveAISuggestion: (
    conversationId: string,
  ): Promise<{
    suggestionId: string;
    conversationId: string;
    proposedText: string;
    agentId: string;
    agentVersion: string;
    createdAt: string;
    expiresAt: string;
    /** Fase 1 «um cérebro só» — chips de conduta do dry-run (ausente na sugestão legado v1). */
    chips?: SuggestionChip[];
  } | null> =>
    callJson(`/v1/ai-suggestions/conversations/${conversationId}/active`, { method: 'GET' }),

  /**
   * Task 59 — alterna modo operacional canonical (H_a / H_b / H_c / H_d).
   * Capability `conversation-lifecycle-strict` (atendente base) v1.
   */
  setOperationMode: (
    conversationId: string,
    mode: 'H_a' | 'H_b' | 'H_c' | 'H_d',
  ): Promise<{
    conversationId: string;
    mode: 'H_a' | 'H_b' | 'H_c' | 'H_d';
    previousMode: 'H_a' | 'H_b' | 'H_c' | 'H_d';
    switchedAt: string;
    switchedByUserId: string;
  }> =>
    callJson(`/v1/conversations/${conversationId}/operation-mode`, {
      method: 'PATCH',
      body: JSON.stringify({ mode }),
    }),

  /**
   * Fatia 11.1 — atendente retoma conversa em modo H_c (Cmd+R).
   * Transição H_c → H_a com audit `ai.conducting.resumed_by_human`.
   */
  takeOver: (
    conversationId: string,
  ): Promise<{
    conversationId: string;
    previousMode: 'H_a' | 'H_b' | 'H_c' | 'H_d';
    mode: 'H_a';
    resumedAt: string;
    resumedByUserId: string;
  }> =>
    callJson(`/v1/conversations/${conversationId}/take-over`, {
      method: 'POST',
    }),

  /**
   * S7 PR1 — abre a delegação POR ETAPA (S5, ADR-154; endpoint mergeado em
   * `conversations.controller.ts:290`, zero consumidor FE até este PR).
   * `objectives` = valores `GovernedStep.objective` (não id/name — vide
   * `SerializedConversationDetail.playbook.steps[].objective`);
   * omitido/vazio ⇒ escopo = todas as etapas completáveis do playbook.
   * `timeoutMinutes` omitido ⇒ timeout de segurança default (120min,
   * servidor decide — nunca "sem timeout" de fato, é sempre saída de
   * emergência). Capability `ai-step-delegation` (403 sem ela).
   */
  delegateSteps: (
    conversationId: string,
    body: { objectives?: string[]; timeoutMinutes?: number },
  ): Promise<{
    conversationId: string;
    objectives: string[] | null;
    timeoutAt: string | null;
    delegatedByUserId: string;
  }> =>
    callJson(`/v1/conversations/${conversationId}/delegate-steps`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Frente engine-led S7 (Condução Governada) — sinal de foco por-conversa.
   * O operador sinaliza que ABRIU (`viewing:true`, no montar + heartbeat) ou
   * FECHOU (`viewing:false`, no desmontar) a Conversa. 204 sem corpo.
   * Capability `conversation-viewing-awareness` (403 sem ela — por isso o hook
   * gateia o emit; ver `use-conversation-viewing`).
   */
  setConversationViewing: (conversationId: string, viewing: boolean): Promise<void> =>
    callJson<void>(`/v1/conversations/${conversationId}/viewing`, {
      method: 'POST',
      body: JSON.stringify({ viewing }),
    }),

  /**
   * Lote Claim — atendente assume conversa não-atribuída (self-claim).
   * Atômico no backend: corrida entre atendentes → 409.
   */
  claimConversation: (
    conversationId: string,
  ): Promise<{ conversationId: string; assignedToUserId: string; claimedAt: string }> =>
    callJson(`/v1/conversations/${conversationId}/claim`, {
      method: 'POST',
    }),

  /**
   * Item 391-B — operador confirma "é o titular": promove o vínculo de contrato
   * a operator_confirmed + marca a identidade do canal verificada-via-operador
   * (TTL 90d). Gated por `customer-registry-confirm`.
   */
  confirmCustomerIdentity: (conversationId: string): Promise<ConfirmIdentityResponse> =>
    callJson(`/v1/customer-registry/confirm-link`, {
      method: 'POST',
      body: JSON.stringify({ conversationId }),
    }),

  /**
   * F2-a — estabelece/evolui o vínculo (Bond↔Person) que falta na conversa
   * (fecha o 409 do confirm-link). Gated por `customer-link-establish` (opt-in).
   */
  establishLink: (body: EstablishLinkRequest): Promise<EstablishLinkResponse> =>
    callJson(`/v1/customer-registry/establish-link`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * F2-a — candidatos de vínculo (nativos sempre; externos best-effort quando há
   * sistema externo + identifier). Read do fluxo de estabelecimento; mesma cap.
   */
  listLinkCandidates: (
    conversationId: string,
    identifier?: string,
  ): Promise<LinkCandidatesResponse> => {
    const s = new URLSearchParams();
    if (identifier !== undefined && identifier.length > 0) s.set('identifier', identifier);
    const qs = s.toString();
    return callJson(
      `/v1/customer-registry/link-candidates/${encodeURIComponent(conversationId)}${qs ? `?${qs}` : ''}`,
    );
  },

  /**
   * Item 391-D — papel do contato nesta conversa (titular/autorizado/...) p/ exibir
   * ao operador. Read-only; gate backend `person-read`.
   */
  getContactRole: (conversationId: string): Promise<ConversationContactRole> =>
    callJson(`/v1/customer-registry/contact-role/${encodeURIComponent(conversationId)}`),

  // ── F4-b — Raiz de Tarefa/Campanha (1 tarefa-mãe → N conversas irmãs) ──────
  // Superfície de operador sobre os endpoints da F4-a. Gate backend
  // `proactive-outbound-campaign` (opt-in). close/cancel usam If-Match (ADR-071).
  /** POST /v1/outbound-task-roots — cria+materializa a campanha (N SOAs-filhas). */
  createOutboundCampaign: (
    body: CreateOutboundCampaignRequest,
  ): Promise<{ data: CreateOutboundCampaignResult }> =>
    callJson(`/v1/outbound-task-roots`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** GET /v1/outbound-task-roots — lista paginada de campanhas do tenant. */
  listOutboundCampaigns: (filters?: {
    status?: string;
    skip?: number;
    take?: number;
  }): Promise<{ data: ListOutboundCampaignsResponse }> => {
    const s = new URLSearchParams();
    if (filters?.status !== undefined) s.set('status', filters.status);
    if (filters?.skip !== undefined) s.set('skip', String(filters.skip));
    if (filters?.take !== undefined) s.set('take', String(filters.take));
    const qs = s.toString();
    return callJson(`/v1/outbound-task-roots${qs ? `?${qs}` : ''}`);
  },

  /** GET /v1/outbound-task-roots/:id — estado ao vivo (raiz + agregado on-demand). */
  getOutboundCampaignState: (id: string): Promise<{ data: OutboundCampaignStateResponse }> =>
    callJson(`/v1/outbound-task-roots/${encodeURIComponent(id)}`),

  /** POST /:id/close — fecho HUMANO (If-Match). force a partir de `active`. */
  closeOutboundCampaign: (
    id: string,
    version: number,
    force = false,
  ): Promise<{ data: { closed: boolean } }> =>
    callJson(`/v1/outbound-task-roots/${encodeURIComponent(id)}/close`, {
      method: 'POST',
      headers: { 'If-Match': version.toString() },
      body: JSON.stringify({ force }),
    }),

  /** POST /:id/cancel — cancela a campanha (SOAs pendentes canceladas; If-Match). */
  cancelOutboundCampaign: (
    id: string,
    version: number,
  ): Promise<{ data: { cancelled: boolean } }> =>
    callJson(`/v1/outbound-task-roots/${encodeURIComponent(id)}/cancel`, {
      method: 'POST',
      headers: { 'If-Match': version.toString() },
    }),

  /**
   * Fatia 11.1 — lista turnos do conductor-agent + inbound-trigger-agent
   * (audit visualizável) para a conversa.
   */
  listConductorTurns: (
    conversationId: string,
    limit?: number,
  ): Promise<{
    data: {
      id: string;
      turnNumber: number;
      agentId: string;
      agentVersion: string;
      action: 'reply' | 'call_tool' | 'escalate' | 'wait';
      payload: Record<string, unknown>;
      justification: string;
      executedAt: string;
      status: 'pending' | 'executed' | 'failed';
      latencyMs: number | null;
    }[];
    meta: { total: number; limit: number };
  }> =>
    callJson(
      `/v1/conversations/${conversationId}/conductor-turns${limit !== undefined ? `?limit=${limit}` : ''}`,
      { method: 'GET' },
    ),

  // Fatia 8 — classification + summary + behavioral.
  getLivingSummary: (
    conversationId: string,
  ): Promise<{
    conversationId: string;
    blocks: LivingSummaryBlocks;
    version: number;
    generatedAt: string;
    sourceMessagesCount: number;
  } | null> => callJson(`/v1/conversations/${conversationId}/living-summary`, { method: 'GET' }),

  /**
   * Resumo Vivo sob demanda — operador pede a regeneração manual ("Atualizar
   * resumo" no header do painel). 202 Accepted; o resumo novo chega via WS
   * `living-summary.updated` (o endpoint só dispara o `regenerate()`). NÃO chama
   * `onForbidden` em 409 (negócio, não permissão — summary-agent desligado).
   */
  regenerateLivingSummary: (conversationId: string): Promise<{ accepted: boolean }> =>
    callJson(`/v1/conversations/${encodeURIComponent(conversationId)}/living-summary/regenerate`, {
      method: 'POST',
    }),

  getQualitySignals: (
    conversationId: string,
  ): Promise<{
    conversationId: string;
    currentSentiment: string | null;
    sentimentTrend: 'ascending' | 'stable' | 'descending' | null;
    customerPosture: string | null;
    attendantPosture: string | null;
    sensitivityLevel: number | null;
    primaryIntent: string | null;
    lastClassifiedAt: string | null;
  } | null> => callJson(`/v1/conversations/${conversationId}/quality-signals`, { method: 'GET' }),

  // Fatia E — Checklists.
  listChecklists: (
    conversationId: string,
  ): Promise<{
    data: {
      id: string;
      name: string;
      status: 'in_progress' | 'completed' | 'abandoned' | 'suspended';
      totalItems: number;
      completedItems: number;
      criticalPending: boolean;
      createdAt: string;
      completedAt: string | null;
    }[];
  }> => callJson(`/v1/conversations/${conversationId}/checklists`, { method: 'GET' }),

  getChecklist: (
    id: string,
  ): Promise<{
    id: string;
    conversationId: string;
    name: string;
    status: 'in_progress' | 'completed' | 'abandoned' | 'suspended';
    totalItems: number;
    completedItems: number;
    criticalPending: boolean;
    items: {
      id: string;
      name: string;
      description: string | null;
      isCritical: boolean;
      order: number;
      status: 'pending' | 'fulfilled' | 'not_applicable' | 'failed';
      fulfilledAt: string | null;
      fulfilledByUserId: string | null;
      fulfilledViaAgent: boolean;
      /** Motivo do N/A — o mockup 2a mostra na linha; o serializer passou a expor. */
      notApplicableReason: string | null;
    }[];
  }> => callJson(`/v1/checklists/${id}`, { method: 'GET' }),

  fulfillChecklistItem: (
    itemId: string,
    body: { evidence?: Record<string, unknown>; notes?: string },
  ): Promise<void> =>
    callJson<void>(`/v1/checklist-items/${itemId}/fulfill`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  markChecklistItemNotApplicable: (itemId: string, reason: string): Promise<void> =>
    callJson<void>(`/v1/checklist-items/${itemId}/mark-not-applicable`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /**
   * Reabre um item que saiu de `pending` — a única porta de volta. O motivo é
   * opcional aqui porque a política de quando ele é exigido vive no engine
   * (janela de 60s do próprio autor em item comum dispensa; crítico, item da IA,
   * outro operador ou fora da janela exigem). O 400 volta com a mensagem certa.
   */
  reopenChecklistItem: (itemId: string, body: { reason?: string }): Promise<void> =>
    callJson<void>(`/v1/checklist-items/${itemId}/reopen`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Fatia F — Lifecycle.
  closeConversation: (
    conversationId: string,
    body: {
      outcomeType:
        | 'resolved'
        | 'unresolved_abandoned_by_customer'
        | 'unresolved_attendant_gave_up'
        | 'unresolved_blocked_by_system'
        | 'unresolved_out_of_scope'
        | 'not_applicable';
      outcomeReason?: string;
      notes?: string;
      overrideJustification?: string;
    },
  ): Promise<{
    conversationId: string;
    status: string;
    outcomeId: string;
    warnings: string[];
  }> =>
    callJson(`/v1/conversations/${conversationId}/close`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  pauseConversation: (conversationId: string, pausedReason: string): Promise<{ status: string }> =>
    callJson(`/v1/conversations/${conversationId}/pause`, {
      method: 'POST',
      body: JSON.stringify({ pausedReason }),
    }),

  resumeConversation: (
    conversationId: string,
    resumeTo?: 'awaiting_attendant' | 'awaiting_customer' | 'in_progress',
  ): Promise<{ status: string }> =>
    callJson(`/v1/conversations/${conversationId}/resume`, {
      method: 'POST',
      body: JSON.stringify(resumeTo !== undefined ? { resumeTo } : {}),
    }),

  reopenConversation: (conversationId: string): Promise<{ status: string; mode: 'reopen-same' }> =>
    callJson(`/v1/conversations/${conversationId}/reopen`, {
      method: 'POST',
    }),

  archiveConversation: (conversationId: string): Promise<{ status: string }> =>
    callJson(`/v1/conversations/${conversationId}/archive`, {
      method: 'POST',
    }),

  // Fatia F — Sequences.
  getSequenceAlerts: (): Promise<{
    data: {
      sequenceId: string;
      bondId: string;
      personId: string | null;
      flags: {
        highVolumeFlag: boolean;
        allAbandonedFlag: boolean;
        escalatingSentimentFlag: boolean;
        crossChannelInsistenceFlag: boolean;
      };
      totalConversations: number;
      lastConversationAt: string;
    }[];
  }> => callJson('/v1/tenants/sequences/alerts', { method: 'GET' }),

  // PR-B.2 (Item 89) — Sequences por person (filtra bond+status); usado pelo `useSequence`.
  listSequencesForPerson: (
    personId: string,
    params: { bondId?: string; status?: 'active' | 'closed' } = {},
  ): Promise<{
    data: {
      id: string;
      bondId: string;
      personId: string | null;
      status: 'active' | 'closed';
      totalConversations: number;
      windowDurationSeconds: number;
      lastConversationAt: string;
      flags: {
        highVolumeFlag: boolean;
        allAbandonedFlag: boolean;
        escalatingSentimentFlag: boolean;
        crossChannelInsistenceFlag: boolean;
      };
      members: { conversationId: string; orderIndex: number; joinedAt: string }[];
    }[];
  }> => {
    const search = new URLSearchParams();
    if (params.bondId !== undefined) search.set('bondId', params.bondId);
    if (params.status !== undefined) search.set('status', params.status);
    const qs = search.toString();
    return callJson(`/v1/persons/${personId}/sequences${qs ? `?${qs}` : ''}`, {
      method: 'GET',
    });
  },

  // PR-B.2 (Item 89) — Conflict alerts (Fatia C — ADR-050); usado pelo `useConflictAlerts`.
  // Brief 2A r7b Fatia 1 — wire type ampliado canonicamente com 3 campos
  // canônicos §32.4 (ADR-117): detectionContext + rationale + parties[].
  listConflictAlerts: (
    params: { status?: 'open' | 'acknowledged' | 'mitigated' | 'false-positive' | 'accepted' } = {},
  ): Promise<{
    data: {
      id: string;
      tenantId: string;
      category: string;
      severity: 'low' | 'medium' | 'high' | 'critical';
      bondsInvolved: string[];
      participantsInvolved: string[];
      status: 'open' | 'acknowledged' | 'mitigated' | 'false-positive' | 'accepted';
      detectionContext: string | null;
      rationale: string | null;
      parties: { type: 'Person' | 'Bond' | 'User'; name: string; role: string }[];
      createdAt: string;
      version: number;
    }[];
  }> => {
    const search = new URLSearchParams();
    if (params.status !== undefined) search.set('status', params.status);
    const qs = search.toString();
    return callJson(`/v1/conflict-alerts${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },

  // ── Tela 7 Centro de Alertas — /v1/conflict-alerts/v2 (lifecycle) + /metrics ──
  listConflictAlertsV2: (
    params: { status?: string; severity?: ConflictSeverity; category?: string } = {},
  ): Promise<ConflictAlertListResponse> => {
    const s = new URLSearchParams();
    if (params.status) s.set('status', params.status);
    if (params.severity) s.set('severity', params.severity);
    if (params.category) s.set('category', params.category);
    const qs = s.toString();
    return callJson(`/v1/conflict-alerts/v2${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  getConflictAlertV2: (id: string): Promise<ConflictAlertRaw> =>
    callJson(`/v1/conflict-alerts/v2/${id}`, { method: 'GET' }),
  getConflictAlertChain: (chainOriginId: string): Promise<{ data: ConflictAlertRaw[] }> =>
    callJson(`/v1/conflict-alerts/v2/chain/${chainOriginId}`, { method: 'GET' }),
  getConflictAlertChainHistory: (id: string): Promise<{ data: ConflictAlertChainHistoryEntry[] }> =>
    callJson(`/v1/conflict-alerts/v2/${id}/chain-history`, { method: 'GET' }),
  getConflictAlertMcpBlockEvents: (id: string): Promise<{ data: McpBlockEventRaw[] }> =>
    callJson(`/v1/conflict-alerts/v2/${id}/mcp-block-events`, { method: 'GET' }),
  getConflictAlertMetrics: (
    params: { periodFrom?: string; periodTo?: string } = {},
  ): Promise<{ data: ConflictAlertMetricsDailyRaw[] }> => {
    const s = new URLSearchParams();
    if (params.periodFrom) s.set('periodFrom', params.periodFrom);
    if (params.periodTo) s.set('periodTo', params.periodTo);
    const qs = s.toString();
    return callJson(`/v1/conflict-alerts/metrics${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  getConflictAlertHistory: (
    params: {
      type?: string;
      severity?: string;
      status?: string;
      cursor?: string;
      limit?: number;
    } = {},
  ): Promise<ConflictAlertHistoryResponse> => {
    const s = new URLSearchParams();
    if (params.type) s.set('type', params.type);
    if (params.severity) s.set('severity', params.severity);
    if (params.status) s.set('status', params.status);
    if (params.cursor) s.set('cursor', params.cursor);
    if (params.limit) s.set('limit', String(params.limit));
    const qs = s.toString();
    return callJson(`/v1/conflict-alerts/metrics/history${qs ? `?${qs}` : ''}`, {
      method: 'GET',
    });
  },
  // Mutations — If-Match via version (ADR-071; mesmo padrão de updateAppointment/reschedule).
  mitigateConflictAlert: (
    id: string,
    version: number,
    body: MitigateConflictAlertBody,
  ): Promise<{ status: string }> =>
    callJson(`/v1/conflict-alerts/v2/${id}/mitigate`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'If-Match': version.toString() },
    }),
  acceptConflictAlertWithJustification: (
    id: string,
    version: number,
    body: AcceptWithJustificationBody,
  ): Promise<{ status: string; dualApprovalPending?: boolean }> =>
    callJson(`/v1/conflict-alerts/v2/${id}/accept-with-justification`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'If-Match': version.toString() },
    }),
  markConflictAlertFalsePositive: (
    id: string,
    version: number,
    body: MarkFalsePositiveBody,
  ): Promise<{ status: string }> =>
    callJson(`/v1/conflict-alerts/v2/${id}/false-positive`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'If-Match': version.toString() },
    }),
  resolveConflictAlert: (
    id: string,
    version: number,
    body: ResolveConflictAlertBody,
  ): Promise<{ status: string }> =>
    callJson(`/v1/conflict-alerts/v2/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'If-Match': version.toString() },
    }),
  // Item 322 faceta a — reopen de alerta terminal → volta a 'detected' (Reopened).
  reopenConflictAlert: (
    id: string,
    version: number,
    body: ReopenConflictAlertBody,
  ): Promise<{ status: string }> =>
    callJson(`/v1/conflict-alerts/v2/${id}/reopen`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'If-Match': version.toString() },
    }),

  // Brief 3 Tela 4 — Inbox do Time (Pendency ADR-109).
  // Cards individuais vêm da lista flat (agregador /team só dá contagens).
  listPendencies: (
    params: { ownerUserId?: string; bondId?: string; status?: string } = {},
  ): Promise<{ data: SerializedPendencyRaw[] }> => {
    const s = new URLSearchParams();
    if (params.ownerUserId) s.set('ownerUserId', params.ownerUserId);
    if (params.bondId) s.set('bondId', params.bondId);
    if (params.status) s.set('status', params.status);
    const qs = s.toString();
    return callJson(`/v1/pendencies${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  getTeamPendencyMetrics: (): Promise<PendencyTeamMetricsRaw> =>
    callJson(`/v1/team/pendencies/metrics`, { method: 'GET' }),
  getTeamPendencyHeatmap: (): Promise<{ data: PendencyHeatmapCellRaw[] }> =>
    callJson(`/v1/team/pendencies/heatmap`, { method: 'GET' }),
  exportTeamPendenciesCsv: async (): Promise<Blob> => {
    const res = await rawFetch(`/v1/team/pendencies/export`, { method: 'GET' });
    if (!res.ok) throw await parseError(res);
    return res.blob();
  },
  bulkReassignPendencies: (body: {
    pendencyIds: string[];
    targetUserId: string;
    reason: string;
    notifyOperators?: boolean;
    notifyManager?: boolean;
    notifyConversation?: boolean;
  }): Promise<{ succeeded: string[] }> =>
    callJson(`/v1/team/pendencies/bulk-reassign`, { method: 'PATCH', body: JSON.stringify(body) }),
  bulkPostponePendencies: (body: {
    pendencyIds: string[];
    newDueAt: string;
    reason: string;
    notifyOperators?: boolean;
  }): Promise<{
    succeeded: string[];
    blocked: { pendencyId: string; currentPostponeCount: number }[];
  }> =>
    callJson(`/v1/team/pendencies/bulk-postpone`, { method: 'PATCH', body: JSON.stringify(body) }),

  // Brief 3 Tela 5 — Painel de SLA (S2). /v1/sla/dashboard* + /v1/sla/definitions.
  getSlaDashboard: (): Promise<SlaDashboardResultRaw> =>
    callJson('/v1/sla/dashboard', { method: 'GET' }),
  getSlaHeatmap: (slaDefinitionId: string): Promise<{ data: SlaHeatmapCellRaw[] }> =>
    callJson(`/v1/sla/dashboard/heatmap?slaDefinitionId=${encodeURIComponent(slaDefinitionId)}`, {
      method: 'GET',
    }),
  getSlaRanking: (periodDays: number): Promise<{ data: SlaRankingEntryRaw[] }> =>
    callJson(`/v1/sla/dashboard/ranking?periodDays=${periodDays.toString()}`, { method: 'GET' }),
  listSlaDefinitions: (): Promise<{ data: SlaDefinitionRaw[] }> =>
    callJson('/v1/sla/definitions', { method: 'GET' }),
  // T5-2 drill-down — GET /v1/sla/dashboard/breaches (cursor; { items, nextCursor }, sem wrapper data).
  getSlaBreaches: (
    params: {
      slaDefinitionId?: string;
      severity?: SlaSeverityRaw;
      assignedToUserId?: string;
      from?: string;
      to?: string;
      cursor?: string;
      limit?: number;
    } = {},
  ): Promise<{ items: SlaBreachRaw[]; nextCursor: string | null }> => {
    const s = new URLSearchParams();
    if (params.slaDefinitionId !== undefined) s.set('slaDefinitionId', params.slaDefinitionId);
    if (params.severity !== undefined) s.set('severity', params.severity);
    if (params.assignedToUserId !== undefined) s.set('assignedToUserId', params.assignedToUserId);
    if (params.from !== undefined) s.set('from', params.from);
    if (params.to !== undefined) s.set('to', params.to);
    if (params.cursor !== undefined) s.set('cursor', params.cursor);
    if (params.limit !== undefined) s.set('limit', params.limit.toString());
    const qs = s.toString();
    return callJson(`/v1/sla/dashboard/breaches${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  // T5-2 padrões — GET /v1/sla/dashboard/patterns (Pro+; { data }).
  listSlaPatterns: (
    params: { slaDefinitionId?: string; status?: 'active' | 'dismissed' | 'all' } = {},
  ): Promise<{ data: SlaTemporalPatternRaw[] }> => {
    const s = new URLSearchParams();
    if (params.slaDefinitionId !== undefined) s.set('slaDefinitionId', params.slaDefinitionId);
    if (params.status !== undefined) s.set('status', params.status);
    const qs = s.toString();
    return callJson(`/v1/sla/dashboard/patterns${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  // T5-2 dispensar padrão — PATCH /v1/sla/dashboard/patterns/:id/dismiss (204, sem body, sem If-Match).
  dismissSlaPattern: (id: string): Promise<void> =>
    callJson(`/v1/sla/dashboard/patterns/${encodeURIComponent(id)}/dismiss`, { method: 'PATCH' }),

  // Brief 3 Tela 6 — Dashboard de Qualidade (/v1/quality/metrics/*).
  // global default periodType=daily no service; demais aceitam periodFrom/To.
  getQualityGlobal: (
    p: { periodType?: string; periodFrom?: string; periodTo?: string } = {},
  ): Promise<QualityGlobalRaw[]> => {
    const s = new URLSearchParams();
    if (p.periodType !== undefined) s.set('periodType', p.periodType);
    if (p.periodFrom !== undefined) s.set('periodFrom', p.periodFrom);
    if (p.periodTo !== undefined) s.set('periodTo', p.periodTo);
    const qs = s.toString();
    return callJson(`/v1/quality/metrics/global${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  // by-operator retorna { items, nextCursor } (cursor opcional).
  getQualityByOperator: (
    p: { periodFrom?: string; periodTo?: string; cursor?: string; limit?: number } = {},
  ): Promise<{ items: OperatorQualityRaw[]; nextCursor: string | null }> => {
    const s = new URLSearchParams();
    if (p.periodFrom !== undefined) s.set('periodFrom', p.periodFrom);
    if (p.periodTo !== undefined) s.set('periodTo', p.periodTo);
    if (p.cursor !== undefined) s.set('cursor', p.cursor);
    if (p.limit !== undefined) s.set('limit', p.limit.toString());
    const qs = s.toString();
    return callJson(`/v1/quality/metrics/by-operator${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  getQualityByPlaybook: (
    p: { periodFrom?: string; periodTo?: string } = {},
  ): Promise<PlaybookQualityRaw[]> => {
    const s = new URLSearchParams();
    if (p.periodFrom !== undefined) s.set('periodFrom', p.periodFrom);
    if (p.periodTo !== undefined) s.set('periodTo', p.periodTo);
    const qs = s.toString();
    return callJson(`/v1/quality/metrics/by-playbook${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  getQualityAgents: (
    p: { periodFrom?: string; periodTo?: string } = {},
  ): Promise<AgentPerformanceRaw[]> => {
    const s = new URLSearchParams();
    if (p.periodFrom !== undefined) s.set('periodFrom', p.periodFrom);
    if (p.periodTo !== undefined) s.set('periodTo', p.periodTo);
    const qs = s.toString();
    return callJson(`/v1/quality/metrics/agents${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  // Qualidade IA (Gestor) — comparação entre modelos (cap `ai-usage-read`).
  getAiCostByModel: (
    p: { fromDate?: string; toDate?: string } = {},
  ): Promise<{ results: AiModelStatRaw[] }> => {
    const s = new URLSearchParams();
    if (p.fromDate !== undefined) s.set('fromDate', p.fromDate);
    if (p.toDate !== undefined) s.set('toDate', p.toDate);
    const qs = s.toString();
    return callJson(`/v1/ai-cost-monitoring/by-model${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  // Qualidade IA (Gestor) — top conversas por custo (cap `ai-usage-read`).
  getTopConversationsByCost: (
    p: { fromDate?: string; toDate?: string; limit?: number } = {},
  ): Promise<TopConversationsCostResponse> => {
    const s = new URLSearchParams();
    if (p.fromDate !== undefined) s.set('fromDate', p.fromDate);
    if (p.toDate !== undefined) s.set('toDate', p.toDate);
    if (p.limit !== undefined) s.set('limit', String(p.limit));
    const qs = s.toString();
    return callJson(`/v1/ai-cost-monitoring/top-conversations${qs ? `?${qs}` : ''}`, {
      method: 'GET',
    });
  },
  // Qualidade IA (Gestor) — custo por agente de uma conversa (cap `ai-usage-read`).
  getConversationCostByAgent: (conversationId: string): Promise<ConversationCostByAgentResponse> =>
    callJson(
      `/v1/ai-cost-monitoring/conversations/${encodeURIComponent(conversationId)}/cost-by-agent`,
      {
        method: 'GET',
      },
    ),
  // Tela 6 T6-2 Análises.
  getQualitySequences: (
    p: { periodFrom?: string; periodTo?: string } = {},
  ): Promise<SequenceFlagsRaw> => {
    const s = new URLSearchParams();
    if (p.periodFrom !== undefined) s.set('periodFrom', p.periodFrom);
    if (p.periodTo !== undefined) s.set('periodTo', p.periodTo);
    const qs = s.toString();
    return callJson(`/v1/quality/metrics/sequences${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  getQualityPlaybookDrift: (
    p: { periodFrom?: string; periodTo?: string } = {},
  ): Promise<PlaybookDriftRaw[]> => {
    const s = new URLSearchParams();
    if (p.periodFrom !== undefined) s.set('periodFrom', p.periodFrom);
    if (p.periodTo !== undefined) s.set('periodTo', p.periodTo);
    const qs = s.toString();
    return callJson(`/v1/quality/metrics/playbook-drift${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  // Dashboard Qualidade §5/§7 — rankings + resolução por trocas (desembrulha `results`).
  getQualityTopAttendants: async (
    p: { periodFrom?: string; periodTo?: string } = {},
  ): Promise<TopAttendantAbandonedRaw[]> => {
    const s = new URLSearchParams();
    if (p.periodFrom !== undefined) s.set('periodFrom', p.periodFrom);
    if (p.periodTo !== undefined) s.set('periodTo', p.periodTo);
    const qs = s.toString();
    const r = await callJson<{ results: TopAttendantAbandonedRaw[] }>(
      `/v1/quality/metrics/sequences/top-attendants${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    );
    return r.results;
  },
  getQualityTopBonds: async (
    p: { periodFrom?: string; periodTo?: string } = {},
  ): Promise<TopBondCriticalRaw[]> => {
    const s = new URLSearchParams();
    if (p.periodFrom !== undefined) s.set('periodFrom', p.periodFrom);
    if (p.periodTo !== undefined) s.set('periodTo', p.periodTo);
    const qs = s.toString();
    const r = await callJson<{ results: TopBondCriticalRaw[] }>(
      `/v1/quality/metrics/sequences/top-bonds${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    );
    return r.results;
  },
  getQualityResolutionBySwitches: async (
    p: { periodFrom?: string; periodTo?: string } = {},
  ): Promise<ResolutionBySwitchBucketRaw[]> => {
    const s = new URLSearchParams();
    if (p.periodFrom !== undefined) s.set('periodFrom', p.periodFrom);
    if (p.periodTo !== undefined) s.set('periodTo', p.periodTo);
    const qs = s.toString();
    const r = await callJson<{ results: ResolutionBySwitchBucketRaw[] }>(
      `/v1/quality/metrics/resolution-by-switches${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    );
    return r.results;
  },
  getBehavioralPatterns: (
    p: { operatorUserId?: string; status?: string; cursor?: string; limit?: number } = {},
  ): Promise<{ items: BehavioralPatternRaw[]; nextCursor: string | null }> => {
    const s = new URLSearchParams();
    if (p.operatorUserId !== undefined) s.set('operatorUserId', p.operatorUserId);
    if (p.status !== undefined) s.set('status', p.status);
    if (p.cursor !== undefined) s.set('cursor', p.cursor);
    if (p.limit !== undefined) s.set('limit', p.limit.toString());
    const qs = s.toString();
    return callJson(`/v1/behavioral-patterns${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  getBehavioralContestations: (
    p: { status?: string; cursor?: string; limit?: number } = {},
  ): Promise<{ items: BehavioralContestationRaw[]; nextCursor: string | null }> => {
    const s = new URLSearchParams();
    if (p.status !== undefined) s.set('status', p.status);
    if (p.cursor !== undefined) s.set('cursor', p.cursor);
    if (p.limit !== undefined) s.set('limit', p.limit.toString());
    const qs = s.toString();
    return callJson(`/v1/behavioral-patterns/contestations${qs ? `?${qs}` : ''}`, {
      method: 'GET',
    });
  },

  // Brief 3 Tela 8 — Painel de Gestão de P1 (/v1/p1/*). Read-only T8-1.
  getP1VolumeStatus: (): Promise<P1VolumeStatusRaw> =>
    callJson('/v1/p1/volume-status', { method: 'GET' }),
  listP1Incidents: (
    params: {
      status?: P1Status;
      category?: P1Category;
      escalationLevel?: P1EscalationLevel;
      cursor?: string;
      limit?: number;
    } = {},
  ): Promise<P1ListResponse> => {
    const s = new URLSearchParams();
    if (params.status !== undefined) s.set('status', params.status);
    if (params.category !== undefined) s.set('category', params.category);
    if (params.escalationLevel !== undefined) s.set('escalationLevel', params.escalationLevel);
    if (params.cursor !== undefined) s.set('cursor', params.cursor);
    if (params.limit !== undefined) s.set('limit', params.limit.toString());
    const qs = s.toString();
    return callJson(`/v1/p1/incidents${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  // timeline-mini só materializa p/ level4_cross_conv (senão null). Sem N+1 da chain:
  // a escalation chain do card deriva do próprio item da lista.
  getP1TimelineMini: (id: string): Promise<P1TimelineMiniResultRaw | null> =>
    callJson(`/v1/p1/incidents/${encodeURIComponent(id)}/timeline-mini`, { method: 'GET' }),

  // T8-2 ações — lifecycle (p1-acknowledge) + escalate (p1-escalate-chain) +
  // trigger-runbook (p1-runbook-trigger). P1Incident não tem version → sem If-Match.
  acknowledgeP1: (id: string): Promise<P1IncidentRaw> =>
    callJson(`/v1/p1/incidents/${encodeURIComponent(id)}/acknowledge`, { method: 'POST' }),
  handleP1: (id: string): Promise<P1IncidentRaw> =>
    callJson(`/v1/p1/incidents/${encodeURIComponent(id)}/handle`, { method: 'POST' }),
  resolveP1: (id: string, resolution: string): Promise<P1IncidentRaw> =>
    callJson(`/v1/p1/incidents/${encodeURIComponent(id)}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ resolution }),
    }),
  closeP1: (id: string): Promise<P1IncidentRaw> =>
    callJson(`/v1/p1/incidents/${encodeURIComponent(id)}/close`, { method: 'POST' }),
  escalateP1: (
    id: string,
    body: { toLevel: P1EscalationLevel; justification: string },
  ): Promise<P1IncidentRaw> =>
    callJson(`/v1/p1/incidents/${encodeURIComponent(id)}/escalate`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  triggerP1Runbook: (
    id: string,
    runbookSlug: string,
  ): Promise<{ executionId: string; status: string }> =>
    callJson(`/v1/p1/incidents/${encodeURIComponent(id)}/trigger-runbook`, {
      method: 'POST',
      body: JSON.stringify({ runbookSlug }),
    }),
  // T8-3 flag manual (p1-manual-flag, Pro+ opt-in OFF) → 201 P1Incident.
  manualFlagP1: (body: {
    conversationId: string;
    category: P1Category;
    reason: string;
  }): Promise<P1IncidentRaw> =>
    callJson('/v1/p1/manual-flag', { method: 'POST', body: JSON.stringify(body) }),

  // Brief 3 Tela 9 — Aprovação de Overrides (/v1/overrides + /v1/dual-approval). Read-only T9-1.
  listOverrides: (
    params: {
      status?: OverrideStatus;
      overrideType?: OverrideType;
      requesterUserId?: string;
      cursor?: string;
      limit?: number;
    } = {},
  ): Promise<OverrideListResponse> => {
    const s = new URLSearchParams();
    if (params.status !== undefined) s.set('status', params.status);
    if (params.overrideType !== undefined) s.set('overrideType', params.overrideType);
    if (params.requesterUserId !== undefined) s.set('requesterUserId', params.requesterUserId);
    if (params.cursor !== undefined) s.set('cursor', params.cursor);
    if (params.limit !== undefined) s.set('limit', params.limit.toString());
    const qs = s.toString();
    return callJson(`/v1/overrides${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  getOverride: (id: string): Promise<OverrideRequestRaw> =>
    callJson(`/v1/overrides/${encodeURIComponent(id)}`, { method: 'GET' }),
  dualApprovalPendingForMe: (): Promise<{ items: DualApprovalRequestRaw[] }> =>
    callJson('/v1/dual-approval/requests/pending-for-me', { method: 'GET' }),
  myRequestedDualApprovals: (): Promise<{ items: DualApprovalRequestRaw[] }> =>
    callJson('/v1/dual-approval/requests/requested-by-me', { method: 'GET' }),

  // T9-2 decisões — approve (dispatch por tipo) + reject. Cada tipo casa o DTO real (R15).
  approveOverride: (id: string, body: { approvalNote?: string }): Promise<OverrideRequestRaw> =>
    callJson(`/v1/overrides/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  rejectOverride: (id: string, approvalNote: string): Promise<OverrideRequestRaw> =>
    callJson(`/v1/overrides/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: JSON.stringify({ approvalNote }),
    }),
  approveOverrideType1: (
    id: string,
    body: { editedText: string; approvalNote?: string },
  ): Promise<{ messageId: string }> =>
    callJson(`/v1/overrides/${encodeURIComponent(id)}/approve-type-1`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  approveOverrideType5: (
    id: string,
    body: { effectiveUntil: string; approvalNote?: string },
  ): Promise<TemporaryFeatureFlagRaw> =>
    callJson(`/v1/overrides/${encodeURIComponent(id)}/approve-type-5`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  approveOverrideType6: (
    id: string,
    body: { documentationDocumentIds: string[]; approvalNote?: string },
  ): Promise<{ exceptionId: string }> =>
    callJson(`/v1/overrides/${encodeURIComponent(id)}/approve-type-6`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  approveOverrideType7: (
    id: string,
    body: { approvalNote?: string },
  ): Promise<OverrideDualResult> =>
    callJson(`/v1/overrides/${encodeURIComponent(id)}/approve-type-7`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  approveOverrideType8: (
    id: string,
    body: { documentationDocumentIds: string[]; approvalNote?: string },
  ): Promise<OverrideDualResult> =>
    callJson(`/v1/overrides/${encodeURIComponent(id)}/approve-type-8`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Brief 3 Tela 10 — Insights Estratégicos (gestor-insight-agent S9, /v1/insights). Read-only T10-1.
  // R15: confidence Decimal → string; lens fixo 'supervisor' (a tela do supervisor não usa 'gestor').
  listInsights: (
    params: {
      status?: InsightStatus;
      lens?: InsightLens;
      category?: InsightCategory;
      period?: string;
      cursor?: string;
      limit?: number;
    } = {},
  ): Promise<InsightListResponse> => {
    const s = new URLSearchParams();
    if (params.status !== undefined) s.set('status', params.status);
    if (params.lens !== undefined) s.set('lens', params.lens);
    if (params.category !== undefined) s.set('category', params.category);
    if (params.period !== undefined) s.set('period', params.period);
    if (params.cursor !== undefined) s.set('cursor', params.cursor);
    if (params.limit !== undefined) s.set('limit', params.limit.toString());
    const qs = s.toString();
    return callJson(`/v1/insights${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  getInsight: (id: string): Promise<{ insight: InsightRaw; actions: unknown[] }> =>
    callJson(`/v1/insights/${encodeURIComponent(id)}`, { method: 'GET' }),
  insightsPendingCount: (lens: InsightLens): Promise<{ lens: InsightLens; count: number }> =>
    callJson(`/v1/insights/pending-count?lens=${lens}`, { method: 'GET' }),
  insightAggregateMetrics: (
    params: { period?: string; lens?: InsightLens } = {},
  ): Promise<InsightAggregateMetrics> => {
    const s = new URLSearchParams();
    if (params.period !== undefined) s.set('period', params.period);
    if (params.lens !== undefined) s.set('lens', params.lens);
    const qs = s.toString();
    return callJson(`/v1/insights/aggregate-metrics${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  listInsightsHistory: (
    params: {
      status?: InsightStatus;
      lens?: InsightLens;
      category?: InsightCategory;
      cursor?: string;
      limit?: number;
    } = {},
  ): Promise<InsightListResponse> => {
    const s = new URLSearchParams();
    if (params.status !== undefined) s.set('status', params.status);
    if (params.lens !== undefined) s.set('lens', params.lens);
    if (params.category !== undefined) s.set('category', params.category);
    if (params.cursor !== undefined) s.set('cursor', params.cursor);
    if (params.limit !== undefined) s.set('limit', params.limit.toString());
    const qs = s.toString();
    return callJson(`/v1/insights/history${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },

  // T10-2 decisões — accept/false-positive/action/archive + geração manual + sensibilidade.
  // R15: action exige insight status=accepted (backend rejeita senão); enums snake_case.
  acceptInsight: (id: string): Promise<InsightRaw> =>
    callJson(`/v1/insights/${encodeURIComponent(id)}/accept`, { method: 'POST' }),
  falsePositiveInsight: (
    id: string,
    body: { reason: InsightFalseFeedbackReason; feedbackText?: string },
  ): Promise<InsightRaw> =>
    callJson(`/v1/insights/${encodeURIComponent(id)}/false-positive`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  takeInsightAction: (
    id: string,
    body: { actionType: InsightActionType; payload: Record<string, unknown> },
  ): Promise<InsightActionRaw> =>
    callJson(`/v1/insights/${encodeURIComponent(id)}/action`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  archiveInsight: (id: string, body: { reasonNote?: string } = {}): Promise<InsightRaw> =>
    callJson(`/v1/insights/${encodeURIComponent(id)}/archive`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  triggerInsightGeneration: (lens: InsightLens = 'supervisor'): Promise<{ jobId: string }> =>
    callJson('/v1/insights/generation/manual', {
      method: 'POST',
      body: JSON.stringify({ lens }),
    }),
  getInsightGenerationJob: (id: string): Promise<InsightGenerationJobRaw> =>
    callJson(`/v1/insights/generation/jobs/${encodeURIComponent(id)}`, { method: 'GET' }),
  setInsightSensitivity: (mode: SensitivityMode): Promise<{ mode: SensitivityMode }> =>
    callJson('/v1/insights/preferences/sensitivity', {
      method: 'POST',
      body: JSON.stringify({ mode }),
    }),

  // Brief 3 Tela 2 — Acompanhamento Silencioso (peer-coordination /v1/coattendance/*).
  // Ciclo de co-atendimento + espelho read-only (composer-state + quality-indicators).
  // R15: start NÃO é idempotente (409 ConflictException em duplicata — D2 trata reuse).
  coattendanceStart: (body: {
    conversationId: string;
    role: CoAttendantRole;
    invisibleJustification?: string;
  }): Promise<{ coAttendanceId: string }> =>
    callJson('/v1/coattendance/start', { method: 'POST', body: JSON.stringify(body) }),
  coattendanceEnd: (id: string): Promise<{ ended: boolean }> =>
    callJson(`/v1/coattendance/${encodeURIComponent(id)}/end`, { method: 'POST' }),
  coattendanceActive: (): Promise<{ data: CoAttendantListItem[] }> =>
    callJson('/v1/coattendance/active', { method: 'GET' }),
  coattendanceRole: (
    id: string,
    body: { role: CoAttendantRole; invisibleJustification?: string },
  ): Promise<CoAttendantListItem> =>
    callJson(`/v1/coattendance/${encodeURIComponent(id)}/role`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  getComposerState: (conversationId: string): Promise<ConversationComposerStateWire> =>
    callJson(`/v1/conversations/${encodeURIComponent(conversationId)}/composer-state`, {
      method: 'GET',
    }),
  getQualityIndicators: (conversationId: string): Promise<QualityIndicators> =>
    callJson(`/v1/conversations/${encodeURIComponent(conversationId)}/quality-indicators`, {
      method: 'GET',
    }),

  // T2-2 — Ferramentas do espelho (coaching dirigido + nota de sessão + discrepância).
  // R15: coaching POST body {text(1-2000), visibleToAttendant?=true}; GET ?after&limit(1-200)
  // → {data}. session-note POST body {text(0-5000)} → upsert (incrementa version, sem If-Match);
  // GET 404 quando a nota ainda não existe (tratado como null). discrepancy só bond adversarial.
  postCoachingMessage: (
    id: string,
    body: { text: string; visibleToAttendant?: boolean },
  ): Promise<CoachingMessage> =>
    callJson(`/v1/coattendance/${encodeURIComponent(id)}/coaching-message`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listCoachingMessages: (
    id: string,
    params: { after?: string; limit?: number } = {},
  ): Promise<{ data: CoachingMessage[] }> => {
    const search = new URLSearchParams();
    if (params.after !== undefined) search.set('after', params.after);
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    const qs = search.toString();
    return callJson(
      `/v1/coattendance/${encodeURIComponent(id)}/coaching-messages${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    );
  },
  /** A-41 — supervisão vista pelo operador (mesma permissão do GET da conversa). */
  getConversationSupervision: (conversationId: string): Promise<ConversationSupervision> =>
    callJson(`/v1/conversations/${encodeURIComponent(conversationId)}/supervision`, {
      method: 'GET',
    }),
  upsertSessionNote: (id: string, body: { text: string }): Promise<SupervisorSessionNote> =>
    callJson(`/v1/coattendance/${encodeURIComponent(id)}/session-note`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getSessionNote: async (id: string): Promise<SupervisorSessionNote | null> => {
    try {
      return await callJson<SupervisorSessionNote>(
        `/v1/coattendance/${encodeURIComponent(id)}/session-note`,
        { method: 'GET' },
      );
    } catch (err) {
      // 404 = nota ainda não criada → texto vazio (não é erro — D6).
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },
  getDiscrepancyIndicator: (conversationId: string): Promise<DiscrepancyIndicatorWire> =>
    callJson(`/v1/conversations/${encodeURIComponent(conversationId)}/discrepancy-indicator`, {
      method: 'GET',
    }),

  // T2-3 — Ações de força (módulo supervisor-actions). R15 confirmado nos controllers reais.
  // force-takeover: reason≥20 + mode? (mode é no-op no service v1, repassado p/ futuro).
  // force-playbook-switch: newPlaybookId≤64 + reason≥20. available-playbooks → ARRAY CRU.
  forceTakeover: (
    conversationId: string,
    body: { reason: string; mode?: string },
  ): Promise<ConversationTakeoverResult> =>
    callJson(
      `/v1/supervisor-actions/conversations/${encodeURIComponent(conversationId)}/force-takeover`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  forcePlaybookSwitch: (
    conversationId: string,
    body: { newPlaybookId: string; reason: string },
  ): Promise<PlaybookChangeResult> =>
    callJson(
      `/v1/supervisor-actions/conversations/${encodeURIComponent(conversationId)}/force-playbook-switch`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  listAvailablePlaybooks: (conversationId: string): Promise<AvailablePlaybook[]> =>
    callJson(
      `/v1/supervisor-actions/conversations/${encodeURIComponent(conversationId)}/available-playbooks`,
      { method: 'GET' },
    ),

  // S6 (Condução Governada, Q-J6) — R15 confirmado playbook-transitions.controller.ts.
  // list devolve {items} cru (sem envelope {data}); confirm/reject devolvem o DTO cru.
  // NOTA: NÃO reusa `listAvailablePlaybooks` acima p/ resolução de nomes — aquele
  // endpoint exige a cap `force-playbook-switch` (supervisor-only, capabilities.spec.yaml
  // linha ~2895), que o atendente comum (dono de `playbook-manual-switch`/
  // `conversation-lifecycle-strict`) NÃO detém. `listPlaybooks({status:'active'})`
  // (cap `playbook-read`, já na base do atendente) é a fonte honesta aqui.
  listPlaybookTransitions: (params: {
    conversationId?: string;
    status?: PlaybookTransitionStatusWire;
  }): Promise<{ items: PlaybookTransitionProposalWire[] }> => {
    const search = new URLSearchParams();
    if (params.conversationId !== undefined) search.set('conversationId', params.conversationId);
    if (params.status !== undefined) search.set('status', params.status);
    const qs = search.toString();
    return callJson(`/v1/playbook-transitions${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },

  confirmPlaybookTransition: (id: string): Promise<PlaybookTransitionProposalWire> =>
    callJson(`/v1/playbook-transitions/${encodeURIComponent(id)}/confirm`, { method: 'POST' }),

  rejectPlaybookTransition: (
    id: string,
    reason?: string,
  ): Promise<PlaybookTransitionProposalWire> =>
    callJson(`/v1/playbook-transitions/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: JSON.stringify(reason !== undefined ? { reason } : {}),
    }),

  // S6 (Condução Governada, Q-J8) — troca manual de playbook pelo atendente comum,
  // SOB a matriz `transitionPolicy` (≠ force-playbook-switch do supervisor, que a
  // IGNORA). Cap `playbook-manual-switch`. 422 = bloqueado pela matriz/proteção
  // (`transition-blocked-by-matrix` etc.) — caller mostra toast honesto.
  manualSwitchPlaybook: (
    conversationId: string,
    body: { toPlaybookId: string; reason?: string },
  ): Promise<PlaybookManualSwitchResultWire> =>
    callJson(`/v1/conversations/${encodeURIComponent(conversationId)}/playbook-switch`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // T3-1 (Tela 3 Intervenção) — lifecycle do takeover. R15 confirmado nos controllers reais.
  // return: returnReason≥10 (backend; UI piso 20). transfer: targetUserId≤64 + transferReason≥10 (UI piso 20).
  // Ambos devolvem o `ConversationTakeover` (subconjunto observável em ConversationTakeoverResult).
  takeoverReturn: (
    takeoverId: string,
    body: { returnReason: string },
  ): Promise<ConversationTakeoverResult> =>
    callJson(`/v1/supervisor-actions/takeovers/${encodeURIComponent(takeoverId)}/return`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  takeoverTransfer: (
    takeoverId: string,
    body: { targetUserId: string; transferReason: string },
  ): Promise<ConversationTakeoverResult> =>
    callJson(`/v1/supervisor-actions/takeovers/${encodeURIComponent(takeoverId)}/transfer`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Item 332 — GET do takeover ATIVO da conversa (fallback de deep-link/refresh
  // da Tela 3 Intervenção, sem o nav state da Tela 2). Mesmo gate `force-takeover`
  // dos POSTs; escopado ao próprio takeover do caller (returnedAt IS NULL). O
  // backend envelopa o caso ausente em `{ takeover | null }` (nunca null cru —
  // corpo vazio quebraria o parse); desembrulhamos para o resultado | null.
  getActiveTakeover: async (conversationId: string): Promise<ConversationTakeoverResult | null> => {
    const res = await callJson<{ takeover: ConversationTakeoverResult | null }>(
      `/v1/supervisor-actions/conversations/${encodeURIComponent(conversationId)}/takeover`,
      { method: 'GET' },
    );
    return res.takeover;
  },

  // Item 335 — atribuir uma conversa sem dono do escopo do supervisor a um atendente.
  // Backend: POST /v1/supervisor-actions/conversations/:id/assign { assignToUserId, reason? }.
  // Cap de gate: conversation-assign. `reason` omitido do body quando ausente.
  assignConversation: (
    conversationId: string,
    body: { assignToUserId: string; reason?: string },
  ): Promise<{
    conversationId: string;
    assignedToUserId: string;
    previousAssignedToUserId: string | null;
  }> =>
    callJson(`/v1/supervisor-actions/conversations/${encodeURIComponent(conversationId)}/assign`, {
      method: 'POST',
      body: JSON.stringify({
        assignToUserId: body.assignToUserId,
        ...(body.reason !== undefined && body.reason !== '' ? { reason: body.reason } : {}),
      }),
    }),

  // Sprint Backend Operacional Brief 2A r7b — Appointment (ADR-115).
  // Substitui fixtures hardcoded de useAppointments (Agent C Sprint Visual).
  listAppointments: (
    conversationId: string,
    params: {
      status?:
        | 'scheduled'
        | 'confirmed'
        | 'imminent'
        | 'completed'
        | 'cancelled'
        | 'no_show'
        | 'rescheduled';
    } = {},
  ): Promise<{ data: AppointmentWireItem[] }> => {
    const search = new URLSearchParams();
    if (params.status !== undefined) search.set('status', params.status);
    const qs = search.toString();
    return callJson(`/v1/conversations/${conversationId}/appointments${qs ? `?${qs}` : ''}`, {
      method: 'GET',
    });
  },

  // ADR-155 Fase 4 (E3) — seleção MANUAL do episódio em foco da conversa
  // (D4). `appointmentId=null` LIMPA o foco (gesto humano explícito,
  // diferente do cancel automático que não limpa). Retorna o detail
  // serializado — foco + paralelas atualizam numa única resposta.
  patchEpisodeFocus: (
    conversationId: string,
    appointmentId: string | null,
  ): Promise<ConversationDetail> =>
    callJson(`/v1/conversations/${conversationId}/episode-focus`, {
      method: 'PATCH',
      body: JSON.stringify({ appointmentId }),
    }),

  // ADR-155 Fase 4 (E4) — candidatos a episódio em foco (raio de posse: mesma
  // conversa / mesma pessoa / bond do cliente), não-terminais. Alimenta o
  // seletor manual ("Outros agendamentos do cliente").
  listEpisodeCandidates: (conversationId: string): Promise<{ data: AppointmentWireItem[] }> =>
    callJson(`/v1/conversations/${conversationId}/episode-candidates`, { method: 'GET' }),

  // Sprint Backend Operacional Brief 2A r7b (ADR-115) — criar Appointment.
  createAppointment: (body: {
    conversationId?: string;
    bondId?: string;
    personId?: string;
    title: string;
    type:
      | 'technical_visit'
      | 'in_person_meeting'
      | 'video_call'
      | 'phone_call'
      | 'delivery'
      | 'other';
    scheduledAt: string;
    durationMinutes?: number;
    addressOrLocation?: string;
    notes?: string;
    assignedToUserId?: string;
    source: 'ai' | 'attendant' | 'customer';
    aiAgentId?: string;
  }): Promise<AppointmentWireItem> =>
    callJson('/v1/appointments', { method: 'POST', body: JSON.stringify(body) }),

  // ADR-115 — atualizar Appointment (status/título/agenda). If-Match obrigatório (ADR-071).
  updateAppointment: (
    appointmentId: string,
    expectedVersion: number,
    body: {
      title?: string;
      status?:
        | 'scheduled'
        | 'confirmed'
        | 'imminent'
        | 'completed'
        | 'cancelled'
        | 'no_show'
        | 'rescheduled';
      scheduledAt?: string;
      durationMinutes?: number;
      addressOrLocation?: string;
      notes?: string;
      assignedToUserId?: string;
      cancellationReason?: string;
    },
  ): Promise<AppointmentWireItem> =>
    callJson(`/v1/appointments/${appointmentId}`, {
      method: 'PATCH',
      headers: { 'If-Match': expectedVersion.toString() },
      body: JSON.stringify(body),
    }),

  // ADR-115 — reagendar Appointment (cria sucessor + rescheduledFromId). If-Match obrigatório.
  rescheduleAppointment: (
    appointmentId: string,
    expectedVersion: number,
    body: {
      newScheduledAt: string;
      durationMinutes?: number;
      addressOrLocation?: string;
      reason?: string;
    },
  ): Promise<AppointmentWireItem> =>
    callJson(`/v1/appointments/${appointmentId}/reschedule`, {
      method: 'POST',
      headers: { 'If-Match': expectedVersion.toString() },
      body: JSON.stringify(body),
    }),

  // ADR-150 — fonte `manual` do motor SOA: agenda follow-up futuro no
  // encerramento ("te procuro semana que vem" → conversa proativa automática).
  // Cap `initiate-proactive-conversation` (mesma permissão do T8, diferida).
  scheduleManualFollowup: (body: {
    conversationId: string;
    waitDays: number;
    content: string;
    mode?: 'H_a' | 'H_b' | 'H_c';
  }): Promise<{
    id: string;
    conversationId: string;
    scheduledFor: string;
    mode: 'H_a' | 'H_b' | 'H_c';
    status: 'scheduled';
  }> => callJson('/v1/scheduled-outbound-actions', { method: 'POST', body: JSON.stringify(body) }),

  // Brief 2B B4 — range query da grade do calendário (`GET /v1/appointments`).
  // `assignedToUserId='me'` resolve ao caller no backend. Tenant-scoped.
  listAppointmentsRange: (params: {
    from: string;
    to: string;
    assignedToUserId?: string;
    status?: AppointmentWireStatus;
    bondId?: string;
  }): Promise<{ data: AppointmentWireItem[] }> => {
    const search = new URLSearchParams();
    search.set('from', params.from);
    search.set('to', params.to);
    if (params.assignedToUserId !== undefined) {
      search.set('assignedToUserId', params.assignedToUserId);
    }
    if (params.status !== undefined) search.set('status', params.status);
    if (params.bondId !== undefined) search.set('bondId', params.bondId);
    return callJson(`/v1/appointments?${search.toString()}`, { method: 'GET' });
  },

  // ── Frente B Bloco II — agenda NATIVA (gated `scheduling-native`) ──────────
  // READ-only: catálogo de recursos/serviços + disponibilidade (livre/ocupado).
  // Alimenta o slot-picker + a visão de disponibilidade do operador.
  getSchedulingResources: (): Promise<{ data: SchedulingResourceWire[] }> =>
    callJson('/v1/scheduling/resources', { method: 'GET' }),
  getSchedulingServices: (): Promise<{ data: SchedulingServiceWire[] }> =>
    callJson('/v1/scheduling/services', { method: 'GET' }),
  getSchedulingAvailability: (params: {
    serviceOfferingId: string;
    from: string;
    to: string;
    resourceIds?: string[];
    limit?: number;
  }): Promise<{ data: SchedulingSlotWire[] }> => {
    const search = new URLSearchParams();
    search.set('serviceOfferingId', params.serviceOfferingId);
    search.set('from', params.from);
    search.set('to', params.to);
    if (params.resourceIds !== undefined && params.resourceIds.length > 0) {
      search.set('resourceIds', params.resourceIds.join(','));
    }
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    return callJson(`/v1/scheduling/availability?${search.toString()}`, { method: 'GET' });
  },
  // Booking NATIVO pelo operador (slot-picker) — grava sob a guarda de slot livre.
  // 409 quando o horário foi tomado entre o fetch e o book → o FE reconduz.
  createNativeBooking: (body: {
    serviceOfferingId: string;
    startsAt: string;
    personId?: string;
    bondId?: string;
    conversationId?: string;
    title?: string;
  }): Promise<AppointmentWireItem> =>
    callJson('/v1/scheduling/book', { method: 'POST', body: JSON.stringify(body) }),

  // ── Frente B Sessão 1 — gestão de agenda do operador: FECHAR/REABRIR período ──
  // AvailabilityBlock por recurso (férias/feriado/almoço). O motor já subtrai blocks
  // da disponibilidade; aqui é a escrita do operador. Gated `scheduling-native`.
  getSchedulingBlocks: (params: {
    /** Omitido = cross-resource (todos os recursos do tenant na janela) — grade recurso×tempo. */
    resourceId?: string;
    from?: string;
    to?: string;
  }): Promise<{ data: SchedulingBlockWire[] }> => {
    const search = new URLSearchParams();
    if (params.resourceId !== undefined) search.set('resourceId', params.resourceId);
    if (params.from !== undefined) search.set('from', params.from);
    if (params.to !== undefined) search.set('to', params.to);
    return callJson(`/v1/scheduling/blocks?${search.toString()}`, { method: 'GET' });
  },
  createSchedulingBlock: (body: {
    resourceId: string;
    startsAt: string;
    endsAt: string;
    reason?: string;
  }): Promise<{ data: SchedulingBlockWire }> =>
    callJson('/v1/scheduling/blocks', { method: 'POST', body: JSON.stringify(body) }),
  deleteSchedulingBlock: (id: string): Promise<{ data: { deleted: true } }> =>
    callJson(`/v1/scheduling/blocks/${id}`, { method: 'DELETE' }),

  // ── Frente B Plano 2 (setup) — gestão do catálogo de RECURSOS (gated `scheduling-native`) ──
  // CRUD da unidade-calendário (cadeira/sala/profissional/equipamento). A LEITURA é
  // getSchedulingResources. PATCH/DELETE usam If-Match (ADR-071) com a `version` do recurso.
  // DELETE é soft-delete (aposenta; preserva o histórico de agendamentos).
  createSchedulingResource: (body: {
    name: string;
    resourceType: SchedulingResourceWire['resourceType'];
    capacity?: number;
    timezone?: string;
    userId?: string;
  }): Promise<{ data: SchedulingResourceWire }> =>
    callJson('/v1/scheduling/resources', { method: 'POST', body: JSON.stringify(body) }),
  updateSchedulingResource: (
    id: string,
    version: number,
    body: {
      name?: string;
      resourceType?: SchedulingResourceWire['resourceType'];
      capacity?: number;
      timezone?: string;
      // `null` desvincula o recurso do profissional (User) — o backend seta userId=null.
      userId?: string | null;
    },
  ): Promise<{ data: SchedulingResourceWire }> =>
    callJson(`/v1/scheduling/resources/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'If-Match': version.toString() },
    }),
  deactivateSchedulingResource: (
    id: string,
    version: number,
  ): Promise<{ data: { deactivated: true } }> =>
    callJson(`/v1/scheduling/resources/${id}`, {
      method: 'DELETE',
      headers: { 'If-Match': version.toString() },
    }),

  // ── Frente B Plano 2 (setup) — gestão do catálogo de SERVIÇOS (gated `scheduling-native`) ──
  // CRUD do catálogo de serviços (duração + buffers + recursos exigidos + lead-time + fila).
  // A LEITURA é getSchedulingServices. PATCH/DELETE usam If-Match. DELETE é soft-delete.
  createSchedulingService: (body: {
    name: string;
    defaultDurationMinutes?: number;
    bufferBeforeMin?: number;
    bufferAfterMin?: number;
    requiredResourceTypes?: SchedulingResourceWire['resourceType'][];
    minLeadTimeMin?: number;
    cancellationWindowMin?: number;
    intakeNote?: string;
    waitlistEnabled?: boolean;
  }): Promise<{ data: SchedulingServiceWire }> =>
    callJson('/v1/scheduling/services', { method: 'POST', body: JSON.stringify(body) }),
  updateSchedulingService: (
    id: string,
    version: number,
    body: {
      name?: string;
      defaultDurationMinutes?: number;
      bufferBeforeMin?: number;
      bufferAfterMin?: number;
      requiredResourceTypes?: SchedulingResourceWire['resourceType'][];
      minLeadTimeMin?: number;
      cancellationWindowMin?: number;
      intakeNote?: string;
      waitlistEnabled?: boolean;
    },
  ): Promise<{ data: SchedulingServiceWire }> =>
    callJson(`/v1/scheduling/services/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'If-Match': version.toString() },
    }),
  deactivateSchedulingService: (
    id: string,
    version: number,
  ): Promise<{ data: { deactivated: true } }> =>
    callJson(`/v1/scheduling/services/${id}`, {
      method: 'DELETE',
      headers: { 'If-Match': version.toString() },
    }),

  // ── Frente B Plano 2 (setup) — HORÁRIO de funcionamento recorrente por recurso ──
  // WorkingHours (janela recorrente, minutos-desde-meia-noite no fuso do recurso). Sem
  // update: editar = remover + recriar. Gated `scheduling-native`. Por-recurso (exige resourceId).
  getSchedulingWorkingHours: (
    resourceId: string,
  ): Promise<{ data: SchedulingWorkingHoursWire[] }> => {
    const search = new URLSearchParams({ resourceId });
    return callJson(`/v1/scheduling/working-hours?${search.toString()}`, { method: 'GET' });
  },
  createSchedulingWorkingHours: (body: {
    resourceId: string;
    dayOfWeek: number;
    startMinute: number;
    endMinute: number;
    effectiveFrom?: string;
    effectiveUntil?: string;
  }): Promise<{ data: SchedulingWorkingHoursWire }> =>
    callJson('/v1/scheduling/working-hours', { method: 'POST', body: JSON.stringify(body) }),
  deleteSchedulingWorkingHours: (id: string): Promise<{ data: { deleted: true } }> =>
    callJson(`/v1/scheduling/working-hours/${id}`, { method: 'DELETE' }),

  // ── Frente N N3a-2 — ESCALA de plantão do operador (gated `operator-oncall-schedule-*`) ──
  // CRUD de OperatorOnCallShift (operador × vigência). Ativa o regime 3 (plantão fura
  // com `urgent` fora do expediente). Leitura gated `-read`; escrita `-manage`.
  // PATCH/DELETE usam If-Match (ADR-071). DELETE é hard-delete (a prova fica em audit F.9).
  getOperatorOnCallShifts: (params?: {
    userId?: string;
    from?: string;
    to?: string;
    /** Filtra os que cobrem o instante (`startsAt <= activeAt < endsAt`) — "de plantão agora?". */
    activeAt?: string;
  }): Promise<{ data: OperatorOnCallShiftWire[] }> => {
    const search = new URLSearchParams();
    if (params?.userId !== undefined) search.set('userId', params.userId);
    if (params?.from !== undefined) search.set('from', params.from);
    if (params?.to !== undefined) search.set('to', params.to);
    if (params?.activeAt !== undefined) search.set('activeAt', params.activeAt);
    const q = search.toString();
    return callJson(`/v1/operator-on-call-shifts${q === '' ? '' : `?${q}`}`, { method: 'GET' });
  },
  createOperatorOnCallShift: (body: {
    userId: string;
    startsAt: string;
    endsAt: string;
    note?: string;
  }): Promise<{ data: OperatorOnCallShiftWire }> =>
    callJson('/v1/operator-on-call-shifts', { method: 'POST', body: JSON.stringify(body) }),
  updateOperatorOnCallShift: (
    id: string,
    version: number,
    body: {
      startsAt?: string;
      endsAt?: string;
      /** `null` limpa a nota. */
      note?: string | null;
    },
  ): Promise<{ data: OperatorOnCallShiftWire }> =>
    callJson(`/v1/operator-on-call-shifts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'If-Match': version.toString() },
    }),
  deleteOperatorOnCallShift: (id: string, version: number): Promise<{ data: { deleted: true } }> =>
    callJson(`/v1/operator-on-call-shifts/${id}`, {
      method: 'DELETE',
      headers: { 'If-Match': version.toString() },
    }),

  // ── Frente B Bloco IV PR6 — controle do operador sobre a fila de espera ─────
  // Gated `scheduling-waitlist`. Listar a fila (ordenada), aprovar/recusar
  // `pending_approval`, reordenar via prioridade, liberar manual. If-Match nas mutações.
  getWaitlist: (params?: {
    serviceOfferingId?: string;
    resourceId?: string;
    /** Frente B Sessão 2 — fila DESTE cliente (mini-card da Conversa). */
    personId?: string;
  }): Promise<{ data: WaitlistEntryWire[] }> => {
    const search = new URLSearchParams();
    if (params?.serviceOfferingId !== undefined) {
      search.set('serviceOfferingId', params.serviceOfferingId);
    }
    if (params?.resourceId !== undefined) search.set('resourceId', params.resourceId);
    if (params?.personId !== undefined) search.set('personId', params.personId);
    const qs = search.toString();
    return callJson(`/v1/scheduling/waitlist${qs !== '' ? `?${qs}` : ''}`, { method: 'GET' });
  },
  approveWaitlistEntry: (id: string, version: number): Promise<{ data: WaitlistEntryWire }> =>
    callJson(`/v1/scheduling/waitlist/${id}/approve`, {
      method: 'POST',
      headers: { 'If-Match': version.toString() },
    }),
  rejectWaitlistEntry: (id: string, version: number): Promise<{ data: WaitlistEntryWire }> =>
    callJson(`/v1/scheduling/waitlist/${id}/reject`, {
      method: 'POST',
      headers: { 'If-Match': version.toString() },
    }),
  updateWaitlistPriority: (
    id: string,
    version: number,
    priorityTier: WaitlistPriorityTierWire,
  ): Promise<{ data: WaitlistEntryWire }> =>
    callJson(`/v1/scheduling/waitlist/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ priorityTier }),
      headers: { 'If-Match': version.toString() },
    }),
  promoteWaitlistEntry: (
    id: string,
    body: { resourceId: string; startsAt: string; durationMinutes?: number },
  ): Promise<{ data: { booked: boolean; appointmentId: string | null; offeredCount: number } }> =>
    callJson(`/v1/scheduling/waitlist/${id}/promote`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Frente B (reconciliação, Sessão 5) — sinais de fila/capacidade do painel do Supervisor.
  getWaitlistSignals: (params?: {
    windowDays?: number;
  }): Promise<{ data: WaitlistSignalsWire }> => {
    const qs = params?.windowDays !== undefined ? `?windowDays=${String(params.windowDays)}` : '';
    return callJson(`/v1/scheduling/waitlist-signals${qs}`, { method: 'GET' });
  },
  getCapacitySignals: (params?: {
    horizonDays?: number;
  }): Promise<{ data: CapacitySignalsWire }> => {
    const qs =
      params?.horizonDays !== undefined ? `?horizonDays=${String(params.horizonDays)}` : '';
    return callJson(`/v1/scheduling/capacity-signals${qs}`, { method: 'GET' });
  },

  // Brief 2B B5 — Painel Pessoal (Tela 5). Composição read-time, caller-scoped.
  getAgentDashboardState: (): Promise<AgentDashboardStateWire> =>
    callJson('/v1/agent-dashboard/state', { method: 'GET' }),

  // S3 Abertura e Fechamento do dia (Corrente de Compromissos, P2 da tese).
  // Cap opt-in `operator-daily-ritual` — gateia o fetch (hook `enabled`), não só o render.
  getDayRitual: (kind: 'opening' | 'closing', since?: string): Promise<DayRitualStateWire> => {
    const qs = since !== undefined ? `&since=${encodeURIComponent(since)}` : '';
    return callJson(`/v1/agent-dashboard/day-ritual?kind=${kind}${qs}`, { method: 'GET' });
  },

  // S5 Corrente de Compromissos (maré baixa produtiva, Item 405).
  // Cap opt-in `proactive-work-suggestions` — gateia o fetch (hook `enabled`), não só o render.
  getLowTideSuggestions: (): Promise<LowTideSuggestionsStateWire> =>
    callJson('/v1/agent-dashboard/low-tide-suggestions', { method: 'GET' }),

  dismissLowTideSuggestion: (id: string, personId: string): Promise<void> =>
    callJson(`/v1/agent-dashboard/low-tide-suggestions/${encodeURIComponent(id)}/dismiss`, {
      method: 'POST',
      body: JSON.stringify({ personId }),
    }),

  acceptLowTideSuggestion: (
    id: string,
    personId: string,
    gesture: 'fazer-agora' | 'ver-pessoa' | 'delegar',
  ): Promise<void> =>
    callJson(`/v1/agent-dashboard/low-tide-suggestions/${encodeURIComponent(id)}/accept`, {
      method: 'POST',
      body: JSON.stringify({ personId, gesture }),
    }),

  // user-preferences (genérico) — usado pela Tela 5 p/ persistir o layout P3.6
  // (namespace 'agent-dashboard-layout', keys 'visible'/'order'). Cap: user-preferences-manage.
  getUserPreferenceNamespace: (namespace: string): Promise<Record<string, unknown>> =>
    callJson(`/v1/user-preferences/me/${encodeURIComponent(namespace)}`, { method: 'GET' }),

  setUserPreference: (
    namespace: string,
    key: string,
    value: unknown,
    expectedVersion?: number,
  ): Promise<{ id: string; version: number }> =>
    callJson(
      `/v1/user-preferences/me/${encodeURIComponent(namespace)}/${encodeURIComponent(key)}`,
      {
        method: 'PUT',
        body: JSON.stringify(
          expectedVersion === undefined ? { value } : { value, expectedVersion },
        ),
      },
    ),

  // Brief 2B T6 — Auxiliar (Tela 6). Preferências de notificação por (channel × template).
  // GET retorna só opt-out/in explícitos; ausência = default-ON. Cap notifications-manage.
  getNotificationPreferences: (): Promise<NotificationPreferenceWire[]> =>
    callJson('/v1/me/notification-preferences', { method: 'GET' }),

  // PUT upsert atômico de 1 preference (enabled). Persiste o toggle do card Notificações.
  setNotificationPreference: (
    channel: NotificationChannel,
    templateId: string,
    enabled: boolean,
  ): Promise<NotificationPreferenceWire> =>
    callJson(
      `/v1/me/notification-preferences/${encodeURIComponent(channel)}/${encodeURIComponent(templateId)}`,
      { method: 'PUT', body: JSON.stringify({ enabled }) },
    ),

  // Brief 2B B4 — marcar no-show (gera Pendency de follow-up). If-Match obrigatório.
  markNoShowAppointment: (
    appointmentId: string,
    expectedVersion: number,
    body: { reason?: string; generatePendency?: boolean } = {},
  ): Promise<AppointmentWireItem> =>
    callJson(`/v1/appointments/${appointmentId}/no-show`, {
      method: 'POST',
      headers: { 'If-Match': expectedVersion.toString() },
      body: JSON.stringify(body),
    }),

  // Sprint Backend Operacional X (ADR-105) — CustomerCommitment.
  // Item NEW-C1 resolvido: substituiu fixture hardcoded em use-panel-fixtures
  // por hook real consumindo `GET /v1/conversations/:id/commitments`.
  listCustomerCommitments: (params: {
    conversationId: string;
    status?: 'pending' | 'fulfilled' | 'broken' | 'cancelled' | 'unclear';
  }): Promise<{
    data: {
      id: string;
      tenantId: string;
      conversationId: string;
      bondId: string | null;
      personId: string | null;
      title: string;
      quote: string | null;
      type: 'financial' | 'document' | 'action' | 'other';
      status: 'pending' | 'fulfilled' | 'broken' | 'cancelled' | 'unclear';
      detectedAt: string;
      dueDate: string | null;
      source: 'ai' | 'attendant' | 'customer' | 'external_event';
      aiAgentId: string | null;
      aiConfidence: number | null;
      fulfilledAt: string | null;
      fulfilledByUserId: string | null;
      cancellationReason: string | null;
      createdAt: string;
      updatedAt: string;
      version: number;
    }[];
  }> => {
    const search = new URLSearchParams();
    if (params.status !== undefined) search.set('status', params.status);
    const qs = search.toString();
    return callJson(`/v1/conversations/${params.conversationId}/commitments${qs ? `?${qs}` : ''}`, {
      method: 'GET',
    });
  },

  // ADR-105 — atualizar CustomerCommitment (status/título/dueDate/motivo).
  // If-Match obrigatório (ADR-071). Transições canônicas: pending→fulfilled/cancelled/broken;
  // unclear→pending (promover) / cancelled (descartar).
  updateCommitment: (
    commitmentId: string,
    expectedVersion: number,
    body: {
      status?: 'pending' | 'fulfilled' | 'broken' | 'cancelled' | 'unclear';
      title?: string;
      dueDate?: string;
      cancellationReason?: string;
    },
  ): Promise<CustomerCommitmentWireItem> =>
    callJson(`/v1/customer-commitments/${commitmentId}`, {
      method: 'PATCH',
      headers: { 'If-Match': expectedVersion.toString() },
      body: JSON.stringify(body),
    }),

  // Sprint Backend Operacional 2A r7b Agent Z — Pendency (ADR-109).
  // Substitui fixture `usePendencies` por API real. Wire types espelham
  // SerializedPendency canonical (snake_case status; overdue flag derivada).
  listPendenciesByConversation: (
    conversationId: string,
    params: { status?: PendencyWireStatus } = {},
  ): Promise<{ data: PendencyWireItem[] }> => {
    const search = new URLSearchParams();
    if (params.status !== undefined) search.set('status', params.status);
    const qs = search.toString();
    return callJson(`/v1/conversations/${conversationId}/pendencies${qs ? `?${qs}` : ''}`, {
      method: 'GET',
    });
  },

  listMyPendencies: (
    params: { ownerUserId?: string; bondId?: string; status?: PendencyWireStatus } = {},
  ): Promise<{ data: PendencyWireItem[] }> => {
    const search = new URLSearchParams();
    if (params.ownerUserId !== undefined) search.set('ownerUserId', params.ownerUserId);
    if (params.bondId !== undefined) search.set('bondId', params.bondId);
    if (params.status !== undefined) search.set('status', params.status);
    const qs = search.toString();
    return callJson(`/v1/pendencies${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },

  /** Item 346 D3 — métricas da própria caixa (caller-scoped pelo backend). */
  getMyPendencyMetrics: (): Promise<PersonalPendencyMetricsRaw> =>
    callJson('/v1/pendencies/my-metrics', { method: 'GET' }),

  createPendency: (body: {
    title: string;
    description?: string;
    conversationId?: string;
    bondId?: string;
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    delegatedToUserId?: string;
    deadline?: string;
    source?: string;
  }): Promise<PendencyWireItem> =>
    callJson('/v1/pendencies', { method: 'POST', body: JSON.stringify(body) }),

  updatePendency: (
    pendencyId: string,
    expectedVersion: number,
    body: {
      title?: string;
      description?: string;
      status?: PendencyWireStatus;
      priority?: 'low' | 'medium' | 'high' | 'urgent';
      delegatedToUserId?: string;
      deadline?: string;
    },
  ): Promise<PendencyWireItem> =>
    callJson(`/v1/pendencies/${pendencyId}`, {
      method: 'PATCH',
      headers: { 'If-Match': expectedVersion.toString() },
      body: JSON.stringify(body),
    }),

  resolvePendency: (
    pendencyId: string,
    expectedVersion: number,
    body: { resolutionNote?: string; outcome?: PendencyResolutionOutcome } = {},
  ): Promise<PendencyWireItem> =>
    callJson(`/v1/pendencies/${pendencyId}/resolve`, {
      method: 'POST',
      headers: { 'If-Match': expectedVersion.toString() },
      body: JSON.stringify(body),
    }),

  /** Brief 2B B1 — adiar pendência individual (status=postponed; postponeCount++). */
  postponePendency: (
    pendencyId: string,
    expectedVersion: number,
    body: { newDueAt: string; reason: string },
  ): Promise<PendencyWireItem> =>
    callJson(`/v1/pendencies/${pendencyId}/postpone`, {
      method: 'POST',
      headers: { 'If-Match': expectedVersion.toString() },
      body: JSON.stringify(body),
    }),

  /** Brief 2B B1 — retomar pendência adiada (postponed → open). */
  resumePendency: (
    pendencyId: string,
    expectedVersion: number,
    body: { reason?: string } = {},
  ): Promise<PendencyWireItem> =>
    callJson(`/v1/pendencies/${pendencyId}/resume`, {
      method: 'POST',
      headers: { 'If-Match': expectedVersion.toString() },
      body: JSON.stringify(body),
    }),

  // Fatia G — Peer-coordination (ADR-049 + Caderno cap 39).
  initiateHandoff: (body: {
    conversationId: string;
    mode: 'push' | 'pull';
    targetUserId?: string;
    targetQueue?: string;
    /** Fila do TIME (pull) — o engine já aceitava; a UI passou a oferecer em 04/Set/2026 (A-32). */
    targetTeamId?: string;
    reason: string;
    contextSummary: string;
    priority?: 'low' | 'normal' | 'urgent';
  }): Promise<{ handoffId: string }> =>
    callJson('/v1/handoffs', { method: 'POST', body: JSON.stringify(body) }),

  listHandoffs: (params: {
    status?: 'pending' | 'rejected' | 'cancelled' | 'completed';
    forUser?: string;
    forQueue?: string;
    limit?: number;
  }): Promise<{
    data: HandoffWireItem[];
  }> => {
    const search = new URLSearchParams();
    if (params.status !== undefined) search.set('status', params.status);
    if (params.forUser !== undefined) search.set('forUser', params.forUser);
    if (params.forQueue !== undefined) search.set('forQueue', params.forQueue);
    if (params.limit !== undefined) search.set('limit', params.limit.toString());
    const qs = search.toString();
    return callJson(`/v1/handoffs${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },

  // Item 293 — destinos de transferência (push). Gated `peer-coordination-handoff`.
  listHandoffTargets: (): Promise<{
    data: HandoffTargetWireItem[];
    /** Times ativos como destino (pull + targetTeamId) — aditivo em 04/Set/2026 (A-32). */
    teams?: HandoffTargetTeamWireItem[];
  }> => callJson('/v1/handoffs/targets', { method: 'GET' }),

  acceptHandoff: (id: string, version: number): Promise<{ version: number }> =>
    callJson(`/v1/handoffs/${id}/accept`, {
      method: 'POST',
      headers: { 'If-Match': version.toString() },
      body: JSON.stringify({ expectedVersion: version }),
    }),

  rejectHandoff: (id: string, version: number, reason: string): Promise<{ version: number }> =>
    callJson(`/v1/handoffs/${id}/reject`, {
      method: 'POST',
      headers: { 'If-Match': version.toString() },
      body: JSON.stringify({ expectedVersion: version, reason }),
    }),

  cancelHandoff: (id: string, version: number, reason?: string): Promise<{ version: number }> =>
    callJson(`/v1/handoffs/${id}/cancel`, {
      method: 'POST',
      headers: { 'If-Match': version.toString() },
      body: JSON.stringify({ expectedVersion: version, ...(reason !== undefined && { reason }) }),
    }),

  // Item 365 — teamScope='mine' restringe aos colegas dos times do caller (faixa
  // "Colegas do meu time" do Painel Pessoal); self-scoped, degrada honesto no backend.
  getTeamPresence: (teamScope?: 'mine'): Promise<{ data: PresenceWireItem[] }> =>
    callJson(`/v1/presence/team${teamScope === 'mine' ? '?teamScope=mine' : ''}`, {
      method: 'GET',
    }),

  updateOwnPresence: (body: {
    status?: 'online' | 'away' | 'busy' | 'offline' | 'on_call';
    customStatusMessage?: string;
    capacity?: number;
  }): Promise<PresenceWireItem> =>
    callJson('/v1/presence/me', { method: 'PATCH', body: JSON.stringify(body) }),

  // Painel V3 Inc 4 — «respaldo do supervisor»: identidade + presença peer-safe
  // (online/away, metric-free). `supervisor: null` ⇒ hierarquia não configurada.
  getMySupervisor: (): Promise<SupervisorRespaldoWire> =>
    callJson('/v1/presence/supervisor', { method: 'GET' }),

  listInternalNotes: (conversationId: string): Promise<{ data: InternalNoteWireItem[] }> =>
    callJson(`/v1/conversations/${conversationId}/internal-notes`, { method: 'GET' }),

  createInternalNote: (
    conversationId: string,
    body: {
      content: string;
      visibility?: 'public_team' | 'private_author' | 'private_supervisors';
    },
  ): Promise<{ noteId: string }> =>
    callJson(`/v1/conversations/${conversationId}/internal-notes`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateInternalNote: (
    noteId: string,
    version: number,
    body: {
      content?: string;
      visibility?: 'public_team' | 'private_author' | 'private_supervisors';
    },
  ): Promise<{ version: number }> =>
    callJson(`/v1/internal-notes/${noteId}`, {
      method: 'PATCH',
      headers: { 'If-Match': version.toString() },
      body: JSON.stringify({ expectedVersion: version, ...body }),
    }),

  deleteInternalNote: (noteId: string, version: number): Promise<{ version: number }> =>
    callJson(`/v1/internal-notes/${noteId}`, {
      method: 'DELETE',
      headers: { 'If-Match': version.toString() },
    }),

  listCoAttendants: (conversationId: string): Promise<{ data: CoAttendantWireItem[] }> =>
    callJson(`/v1/conversations/${conversationId}/co-attendants`, { method: 'GET' }),

  addCoAttendant: (
    conversationId: string,
    body: {
      userId: string;
      role: 'shadow_observer' | 'collaborative_observer' | 'supervisor_monitor';
    },
  ): Promise<{ coAttendantId: string }> =>
    callJson(`/v1/conversations/${conversationId}/co-attendants`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  removeCoAttendant: (conversationId: string, userId: string): Promise<{ removed: true }> =>
    callJson(`/v1/conversations/${conversationId}/co-attendants/${userId}`, {
      method: 'DELETE',
    }),

  // Sprint Frontend Desktop W3.2 — LGPD / Centro Direitos do Titular (Brief 5 B1).
  // Capabilities: lgpd-requests-read (Team+ supervisor / DPO) + lgpd-requests-create +
  //               lgpd-requests-approve + lgpd-anonymization-execute + lgpd-legal-holds-manage.
  // ADRs: ADR-024 + ADR-098 + ADR-110.

  listLgpdRequests: (params: {
    status?: string;
    requestType?: string;
    cursor?: string;
    limit?: number;
  }): Promise<ListLgpdRequestsResponse> => {
    const search = new URLSearchParams();
    if (params.status !== undefined) search.set('status', params.status);
    if (params.requestType !== undefined) search.set('requestType', params.requestType);
    if (params.cursor !== undefined) search.set('cursor', params.cursor);
    if (params.limit !== undefined) search.set('limit', params.limit.toString());
    const qs = search.toString();
    return callJson(`/v1/lgpd/requests${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },

  getLgpdRequest: (requestId: string): Promise<LgpdRequestWireItem> =>
    callJson(`/v1/lgpd/requests/${requestId}`, { method: 'GET' }),

  // R15 (Item 362): `requestDetails` é OBJETO no Zod do Engine (era string aqui —
  // drift que renderia 400); o controller devolve o request serializado completo.
  createLgpdRequest: (body: {
    requestType: string;
    requesterPersonId?: string;
    requesterSelfDeclaredData?: { name?: string; email?: string; cpf?: string; phone?: string };
    requestDetails?: Record<string, unknown>;
  }): Promise<LgpdRequestWireItem> =>
    callJson('/v1/lgpd/requests', { method: 'POST', body: JSON.stringify(body) }),

  transitionLgpdRequest: (
    requestId: string,
    version: number,
    body: {
      newStatus: string;
      rejectionReason?: string;
      responseLetter?: string;
      // ISO 8601 — obrigatório p/ identity_validated → in_processing (Step 3 LGPD §1.2).
      reviewedAt?: string;
    },
  ): Promise<LgpdRequestWireItem> =>
    callJson(`/v1/lgpd/requests/${requestId}/transition`, {
      method: 'PATCH',
      headers: { 'If-Match': version.toString() },
      body: JSON.stringify({ ...body, expectedVersion: version }),
    }),

  assignLgpdDpo: (
    requestId: string,
    version: number,
    dpoUserId: string,
  ): Promise<LgpdRequestWireItem> =>
    callJson(`/v1/lgpd/requests/${requestId}/assign-dpo`, {
      method: 'PATCH',
      headers: { 'If-Match': version.toString() },
      body: JSON.stringify({ dpoUserId, expectedVersion: version }),
    }),

  listLgpdAnonymizationsByRequest: (
    requestId: string,
  ): Promise<{ data: AnonymizationWireItem[] }> =>
    callJson(`/v1/lgpd/requests/${requestId}/anonymizations`, { method: 'GET' }),

  createAnonymization: (body: {
    entityType: string;
    entityId: string;
    level: string;
    lgpdRequestId?: string;
  }): Promise<{ anonymizationId: string }> =>
    callJson('/v1/lgpd/anonymizations', { method: 'POST', body: JSON.stringify(body) }),

  executeAnonymization: (anonymizationId: string): Promise<{ status: string }> =>
    callJson(`/v1/lgpd/anonymizations/${anonymizationId}/execute`, { method: 'POST' }),

  // ── Brief 5 B5 Anonimização (Tela 3) — contrato REAL triangulado (R15). ──
  // Métodos dedicados; os 3 acima são do drawer centro-direitos (contrato antigo
  // drifted — ver features/dpo/anonimizacao/types.ts). Aqui o serializer real:
  // create/execute/complete retornam o objeto serializado completo.
  listAnonymizationsByEntity: (
    entityType: string,
    entityId: string,
  ): Promise<{ data: AnonymizationFullWireItem[] }> =>
    callJson(
      `/v1/lgpd/anonymizations?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`,
      { method: 'GET' },
    ),

  createAnonymizationFull: (body: CreateAnonymizationRequest): Promise<AnonymizationFullWireItem> =>
    callJson('/v1/lgpd/anonymizations', { method: 'POST', body: JSON.stringify(body) }),

  // Fatia 1 motor ADR-110 — dry-run NÃO-destrutivo (zero mutação no BE).
  // Retorna o plano DIRETO (sem envelope {data}); v1 cobre entityType='person'.
  previewAnonymization: (body: PreviewAnonymizationRequest): Promise<AnonymizationPreviewPlan> =>
    callJson('/v1/lgpd/anonymizations/preview', { method: 'POST', body: JSON.stringify(body) }),

  executeAnonymizationFull: (anonymizationId: string): Promise<AnonymizationFullWireItem> =>
    callJson(`/v1/lgpd/anonymizations/${anonymizationId}/execute`, { method: 'POST' }),

  completeAnonymization: (
    anonymizationId: string,
    body: CompleteAnonymizationRequest,
  ): Promise<AnonymizationFullWireItem> =>
    callJson(`/v1/lgpd/anonymizations/${anonymizationId}/complete`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ── Brief 5 B4 Relatórios LGPD (módulo lgpd-reports / Fatia 1 backend). ──
  // Gate `lgpd-requests-management`. Tipos forenses exigem também
  // `audit-events-read-sensitive` (availableToCaller=false + 403 no generate).
  listLgpdReportTypes: (): Promise<{ data: LgpdReportTypeDescriptor[] }> =>
    callJson('/v1/lgpd/reports/types', { method: 'GET' }),

  generateLgpdReport: (body: GenerateLgpdReportRequest): Promise<GeneratedLgpdReport> =>
    callJson('/v1/lgpd/reports/generate', { method: 'POST', body: JSON.stringify(body) }),

  // ── Brief 5 B6 Guardianship / Curadoria (módulo adversarial). ──
  // Gate `legal-capacity-management` (Regulated). Revoke usa If-Match (version, ADR-071).
  listGuardianships: (wardId?: string): Promise<{ data: GuardianshipWireItem[] }> => {
    const qs = wardId !== undefined ? `?wardId=${encodeURIComponent(wardId)}` : '';
    return callJson(`/v1/guardianships${qs}`, { method: 'GET' });
  },

  getGuardianship: (id: string): Promise<GuardianshipWireItem> =>
    callJson(`/v1/guardianships/${encodeURIComponent(id)}`, { method: 'GET' }),

  createGuardianship: (body: CreateGuardianshipRequest): Promise<GuardianshipWireItem> =>
    callJson('/v1/guardianships', { method: 'POST', body: JSON.stringify(body) }),

  revokeGuardianship: (
    id: string,
    version: number,
    body: RevokeGuardianshipRequest,
  ): Promise<GuardianshipWireItem> =>
    callJson(`/v1/guardianships/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: JSON.stringify(body),
      headers: { 'If-Match': version.toString() },
    }),

  listLegalHolds: (
    params: { status?: string; entityId?: string } = {},
  ): Promise<ListLegalHoldsResponse> => {
    const search = new URLSearchParams();
    if (params.status !== undefined) search.set('status', params.status);
    if (params.entityId !== undefined) search.set('entityId', params.entityId);
    const qs = search.toString();
    return callJson(`/v1/lgpd/legal-holds${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },

  getLegalHold: (holdId: string): Promise<LegalHoldWireItem> =>
    callJson(`/v1/lgpd/legal-holds/${holdId}`, { method: 'GET' }),

  createLegalHold: (body: {
    entityType: string;
    entityId: string;
    holdType: string;
    reason: string;
    reference?: string;
    expiresAt?: string;
  }): Promise<{ holdId: string }> =>
    callJson('/v1/lgpd/legal-holds', { method: 'POST', body: JSON.stringify(body) }),

  liftLegalHold: (holdId: string, liftReason: string): Promise<{ status: string }> =>
    callJson(`/v1/lgpd/legal-holds/${holdId}/lift`, {
      method: 'PATCH',
      body: JSON.stringify({ liftReason }),
    }),

  // W1.4 — AuditController cascade endpoint (Item 135 RESOLVED).
  // Capability: audit-events-read (Team+); hasSensitive masking for callers without
  // audit-events-read-sensitive (defense-in-depth — ADR-107 cascade chain).
  getAuditCascade: (entityId: string): Promise<AuditCascadeResponse> =>
    callJson(`/v1/audit-events/cascade/${entityId}`, { method: 'GET' }),

  // Brief 5 FE Tela 6 (A5 Audit revamp · #310) — explorador de auditoria.
  // Tipos triangulados (R15) contra `AuditService.serialize` real (10 campos,
  // `occurredAt`, `{ items, nextCursor }`, cascade `{ root, children }`).
  // audit-events-read (Team+); payload mascarado sem audit-events-read-sensitive.
  listAuditEvents: (filters: AuditListFilters = {}): Promise<AuditListResultWire> => {
    const qs = new URLSearchParams();
    if (filters.eventType !== undefined) qs.set('eventType', filters.eventType);
    if (filters.actor !== undefined) qs.set('actor', filters.actor);
    if (filters.since !== undefined) qs.set('since', filters.since);
    if (filters.until !== undefined) qs.set('until', filters.until);
    if (filters.entityType !== undefined) qs.set('entityType', filters.entityType);
    if (filters.entityId !== undefined) qs.set('entityId', filters.entityId);
    if (filters.severity !== undefined) qs.set('severity', filters.severity);
    if (filters.category !== undefined) qs.set('category', filters.category);
    if (filters.agent !== undefined) qs.set('agent', filters.agent);
    if (filters.cursor !== undefined) qs.set('cursor', filters.cursor);
    if (filters.limit !== undefined) qs.set('limit', String(filters.limit));
    const suffix = qs.toString();
    return callJson(`/v1/audit-events${suffix ? `?${suffix}` : ''}`, { method: 'GET' });
  },

  getAuditTimeline: (filters: AuditTimelineFilters = {}): Promise<AuditTimelineResultWire> => {
    const qs = new URLSearchParams();
    if (filters.bucket !== undefined) qs.set('bucket', filters.bucket);
    if (filters.eventType !== undefined) qs.set('eventType', filters.eventType);
    if (filters.actor !== undefined) qs.set('actor', filters.actor);
    if (filters.since !== undefined) qs.set('since', filters.since);
    if (filters.until !== undefined) qs.set('until', filters.until);
    if (filters.entityType !== undefined) qs.set('entityType', filters.entityType);
    if (filters.entityId !== undefined) qs.set('entityId', filters.entityId);
    if (filters.severity !== undefined) qs.set('severity', filters.severity);
    if (filters.category !== undefined) qs.set('category', filters.category);
    if (filters.agent !== undefined) qs.set('agent', filters.agent);
    const suffix = qs.toString();
    return callJson(`/v1/audit-events/timeline${suffix ? `?${suffix}` : ''}`, { method: 'GET' });
  },

  getAuditEvent: (id: string): Promise<AuditEventWire> =>
    callJson(`/v1/audit-events/${id}`, { method: 'GET' }),

  getAuditCascadeChain: (entityId: string): Promise<AuditCascadeResultWire> =>
    callJson(`/v1/audit-events/cascade/${entityId}`, { method: 'GET' }),

  /**
   * Exporta o conjunto FILTRADO de eventos (não a página) em CSV/JSON (A5).
   * Mesmos filtros do list; payload mascarado igual à tela (defense-in-depth no
   * backend). Retorna Blob para download autenticado (mesmo molde do export de
   * pendências). PDF assinado fica adiado (infra ICP-Brasil ausente).
   */
  exportAuditEvents: async (
    filters: AuditListFilters = {},
    format: 'csv' | 'json' = 'csv',
  ): Promise<Blob> => {
    const qs = new URLSearchParams();
    qs.set('format', format);
    if (filters.eventType !== undefined) qs.set('eventType', filters.eventType);
    if (filters.actor !== undefined) qs.set('actor', filters.actor);
    if (filters.since !== undefined) qs.set('since', filters.since);
    if (filters.until !== undefined) qs.set('until', filters.until);
    if (filters.entityType !== undefined) qs.set('entityType', filters.entityType);
    if (filters.entityId !== undefined) qs.set('entityId', filters.entityId);
    if (filters.severity !== undefined) qs.set('severity', filters.severity);
    if (filters.category !== undefined) qs.set('category', filters.category);
    if (filters.agent !== undefined) qs.set('agent', filters.agent);
    const res = await rawFetch(`/v1/audit-events/export?${qs.toString()}`, { method: 'GET' });
    if (!res.ok) throw await parseError(res);
    return res.blob();
  },

  // Fatia B + B-bis — Bonds (ADR-037 / 129 / 130 / 131).
  // Capability `bond-basic` (Solo+) requerida server-side.
  getBond: (bondId: string): Promise<BondWireItem> =>
    callJson(`/v1/bonds/${bondId}`, { method: 'GET' }),

  // Item 363 F1 — descongela alterações cadastrais do Bond (revisão humana da
  // Pendency ALTA de fraude). Cap `override-gatilho-protecao` (Team+) server-side.
  unfreezeBondCadastral: (bondId: string, reason?: string): Promise<BondWireItem> =>
    callJson(`/v1/bonds/${encodeURIComponent(bondId)}/unfreeze-cadastral`, {
      method: 'POST',
      body: JSON.stringify(reason !== undefined && reason !== '' ? { reason } : {}),
    }),

  listBondParticipants: (
    bondId: string,
    params: { includeRevoked?: boolean } = {},
  ): Promise<{ data: BondParticipantWireItem[] }> => {
    const qs = params.includeRevoked === true ? '?includeRevoked=true' : '';
    return callJson(`/v1/bonds/${bondId}/participants${qs}`, { method: 'GET' });
  },

  listBondRelatedPersons: (
    bondId: string,
    params: { includeTerminated?: boolean } = {},
  ): Promise<{ data: BondRelatedPersonWireItem[] }> => {
    const qs = params.includeTerminated === true ? '?includeTerminated=true' : '';
    return callJson(`/v1/bonds/${bondId}/related-persons${qs}`, { method: 'GET' });
  },

  listBondAuthorizedContacts: (
    bondId: string,
    params: { includeRevoked?: boolean } = {},
  ): Promise<{ data: BondAuthorizedContactWireItem[] }> => {
    const qs = params.includeRevoked === true ? '?includeRevoked=true' : '';
    return callJson(`/v1/bonds/${bondId}/authorized-contacts${qs}`, { method: 'GET' });
  },

  // Visão tenant-wide (oversight DPO · Centro de Direitos). Gated server-side
  // por `lgpd-authorized-contacts-read`. Enriquecida com personName + bondType.
  listTenantAuthorizedContacts: (
    params: { includeRevoked?: boolean } = {},
  ): Promise<{ data: TenantAuthorizedContactWireItem[] }> => {
    const qs = params.includeRevoked === true ? '?includeRevoked=true' : '';
    return callJson(`/v1/lgpd/authorized-contacts${qs}`, { method: 'GET' });
  },

  addBondAuthorizedContact: (
    bondId: string,
    body: AddAuthorizedContactRequest,
  ): Promise<BondAuthorizedContactWireItem> =>
    callJson(`/v1/bonds/${bondId}/authorized-contacts`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // PATCH/DELETE usam optimistic-lock por If-Match (ADR-071): header com a version.
  updateBondAuthorizedContact: (
    bondId: string,
    contactId: string,
    version: number,
    body: UpdateAuthorizedContactRequest,
  ): Promise<BondAuthorizedContactWireItem> =>
    callJson(`/v1/bonds/${bondId}/authorized-contacts/${encodeURIComponent(contactId)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'If-Match': version.toString() },
    }),

  revokeBondAuthorizedContact: (
    bondId: string,
    contactId: string,
    version: number,
    body: RevokeAuthorizedContactRequest,
  ): Promise<BondAuthorizedContactWireItem> =>
    callJson(`/v1/bonds/${bondId}/authorized-contacts/${encodeURIComponent(contactId)}`, {
      method: 'DELETE',
      body: JSON.stringify(body),
      headers: { 'If-Match': version.toString() },
    }),

  // ─── Bond related-persons (ADR-129) — vínculos factuais, 3 mutadores ──────
  addBondRelatedPerson: (
    bondId: string,
    body: AddRelatedPersonRequest,
  ): Promise<BondRelatedPersonWireItem> =>
    callJson(`/v1/bonds/${bondId}/related-persons`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // PATCH/DELETE usam optimistic-lock por If-Match (ADR-071): header com a version.
  updateBondRelatedPerson: (
    bondId: string,
    relatedPersonId: string,
    version: number,
    body: UpdateRelatedPersonRequest,
  ): Promise<BondRelatedPersonWireItem> =>
    callJson(`/v1/bonds/${bondId}/related-persons/${encodeURIComponent(relatedPersonId)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'If-Match': version.toString() },
    }),

  // DELETE = terminate soft (status→terminated); endedAt/reason opcionais.
  terminateBondRelatedPerson: (
    bondId: string,
    relatedPersonId: string,
    version: number,
    body: TerminateRelatedPersonRequest,
  ): Promise<BondRelatedPersonWireItem> =>
    callJson(`/v1/bonds/${bondId}/related-persons/${encodeURIComponent(relatedPersonId)}`, {
      method: 'DELETE',
      body: JSON.stringify(body),
      headers: { 'If-Match': version.toString() },
    }),

  // ─── Bond participants (ADR-130) — papel + permissões (ACL), 3 mutadores ──
  addBondParticipant: (
    bondId: string,
    body: AddParticipantRequest,
  ): Promise<BondParticipantWireItem> =>
    callJson(`/v1/bonds/${bondId}/participants`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // PATCH usa optimistic-lock por If-Match (ADR-071): header com a version.
  updateBondParticipant: (
    bondId: string,
    participantId: string,
    version: number,
    body: UpdateParticipantRequest,
  ): Promise<BondParticipantWireItem> =>
    callJson(`/v1/bonds/${bondId}/participants/${encodeURIComponent(participantId)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'If-Match': version.toString() },
    }),

  // DELETE = revoke; reason vai via QUERY param (não body) + If-Match version.
  revokeBondParticipant: (
    bondId: string,
    participantId: string,
    version: number,
    reason?: string,
  ): Promise<BondParticipantWireItem> => {
    const qs = reason !== undefined && reason !== '' ? `?reason=${encodeURIComponent(reason)}` : '';
    return callJson(`/v1/bonds/${bondId}/participants/${encodeURIComponent(participantId)}${qs}`, {
      method: 'DELETE',
      headers: { 'If-Match': version.toString() },
    });
  },

  // ─── Item 377 (S4 PR-b) — Carteira de Vendedor (customer-portfolio) ───────
  // Vínculo cliente↔operador consumido pelo roteamento de CRIAÇÃO (ANTES da
  // cascata). Caps: `customer-portfolio-read` (GET) / `customer-portfolio-manage`
  // (assign/remove). DELETE exige If-Match (ADR-071).

  listCustomerPortfolio: (
    params: { personId?: string; operatorUserId?: string; includeInactive?: boolean } = {},
  ): Promise<{ data: PortfolioEntryWireItem[] }> => {
    const qs = new URLSearchParams();
    if (params.personId !== undefined) qs.set('personId', params.personId);
    if (params.operatorUserId !== undefined) qs.set('operatorUserId', params.operatorUserId);
    if (params.includeInactive === true) qs.set('includeInactive', 'true');
    const query = qs.toString();
    return callJson(`/v1/customer-portfolio${query.length > 0 ? `?${query}` : ''}`, {
      method: 'GET',
    });
  },

  assignCustomerPortfolio: (body: {
    personId: string;
    operatorUserId: string;
  }): Promise<{ data: PortfolioEntryWireItem }> =>
    callJson('/v1/customer-portfolio', { method: 'POST', body: JSON.stringify(body) }),

  removeCustomerPortfolio: (id: string, version: number): Promise<void> =>
    callJson(`/v1/customer-portfolio/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: { 'If-Match': version.toString() },
    }),

  // ─── Item 36 (PR-B2) — Documents ────────────────────────────────────────

  /**
   * Lista documentos de um bond com paginação cursor-based.
   * `GET /v1/documents?bondId=<bondId>[&search=<s>][&limit=<n>][&cursor=<c>]`
   * Capability `document-attach` requerida server-side.
   */
  /**
   * `bondId` opcional desde o Localizador Universal (⌘K): com bond, o engine usa
   * `listForBondPicker` (documentos elegíveis para anexar); sem bond, cai no
   * `list()` global, que passou a honrar `search` por nome na mesma frente.
   */
  listDocuments: (opts: {
    bondId?: string;
    search?: string;
    limit?: number;
    cursor?: string;
  }): Promise<{ data: DocumentSummary[]; nextCursor: string | null; hasMore: boolean }> => {
    const params = new URLSearchParams();
    if (opts.bondId !== undefined) params.set('bondId', opts.bondId);
    if (opts.search !== undefined) params.set('search', opts.search);
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts.cursor !== undefined) params.set('cursor', opts.cursor);
    return callJson(`/v1/documents?${params.toString()}`, { method: 'GET' });
  },

  /**
   * Faz upload multipart de um arquivo e retorna o DocumentSummary criado.
   * `POST /v1/documents` — multipart/form-data.
   *
   * NÃO setar Content-Type manualmente — browser injeta boundary automaticamente.
   * Capability `document-attach` requerida server-side.
   */
  uploadDocument: (
    file: File,
    opts: { name?: string; type?: string },
  ): Promise<DocumentSummary & { deduplicated: boolean }> => {
    const formData = new FormData();
    formData.append('file', file);
    if (opts.name !== undefined) formData.append('name', opts.name);
    if (opts.type !== undefined) formData.append('type', opts.type);
    return callFormData(`/v1/documents`, formData);
  },

  // ─── Brief 5 A3 Branding — /v1/tenant-branding (ADR-145, PR #312) ──────
  // Gate `tenant-config-read` (GET) / `tenant-config-manage` (escrita). Escrita
  // exige tier >= Team no service (Solo → 403). Assets via multipart dedicado.
  getTenantBranding: (): Promise<{ data: SerializedTenantBranding }> =>
    callJson(`/v1/tenant-branding`, { method: 'GET' }),
  updateTenantBranding: (
    body: UpdateTenantBrandingRequest,
  ): Promise<{ data: SerializedTenantBranding }> =>
    callJson(`/v1/tenant-branding`, { method: 'PATCH', body: JSON.stringify(body) }),
  uploadTenantBrandingAsset: (
    kind: BrandingAssetKind,
    file: File,
  ): Promise<{ data: SerializedTenantBranding }> => {
    const formData = new FormData();
    formData.append('file', file);
    return callFormData(`/v1/tenant-branding/${kind}`, formData);
  },
  deleteTenantBrandingAsset: (
    kind: BrandingAssetKind,
  ): Promise<{ data: SerializedTenantBranding }> =>
    callJson(`/v1/tenant-branding/${kind}`, { method: 'DELETE' }),

  // ─── Brief 5 A2 Config Tenant — /v1/tenant-config + operation-profile ──
  // Gate `tenant-config-read` (GET) / `tenant-config-manage` (escrita).
  // tenant-config PATCH é `.strict()`: só aceita os 3 campos operacionais.
  // operation-profile/apply seta privacyProfile + defaultRoutingStrategy
  // (evento auditado `high` — #313, ADR-030 Eixo N).
  getTenantConfig: (): Promise<{ data: SerializedTenantConfig }> =>
    callJson(`/v1/tenant-config`, { method: 'GET' }),
  updateTenantConfig: (
    body: UpdateTenantConfigRequest,
  ): Promise<{ data: SerializedTenantConfig }> =>
    callJson(`/v1/tenant-config`, { method: 'PATCH', body: JSON.stringify(body) }),
  /**
   * Fase 2 «Conduta e Contexto» — a conduta RESOLVIDA (efetivo + origem por eixo).
   *
   * `playbookId` ausente ⇒ cascata Tenant ▸ plataforma (a pergunta da tela do Tenant).
   * Presente ⇒ a cascata inteira, incl. a persona que ESTE playbook aponta.
   *
   * A cascata NÃO se refaz aqui de propósito: duas implementações da mesma precedência
   * divergem, e a divergência aparece como «a IA ignorou minha configuração».
   */
  getEffectiveConductProfile: (playbookId?: string): Promise<{ data: ConductProfileResolution }> =>
    callJson(
      playbookId === undefined
        ? `/v1/conduct-profile/effective`
        : `/v1/conduct-profile/effective?playbookId=${encodeURIComponent(playbookId)}`,
      { method: 'GET' },
    ),
  /** Os playbooks ATIVOS que apontam para uma persona (a cadeia do meio bifurca). */
  getPersonaConductUsage: (
    personaId: string,
  ): Promise<{ data: { playbooks: { id: string; name: string }[] } }> =>
    callJson(`/v1/conduct-profile/persona-usage?personaId=${encodeURIComponent(personaId)}`, {
      method: 'GET',
    }),
  getOperationProfile: (): Promise<{ data: OperationProfileStateWire }> =>
    callJson(`/v1/tenant-operation-profile`, { method: 'GET' }),
  applyOperationProfile: (
    profile: OperationProfileId,
  ): Promise<{ data: OperationProfileStateWire }> =>
    callJson(`/v1/tenant-operation-profile/apply`, {
      method: 'POST',
      body: JSON.stringify({ profile }),
    }),

  // ─── W3.3 Tenant Lifecycle — T13 (ADR-111) ─────────────────────────────

  getTenantLifecycleDashboard: (
    params: {
      status?: string;
      packageType?: string;
      cursor?: string;
      limit?: number;
    } = {},
  ): Promise<{
    items: TenantLifecycleSnapshotWire[];
    nextCursor: string | null;
    totals: Record<string, number>;
  }> => {
    const search = new URLSearchParams();
    if (params.status) search.set('status', params.status);
    if (params.packageType) search.set('packageType', params.packageType);
    if (params.cursor) search.set('cursor', params.cursor);
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    const qs = search.toString() ? `?${search.toString()}` : '';
    return callJson(`/v1/tenants/lifecycle/dashboard${qs}`, { method: 'GET' });
  },

  getTenantLifecycleDetail: (
    tenantId: string,
  ): Promise<{
    tenant: TenantLifecycleSnapshotWire;
    history: TenantLifecycleHistoryEventWire[];
  }> => callJson(`/v1/tenants/${tenantId}/lifecycle`, { method: 'GET' }),

  transitionTenantStatus: (
    tenantId: string,
    body: { toStatus: string; reason?: string | null },
    version: number,
  ): Promise<TenantLifecycleSnapshotWire> =>
    callJson(`/v1/tenants/${tenantId}/lifecycle/transition`, {
      method: 'POST',
      headers: { 'If-Match': String(version), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  initiateChurning: (
    tenantId: string,
    body: {
      reason: string;
      anonymizationLevel?: 'mask' | 'pseudonymize' | 'hash_irreversible';
      cancellablePeriodDays?: number;
    },
    version: number,
  ): Promise<TenantLifecycleSnapshotWire> =>
    callJson(`/v1/tenants/${tenantId}/lifecycle/churning/initiate`, {
      method: 'POST',
      headers: { 'If-Match': String(version), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  cancelChurning: (
    tenantId: string,
    body: { reason: string },
    version: number,
  ): Promise<TenantLifecycleSnapshotWire> =>
    callJson(`/v1/tenants/${tenantId}/lifecycle/churning/cancel`, {
      method: 'POST',
      headers: { 'If-Match': String(version), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  // ─── Fase 2 multi-modelo — IA admin (Brief 5 §7.5, ADR-123) ──────────────
  listAiAgents: (): Promise<ListAgentsResponse> =>
    callJson<ListAgentsResponse>('/v1/tenant-ai-config/agents', { method: 'GET' }),

  listAiAvailableModels: (): Promise<ListProvidersResponse> =>
    callJson<ListProvidersResponse>('/v1/tenant-ai-config/available-models', { method: 'GET' }),

  setAiAgentModel: (agentId: string, model: string): Promise<SingleAgentResponse> =>
    callJson<SingleAgentResponse>(`/v1/tenant-ai-config/agents/${agentId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model }),
    }),

  // Item 566 — o «pensar» do agente; `null` volta a «não escolheu».
  setAiAgentReasoning: (
    agentId: string,
    effort: 'none' | 'low' | 'medium' | null,
  ): Promise<SingleAgentResponse> =>
    callJson<SingleAgentResponse>(`/v1/tenant-ai-config/agents/${agentId}/reasoning`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ effort }),
    }),

  clearAiAgentModel: (agentId: string): Promise<SingleAgentResponse> =>
    callJson<SingleAgentResponse>(`/v1/tenant-ai-config/agents/${agentId}`, { method: 'DELETE' }),

  // Wire cru `{ enabled }` (sem envelope data — confirmado contra o engine;
  // igual aos demais endpoints escalares de tenant-ai-config).
  getFailoverEnabled: (): Promise<FailoverConfigWire> =>
    callJson<FailoverConfigWire>('/v1/tenant-ai-config/failover', { method: 'GET' }),

  setFailoverEnabled: (enabled: boolean): Promise<FailoverConfigWire> =>
    callJson<FailoverConfigWire>('/v1/tenant-ai-config/failover', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    }),

  // Fast-follow — escala de dinamismo do behavioral-agent (econômico/equilibrado/cobertura_total).
  // Wire cru `{ scale }` (sem envelope data — confirmado contra o engine).
  getDynamismScale: (): Promise<DynamismScaleResponse> =>
    callJson<DynamismScaleResponse>('/v1/tenant-ai-config/dynamism-scale', { method: 'GET' }),

  setDynamismScale: (scale: DynamismScaleWire): Promise<DynamismScaleResponse> =>
    callJson<DynamismScaleResponse>('/v1/tenant-ai-config/dynamism-scale', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ scale }),
    }),

  setAgentFallback: (agentId: string, models: string[]): Promise<SetAgentFallbackResponse> =>
    callJson<SetAgentFallbackResponse>(`/v1/tenant-ai-config/agents/${agentId}/fallback`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ models }),
    }),

  // Task 6 — perfis de operação de IA (econômico / equilibrado / cobertura_total).
  getAiProfiles: (): Promise<ListAiProfilesResponse> =>
    callJson<ListAiProfilesResponse>('/v1/tenant-ai-config/ai-profile', { method: 'GET' }),

  applyAiProfile: (profile: string): Promise<ApplyAiProfileResponse> =>
    callJson<ApplyAiProfileResponse>('/v1/tenant-ai-config/ai-profile/apply', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ profile }),
    }),

  setAgentActivationMode: (agentId: string, mode: string): Promise<{ ok: boolean }> =>
    callJson<{ ok: boolean }>(`/v1/tenant-ai-config/agents/${agentId}/activation-mode`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mode }),
    }),

  // ─── IA como serviço S2 — carteira de IA (`ai-billing-read`/`-manage`) ───
  // Quatro leituras, nenhuma mutação: recarga e ajuste nascem no console da
  // plataforma (`admin-lume`, S5). O tenant vê o próprio dinheiro; não o move.
  getAiWallet: (): Promise<WalletSnapshotWire> =>
    callJson<WalletSnapshotWire>('/v1/ai-wallet', { method: 'GET' }),

  listAiWalletEntries: (cursor?: string, limit?: number): Promise<WalletEntriesPageWire> => {
    const qs = new URLSearchParams();
    if (cursor !== undefined && cursor !== '') qs.set('cursor', cursor);
    if (limit !== undefined) qs.set('limit', String(limit));
    const suffix = qs.toString();
    return callJson<WalletEntriesPageWire>(
      `/v1/ai-wallet/entries${suffix === '' ? '' : `?${suffix}`}`,
      { method: 'GET' },
    );
  },

  // `200` com `null` (não 404) quando o tenant ainda não tem contrato — ausência
  // de contrato é estado legítimo do produto, não erro de rota.
  getAiWalletContract: (): Promise<WalletContractWire> =>
    callJson<WalletContractWire>('/v1/ai-wallet/contract', { method: 'GET' }),

  // ─── Base de conhecimento do tenant (`knowledge-base-author`, só admin_tenant) ───
  // Contrato: spec 2026-10-08 §2. A lista vem inteira (sem paginação no contrato); a tela
  // filtra no cliente. `ratifiedBy/At` são carimbados pelo servidor — nunca enviados.
  listKnowledgeEntries: (): Promise<ListKnowledgeEntriesResponse> =>
    callJson<ListKnowledgeEntriesResponse>('/v1/knowledge-entries', { method: 'GET' }),

  createKnowledgeEntry: (body: CreateKnowledgeEntryBody): Promise<KnowledgeEntryDto> =>
    callJson<KnowledgeEntryDto>('/v1/knowledge-entries', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateKnowledgeEntry: (id: string, body: UpdateKnowledgeEntryBody): Promise<KnowledgeEntryDto> =>
    callJson<KnowledgeEntryDto>(`/v1/knowledge-entries/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  deleteKnowledgeEntry: (id: string): Promise<void> =>
    callJson<void>(`/v1/knowledge-entries/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /**
   * Download autenticado do extrato — mesmo molde de `exportAuditEvents`. Cap
   * `ai-billing-manage`.
   *
   * Não existe um `aiWalletCsvUrl` ao lado deste método, embora o plano da Task 14 o
   * listasse: `rawFetch` põe `Authorization` e `X-Tenant-Slug` em HEADER (não há cookie de
   * sessão neste app), então uma URL absoluta do CSV só serviria para virar `href` — e um
   * `href` para cá chega ao engine sem credencial e volta 401. Uma função exportada que
   * ninguém chama, num arquivo de dinheiro, é convite a alguém usá-la para exatamente isso.
   */
  exportAiWalletCsv: async (since: string, until: string): Promise<Blob> => {
    const qs = new URLSearchParams({ since, until });
    const res = await rawFetch(`/v1/ai-wallet/entries.csv?${qs.toString()}`, { method: 'GET' });
    if (!res.ok) throw await parseError(res);
    return res.blob();
  },

  // ─── Sub-fatia 10.0 PR 3 — Customer Context (ADR-142) ────────────────────
  getCustomerContext: (conversationId: string): Promise<CustomerContextResponse> =>
    callJson<CustomerContextResponse>(`/v1/conversations/${conversationId}/customer-context`, {
      method: 'GET',
    }),

  refreshPersonFromErp: (personId: string): Promise<{ outcome: string; status: string }> =>
    callJson<{ outcome: string; status: string }>(`/v1/persons/${personId}/refresh-from-erp`, {
      method: 'POST',
    }),

  // ─── Sprint A4 S1 PR 3 — Admin Integrations (Item 308) ───────────────────
  listMcpConnectors: (): Promise<McpConnectorWire[]> =>
    callJson<McpConnectorWire[]>('/v1/admin/integrations/mcp-connectors', { method: 'GET' }),

  getMcpConnector: (id: string): Promise<McpConnectorWire> =>
    callJson<McpConnectorWire>(`/v1/admin/integrations/mcp-connectors/${id}`, { method: 'GET' }),

  createMcpConnector: (input: CreateMcpConnectorInput): Promise<McpConnectorWire> =>
    callJson<McpConnectorWire>('/v1/admin/integrations/mcp-connectors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),

  updateMcpConnector: (
    id: string,
    input: UpdateMcpConnectorInput,
    version: number,
  ): Promise<McpConnectorWire> =>
    callJson<McpConnectorWire>(`/v1/admin/integrations/mcp-connectors/${id}`, {
      method: 'PATCH',
      headers: { 'If-Match': String(version), 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),

  deleteMcpConnector: (id: string, version: number): Promise<void> =>
    callJson<void>(`/v1/admin/integrations/mcp-connectors/${id}`, {
      method: 'DELETE',
      headers: { 'If-Match': String(version) },
    }),

  testMcpHandshake: (id: string): Promise<{ status: 'pending'; connectorId: string }> =>
    callJson<{ status: 'pending'; connectorId: string }>(
      `/v1/admin/integrations/mcp-connectors/${id}/test-handshake`,
      { method: 'POST' },
    ),

  rotateMcpCredentials: (
    id: string,
    input: RotateCredentialsInput,
    version: number,
  ): Promise<McpConnectorWire> =>
    callJson<McpConnectorWire>(`/v1/admin/integrations/mcp-connectors/${id}/rotate-credentials`, {
      method: 'POST',
      headers: { 'If-Match': String(version), 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),

  // ── F2 «Canal como produto» — saúde, teste e credencial do canal ──────────
  // A tela lê estes três para responder «este canal está de pé?» sem log de
  // container. `connected` NÃO é «tem credencial»: em 15/Set/2026 o segredo estava
  // presente e errado, e a pergunta da presença respondia «sim» com o inbound morto.

  listChannelConfigs: (): Promise<ChannelConfigWire[]> =>
    callJson<{ data: ChannelConfigWire[] }>('/v1/tenant-channel-configs', { method: 'GET' }).then(
      (r) => r.data,
    ),

  getChannelHealth: (id: string): Promise<ChannelHealthWire> =>
    callJson<{ data: ChannelHealthWire }>(`/v1/tenant-channel-configs/${id}/health`, {
      method: 'GET',
    }).then((r) => r.data),

  testChannelConnection: (id: string): Promise<ChannelTestWire> =>
    callJson<{ data: ChannelTestWire }>(`/v1/tenant-channel-configs/${id}/test`, {
      method: 'POST',
    }).then((r) => r.data),

  /** O corpo carrega o segredo; a resposta devolve só data e versão. */
  writeChannelCredential: (
    id: string,
    version: number,
    secret: string,
  ): Promise<{ credentialRotatedAt: string; version: number }> =>
    callJson<{ data: { credentialRotatedAt: string; version: number } }>(
      `/v1/tenant-channel-configs/${id}/credentials`,
      {
        method: 'POST',
        headers: { 'If-Match': String(version), 'Content-Type': 'application/json' },
        body: JSON.stringify({ secret }),
      },
    ).then((r) => r.data),

  /**
   * Liga/desliga o canal (Item 505). É `PATCH` com `If-Match`, como toda mutação de
   * config: dois operadores mexendo no mesmo canal não podem sobrescrever um ao outro em
   * silêncio — e este é um campo cujo valor errado tira o cliente do ar.
   */
  setChannelEnabled: (id: string, version: number, enabled: boolean): Promise<ChannelConfigWire> =>
    callJson<{ data: ChannelConfigWire }>(`/v1/tenant-channel-configs/${id}`, {
      method: 'PATCH',
      headers: { 'If-Match': String(version), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: enabled ? 'active' : 'inactive' }),
    }).then((r) => r.data),

  // ── W2 — conectar WhatsApp pelo popup da Meta (spec 2026-09-29 §6.2) ───────
  // O `code` do popup vale 30 s: `postOnboardingResult` é chamado no instante em que o
  // popup fecha e nunca guarda nada localmente.

  getOnboardingAvailability: (): Promise<OnboardingAvailabilityWire> =>
    callJson<{ data: OnboardingAvailabilityWire }>(
      '/v1/tenant-channel-configs/whatsapp/onboarding/availability',
      { method: 'GET' },
    ).then((r) => r.data),

  getCurrentOnboardingAttempt: (): Promise<OnboardingAttemptWire | null> =>
    callJson<{ data: OnboardingAttemptWire | null }>(
      '/v1/tenant-channel-configs/whatsapp/onboarding/attempts/current',
      { method: 'GET' },
    ).then((r) => r.data),

  startOnboardingAttempt: (body: {
    path: OnboardingPathWire;
    history?: { import: boolean; retentionMonths: 6 | 12 | 24 | 60 };
  }): Promise<OnboardingStartWire> =>
    callJson<{ data: OnboardingStartWire }>(
      '/v1/tenant-channel-configs/whatsapp/onboarding/attempts',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    ).then((r) => r.data),

  postOnboardingResult: (id: string, body: PopupResultWire): Promise<OnboardingAttemptWire> =>
    callJson<{ data: OnboardingAttemptWire }>(
      `/v1/tenant-channel-configs/whatsapp/onboarding/attempts/${id}/result`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    ).then((r) => r.data),

  resumeOnboardingAttempt: (id: string, pin?: string): Promise<OnboardingAttemptWire> =>
    callJson<{ data: OnboardingAttemptWire }>(
      `/v1/tenant-channel-configs/whatsapp/onboarding/attempts/${id}/resume`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pin === undefined ? {} : { pin }),
      },
    ).then((r) => r.data),

  cancelOnboardingAttempt: (id: string): Promise<OnboardingAttemptWire> =>
    callJson<{ data: OnboardingAttemptWire }>(
      `/v1/tenant-channel-configs/whatsapp/onboarding/attempts/${id}/cancel`,
      { method: 'POST' },
    ).then((r) => r.data),

  disconnectWhatsApp: (): Promise<{ unsubscribed: boolean }> =>
    callJson<{ data: { unsubscribed: boolean } }>(
      '/v1/tenant-channel-configs/whatsapp/onboarding/disconnect',
      { method: 'POST' },
    ).then((r) => r.data),

  refreshRemoteHealth: (id: string): Promise<RemoteHealthWire> =>
    callJson<{ data: RemoteHealthWire }>(`/v1/tenant-channel-configs/${id}/remote-health/refresh`, {
      method: 'POST',
    }).then((r) => r.data),

  resubscribeChannel: (id: string): Promise<RemoteHealthWire> =>
    callJson<{ data: RemoteHealthWire }>(
      `/v1/tenant-channel-configs/${id}/remote-health/resubscribe`,
      { method: 'POST' },
    ).then((r) => r.data),

  pauseIntegrations: (): Promise<{ paused: true; pausedAt: string }> =>
    callJson<{ paused: true; pausedAt: string }>('/v1/admin/integrations/pause', {
      method: 'POST',
    }),

  resumeIntegrations: (): Promise<{ paused: false }> =>
    callJson<{ paused: false }>('/v1/admin/integrations/resume', { method: 'POST' }),

  // ─── A4 REVAMP — Reconciliation (Brief 5 Fatia 3a · #314) ────────────────
  getIntegrationMetrics: (): Promise<IntegrationMetricsWire> =>
    callJson<IntegrationMetricsWire>('/v1/admin/integrations/metrics', { method: 'GET' }),

  runReconciliation: (): Promise<ReconciliationRunResultWire> =>
    callJson<ReconciliationRunResultWire>('/v1/admin/integrations/reconcile', { method: 'POST' }),

  listReconciliationFlags: (params?: {
    externalSystemId?: string;
    status?: string;
  }): Promise<{ data: ReconciliationFlagWire[] }> => {
    const qs = new URLSearchParams();
    if (params?.externalSystemId !== undefined) qs.set('externalSystemId', params.externalSystemId);
    if (params?.status !== undefined) qs.set('status', params.status);
    const suffix = qs.toString();
    return callJson<{ data: ReconciliationFlagWire[] }>(
      `/v1/admin/integrations/reconciliation-flags${suffix ? `?${suffix}` : ''}`,
      { method: 'GET' },
    );
  },

  resolveReconciliationFlag: (
    id: string,
    input: ResolveReconciliationFlagInput,
  ): Promise<{ data: ReconciliationFlagWire }> =>
    callJson<{ data: ReconciliationFlagWire }>(
      `/v1/admin/integrations/reconciliation-flags/${id}/resolve`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  // ─── Supervisor Dashboard (Brief 3 V2 Tela 1 — S10) ─────────────────────
  /**
   * Busca por conteúdo de mensagem atravessando PESSOAS — Localizador ⌘K.
   *
   * Gate duplo no servidor (flag ∧ capability `message-content-search`) com
   * regra do silêncio: sem um dos dois devolve `meta.enabled = false`, nunca
   * 403. O caller esconde a seção lendo `meta.enabled`.
   *
   * `q` exige 3+ caracteres — limite do índice trigram, não de gosto.
   */
  searchMessageContent: (opts: {
    q: string;
    limit?: number;
  }): Promise<{
    data: {
      messageId: string;
      conversationId: string;
      sentAt: string;
      excerpt: string;
      /** Quem conversava. A linha precisa de QUEM, não só de quando: «mari»
       *  casa «marido». `null` quando a conversa não tem pessoa vinculada. */
      personDisplayName: string | null;
    }[];
    meta: { enabled: boolean };
  }> => {
    const params = new URLSearchParams({ q: opts.q });
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    return callJson(`/v1/messages-search?${params.toString()}`, { method: 'GET' });
  },

  getSupervisorDashboardState: (): Promise<SupervisorDashboardState> =>
    callJson<SupervisorDashboardState>('/v1/supervisor-dashboard/state', { method: 'GET' }),

  getSupervisorDashboardKpis: (): Promise<KpisResponse> =>
    callJson<KpisResponse>('/v1/supervisor-dashboard/kpis', { method: 'GET' }),

  getSupervisorPriorityQueue: (): Promise<PriorityQueueResponse> =>
    callJson<PriorityQueueResponse>('/v1/supervisor-dashboard/priority-queue', { method: 'GET' }),

  getSupervisorWidgetLayout: (): Promise<WidgetLayoutResponse> =>
    callJson<WidgetLayoutResponse>('/v1/supervisor-dashboard/widgets/layout', { method: 'GET' }),

  setWidgetPreference: (
    widgetId: string,
    body: SetWidgetPreferencePayload,
  ): Promise<WidgetLayoutEntry> =>
    callJson<WidgetLayoutEntry>(`/v1/supervisor-dashboard/widgets/${widgetId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  resetSupervisorWidgets: (): Promise<ResetLayoutResponse> =>
    callJson<ResetLayoutResponse>('/v1/supervisor-dashboard/widgets/reset', { method: 'POST' }),

  refreshDashboard: (): Promise<RefreshResponse> =>
    callJson<RefreshResponse>('/v1/supervisor-dashboard/refresh', { method: 'POST' }),

  getSupervisorSnapshotsHistory: (period?: string): Promise<SnapshotsHistoryResponse> => {
    const search = new URLSearchParams();
    if (period !== undefined) search.set('period', period);
    const qs = search.toString();
    return callJson<SnapshotsHistoryResponse>(
      `/v1/supervisor-dashboard/snapshots/history${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    );
  },

  // ── Brief 4 Tela 2 — Personas (módulo personas · leitura PR1). ──
  // Gate `persona-read` (leitura) / `persona-author` (mutações · PRs seguintes).
  // Serializer pass-through do Prisma confirmado no R15 ao vivo (sem divergência
  // DTO×wire); `voiceProfile`/`vocabulary`/`ethicalBoundaries` vêm como objeto.
  listPersonas: (params: PersonaListParams = {}): Promise<PersonaDto[]> => {
    const search = new URLSearchParams();
    if (params.status !== undefined) search.set('status', params.status);
    if (params.limit !== undefined) search.set('limit', params.limit.toString());
    if (params.cursor !== undefined) search.set('cursor', params.cursor);
    const qs = search.toString();
    return callJson<PersonaDto[]>(`/v1/personas${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },

  getPersona: (id: string): Promise<PersonaDto> =>
    callJson<PersonaDto>(`/v1/personas/${encodeURIComponent(id)}`, { method: 'GET' }),

  // PR6 — criar persona (gate `persona-author`). Status nasce `active`, version 1
  // (o POST não aceita `status`/`version` · R15 ao vivo); os campos opcionais
  // (examples/language/sideDefault/adversarialProfileDefault/metadata) recebem default
  // no schema/serviço. Espelha `createPlaybook`. 409 se o nome colidir no tenant.
  createPersona: (body: CreatePersonaBody): Promise<PersonaDto> =>
    callJson<PersonaDto>('/v1/personas', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // PR3 — editar persona (gate `persona-author`). If-Match obrigatório (ADR-071,
  // OptimisticConcurrencyInterceptor): 409 com { expectedVersion, currentVersion } se a
  // versão divergir. Body parcial (só os campos alterados). Os DTOs do controller são
  // value-import (metatype do ValidationPipe) — sem isso o PATCH multi-campo dá 400.
  updatePersona: (
    id: string,
    expectedVersion: number,
    body: UpdatePersonaBody,
  ): Promise<PersonaDto> =>
    callJson<PersonaDto>(`/v1/personas/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'If-Match': expectedVersion.toString() },
      body: JSON.stringify(body),
    }),

  // PR5 — descontinuar persona (gate `persona-author`). Soft-delete → `status: deprecated`
  // (NÃO remove · preserva referências de playbooks). If-Match obrigatório (ADR-071):
  // 409 se a versão divergir. HTTP 204 sem corpo. Não há reativar via API (R15 ao vivo).
  deprecatePersona: (id: string, expectedVersion: number): Promise<void> =>
    callJson<void>(`/v1/personas/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: { 'If-Match': expectedVersion.toString() },
    }),

  // ── Brief 4 Tela 3 — Templates Meta WhatsApp / HSM (módulo message-templates · CRUD). ──
  // Gate `message-template-read` (leitura) / `message-template-manage` (mutações).
  // Serializer confirmado no R15 ao vivo: `category`/`status` em lowercase,
  // `components[]` Meta-native, `usageStats` null, `version` = If-Match (ADR-071).
  listTemplates: (params: TemplateListParams = {}): Promise<TemplateDto[]> => {
    const search = new URLSearchParams();
    if (params.status !== undefined) search.set('status', params.status);
    if (params.category !== undefined) search.set('category', params.category);
    if (params.language !== undefined) search.set('language', params.language);
    const qs = search.toString();
    return callJson<{ data: TemplateDto[] }>(`/v1/templates${qs ? `?${qs}` : ''}`, {
      method: 'GET',
    }).then((r) => r.data);
  },

  getTemplate: (id: string): Promise<TemplateDto> =>
    callJson<{ data: TemplateDto }>(`/v1/templates/${encodeURIComponent(id)}`, {
      method: 'GET',
    }).then((r) => r.data),

  // PR2 — criar template (gate `message-template-manage`). Nasce `draft`; não
  // submete à Meta automaticamente (ação separada via submitTemplateToMeta).
  createTemplate: (body: CreateTemplateBody): Promise<TemplateDto> =>
    callJson<{ data: TemplateDto }>('/v1/templates', {
      method: 'POST',
      body: JSON.stringify(body),
    }).then((r) => r.data),

  // PR2 — editar template (gate `message-template-manage`). If-Match obrigatório
  // (ADR-071): 409 em versão divergente. Conteúdo editável só em draft/rejected
  // (422 `content-not-editable` caso contrário); o toggle `enabled` vale sempre.
  updateTemplate: (
    id: string,
    expectedVersion: number,
    body: UpdateTemplateBody,
  ): Promise<TemplateDto> =>
    callJson<{ data: TemplateDto }>(`/v1/templates/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'If-Match': expectedVersion.toString() },
      body: JSON.stringify(body),
    }).then((r) => r.data),

  // PR2 — submeter à Meta (gate `message-template-manage`). DRAFT/REJECTED →
  // SUBMITTED. If-Match obrigatório. Em dev sem credencial Meta retorna 502
  // (estado preservado · falha honesta, NÃO bug de UI).
  submitTemplateToMeta: (id: string, expectedVersion: number): Promise<TemplateDto> =>
    callJson<{ data: TemplateDto }>(`/v1/templates/${encodeURIComponent(id)}/submit-meta`, {
      method: 'POST',
      headers: { 'If-Match': expectedVersion.toString() },
    }).then((r) => r.data),

  // PR2 — remover template (gate `message-template-manage`). Soft-delete.
  // If-Match obrigatório (ADR-071); HTTP 204 sem corpo.
  deleteTemplate: (id: string, expectedVersion: number): Promise<void> =>
    callJson<void>(`/v1/templates/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: { 'If-Match': expectedVersion.toString() },
    }),

  // ── Brief 4 Tela 9 — Configuração de Bonds (módulo bond-type-configs · overlay). ──
  // Gate `bond-type-config-read` (GET) / `bond-type-config-manage` (PATCH). GET
  // devolve o catálogo canônico de 21 tipos mesclado com o estado ativo do tenant
  // + contagem de Bonds por tipo (envelope `{ data }`). PATCH liga/desliga um tipo;
  // If-Match obrigatório (ADR-071) — aceita v0 quando o tipo ainda não tem linha.
  listBondTypeConfigs: (): Promise<BondTypeConfigView[]> =>
    callJson<{ data: BondTypeConfigView[] }>('/v1/bond-type-configs', {
      method: 'GET',
    }).then((r) => r.data),

  updateBondTypeConfig: (
    bondType: string,
    expectedVersion: number,
    body: UpdateBondTypeConfigBody,
  ): Promise<BondTypeConfigView> =>
    callJson<{ data: BondTypeConfigView }>(
      `/v1/bond-type-configs/${encodeURIComponent(bondType)}`,
      {
        method: 'PATCH',
        headers: { 'If-Match': expectedVersion.toString() },
        body: JSON.stringify(body),
      },
    ).then((r) => r.data),

  // ── Item 366 Fase 4 — Times & Roteamento (módulo team-configs · CRUD). ──
  // Gate `team-config-read` (GETs) / `team-config-manage` (mutações). If-Match
  // (ADR-071) em PATCH/DELETE de Team; membros não têm version (unicidade é o guard).
  listTeams: (includeInactive = false): Promise<TeamView[]> =>
    callJson<{ data: TeamView[] }>(
      `/v1/team-configs${includeInactive ? '?includeInactive=true' : ''}`,
      { method: 'GET' },
    ).then((r) => r.data),

  createTeam: (body: CreateTeamBody): Promise<TeamView> =>
    callJson<{ data: TeamView }>('/v1/team-configs', {
      method: 'POST',
      body: JSON.stringify(body),
    }).then((r) => r.data),

  updateTeam: (id: string, expectedVersion: number, body: UpdateTeamBody): Promise<TeamView> =>
    callJson<{ data: TeamView }>(`/v1/team-configs/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'If-Match': expectedVersion.toString() },
      body: JSON.stringify(body),
    }).then((r) => r.data),

  deleteTeam: (id: string, expectedVersion: number): Promise<void> =>
    callJson<void>(`/v1/team-configs/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: { 'If-Match': expectedVersion.toString() },
    }),

  // ── Motor Neutro D2 — casa de config de CATEGORIA DE RISCO DE AÇÃO (módulo A3). ──
  // Gates: `action-risk-category-read` (resolved/preview) · `-author` (POST/PATCH/DELETE) ·
  // `-platform-approve` (approve). If-Match (ADR-071) em PATCH/DELETE/approve.
  // `vertical` é PlaybookVertical (eixo FUNCIONAL); o Engine devolve 400 no eixo setorial.
  getResolvedActionRiskCategories: (vertical: string | null): Promise<ResolvedCatalogView> =>
    callJson<{ data: ResolvedCatalogView }>(
      `/v1/action-risk-categories/resolved${
        vertical !== null ? `?vertical=${encodeURIComponent(vertical)}` : ''
      }`,
      { method: 'GET' },
    ).then((r) => r.data),

  previewActionRiskCategory: (body: PreviewActionRiskBody): Promise<PreviewResultView> =>
    callJson<{ data: PreviewResultView }>('/v1/action-risk-categories/preview', {
      method: 'POST',
      body: JSON.stringify(body),
    }).then((r) => r.data),

  createActionRiskCategory: (body: CreateActionRiskBody): Promise<TenantOverrideView> =>
    callJson<{ data: TenantOverrideView }>('/v1/action-risk-categories', {
      method: 'POST',
      body: JSON.stringify(body),
    }).then((r) => r.data),

  updateActionRiskCategory: (
    id: string,
    expectedVersion: number,
    body: UpdateActionRiskBody,
  ): Promise<TenantOverrideView> =>
    callJson<{ data: TenantOverrideView }>(`/v1/action-risk-categories/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'If-Match': expectedVersion.toString() },
      body: JSON.stringify(body),
    }).then((r) => r.data),

  deleteActionRiskCategory: (id: string, expectedVersion: number): Promise<void> =>
    callJson<void>(`/v1/action-risk-categories/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: { 'If-Match': expectedVersion.toString() },
    }),

  approveActionRiskCategory: (id: string, expectedVersion: number): Promise<TenantOverrideView> =>
    callJson<{ data: TenantOverrideView }>(
      `/v1/action-risk-categories/${encodeURIComponent(id)}/approve`,
      { method: 'POST', headers: { 'If-Match': expectedVersion.toString() } },
    ).then((r) => r.data),

  addTeamMember: (teamId: string, body: AddMemberBody): Promise<TeamView> =>
    callJson<{ data: TeamView }>(`/v1/team-configs/${encodeURIComponent(teamId)}/members`, {
      method: 'POST',
      body: JSON.stringify(body),
    }).then((r) => r.data),

  updateTeamMember: (teamId: string, userId: string, body: UpdateMemberBody): Promise<TeamView> =>
    callJson<{ data: TeamView }>(
      `/v1/team-configs/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ).then((r) => r.data),

  removeTeamMember: (teamId: string, userId: string): Promise<TeamView> =>
    callJson<{ data: TeamView }>(
      `/v1/team-configs/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE' },
    ).then((r) => r.data),

  // Regras de roteamento (módulo routing-agent · gate playbook-author). conditions é Json livre.
  listRoutingRules: (): Promise<RoutingRuleDto[]> =>
    callJson<RoutingRuleDto[]>('/v1/routing-rules', { method: 'GET' }),

  createRoutingRule: (body: CreateRoutingRuleBody): Promise<RoutingRuleDto> =>
    callJson<RoutingRuleDto>('/v1/routing-rules', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  deleteRoutingRule: (id: string, expectedVersion: number): Promise<void> =>
    callJson<void>(`/v1/routing-rules/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: { 'If-Match': expectedVersion.toString() },
    }),

  // ── Brief 4 Tela 1 — Playbooks (módulo playbooks · CRUD). ──
  // Gate `playbook-read` (leitura) / `playbook-author` (mutações).
  listPlaybooks: (params: PlaybookListParams = {}): Promise<PlaybookDto[]> => {
    const search = new URLSearchParams();
    if (params.status !== undefined) search.set('status', params.status);
    if (params.limit !== undefined) search.set('limit', params.limit.toString());
    if (params.cursor !== undefined) search.set('cursor', params.cursor);
    const qs = search.toString();
    return callJson<PlaybookDto[]>(`/v1/playbooks${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },

  getPlaybook: (id: string): Promise<PlaybookDto> =>
    callJson<PlaybookDto>(`/v1/playbooks/${encodeURIComponent(id)}`, { method: 'GET' }),

  // PR5 — criar playbook (gate `playbook-author`). Status nasce `active` (sem `draft`
  // no POST · R15 ao vivo). Usado tanto pelo form de "Novo playbook" quanto pela
  // duplicação client-side (payload espelhado de um playbook existente).
  createPlaybook: (body: CreatePlaybookBody): Promise<PlaybookDto> =>
    callJson<PlaybookDto>('/v1/playbooks', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // PR5 — arquivar playbook (gate `playbook-author`). Soft-delete → `status: deprecated`
  // (NÃO remove · preserva referências). If-Match obrigatório (ADR-071): 409 se a versão
  // divergir. HTTP 204 sem corpo.
  archivePlaybook: (id: string, expectedVersion: number): Promise<void> =>
    callJson<void>(`/v1/playbooks/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: { 'If-Match': expectedVersion.toString() },
    }),

  // PR4 — editar playbook (gate `playbook-author`). If-Match obrigatório (ADR-071,
  // OptimisticConcurrencyInterceptor): 409 com { expectedVersion, currentVersion } se
  // a versão divergir. Body parcial (só os campos alterados).
  updatePlaybook: (
    id: string,
    expectedVersion: number,
    body: UpdatePlaybookBody,
  ): Promise<PlaybookDto> =>
    callJson<PlaybookDto>(`/v1/playbooks/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'If-Match': expectedVersion.toString() },
      body: JSON.stringify(body),
    }),

  // Resolve a checklist vinculada a um playbook (aba Checklist · gate `checklists-basic`).
  getChecklistTemplate: (id: string): Promise<ChecklistTemplateDto> =>
    callJson<ChecklistTemplateDto>(`/v1/checklist-templates/${encodeURIComponent(id)}`, {
      method: 'GET',
    }),

  // Lista templates de checklist do tenant (para o select de vínculo na edição ·
  // gate `checklists-basic`). O endpoint envelopa em { data: [...] }.
  listChecklistTemplates: (): Promise<ChecklistTemplateSummary[]> =>
    callJson<{ data: ChecklistTemplateSummary[] }>('/v1/checklist-templates', {
      method: 'GET',
    }).then((res) => res.data),

  // Item 368 F2 Fatia D — Propostas de condução H_c (ConductionProposal).
  // Cap gate: `conversation-lifecycle-strict` (atendente já possui via ROLE_CAPABILITY_MAPPING).
  // R15: shape real de ProposalForOperator do conduction-proposal.service.ts.
  myConductionProposals: (): Promise<{ items: ConductionProposalWire[] }> =>
    callJson('/v1/conduction-proposals/requested-for-me', { method: 'GET' }),

  grantConductionProposal: (id: string): Promise<{ conversationId: string }> =>
    callJson(`/v1/conduction-proposals/${encodeURIComponent(id)}/grant`, { method: 'POST' }),

  denyConductionProposal: (id: string, reason?: string): Promise<void> =>
    callJson(`/v1/conduction-proposals/${encodeURIComponent(id)}/deny`, {
      method: 'POST',
      body: JSON.stringify(reason !== undefined ? { reason } : {}),
    }),

  // Item 368 F2 Fatia C — métricas de aceitação por playbook (aba Autonomia).
  // Gate `playbook-read` (visão de gestão). R15: shape de ProposalMetrics.
  proposalMetrics: (playbookId: string, windowDays = 30): Promise<{ data: ProposalMetricsWire }> =>
    callJson<{ data: ProposalMetricsWire }>(
      `/v1/conduction-proposals/metrics?playbookId=${encodeURIComponent(playbookId)}&windowDays=${windowDays}`,
      { method: 'GET' },
    ),

  // Item 391-C — Importação da base de clientes (CSV parseado client-side → JSON).
  // Cap gate backend: `customer-registry-import` (admin_tenant). A FE lê o arquivo,
  // parseia para `rows` (coluna→valor) + `mapping` (campo→coluna) e envia JSON.
  importCustomerBase: (
    rows: Record<string, string>[],
    mapping: ImportBaseMapping,
    defaultChannel: ImportDefaultChannel = 'whatsapp_cloud_api',
  ): Promise<ImportResult> =>
    callJson<ImportResult>('/v1/customer-registry/import', {
      method: 'POST',
      body: JSON.stringify({ rows, mapping, defaultChannel }),
    }),

  // ── Item 8 / varredura #8b — Mensageria interna dirigida (módulo direct-messages). ──
  // Gate `direct-message-initiate` (criar/broadcast) / `direct-message-participate`
  // (responder/ler/marcar lido/badge). Respostas JSON CRU — array puro em GET /threads,
  // objetos planos no resto (NÃO usam o envelope { data }; verificado no controller).
  // Os 403 da mensageria são esperados (escopo de supervisão / participação) e
  // mapeados em banner inline honesto pelo FE — `suppressForbiddenToast: true`
  // pula o toast genérico mas mantém o throw do ApiError (caller trata inline).
  listDirectThreads: (
    params: { cursor?: string; limit?: number } = {},
  ): Promise<ThreadSummary[]> => {
    const search = new URLSearchParams();
    if (params.cursor !== undefined) search.set('cursor', params.cursor);
    if (params.limit !== undefined) search.set('limit', params.limit.toString());
    const qs = search.toString();
    return callJson<ThreadSummary[]>(`/v1/direct-messages/threads${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      suppressForbiddenToast: true,
    });
  },

  getDirectThread: (threadId: string): Promise<ThreadDetail> =>
    callJson<ThreadDetail>(`/v1/direct-messages/threads/${encodeURIComponent(threadId)}`, {
      method: 'GET',
      suppressForbiddenToast: true,
    }),

  getDirectUnreadCount: (): Promise<UnreadCountResult> =>
    callJson<UnreadCountResult>('/v1/direct-messages/unread-count', {
      method: 'GET',
      suppressForbiddenToast: true,
    }),

  // Picker escopado — quem o ator pode INICIAR contato (1:1) + times de broadcast.
  // Mesma fonte de verdade do enforcement de direção no backend. 403 (sem cap de
  // iniciação) tratado como escopo vazio pelo hook → suppressForbiddenToast.
  getDirectRecipientScope: (): Promise<RecipientScope> =>
    callJson<RecipientScope>('/v1/direct-messages/recipient-scope', {
      method: 'GET',
      suppressForbiddenToast: true,
    }),

  initiateDirectThread: (body: InitiateThreadBody): Promise<InitiateThreadResult> =>
    callJson<InitiateThreadResult>('/v1/direct-messages/threads', {
      method: 'POST',
      body: JSON.stringify(body),
      suppressForbiddenToast: true,
    }),

  broadcastDirectMessage: (body: BroadcastBody): Promise<BroadcastResult> =>
    callJson<BroadcastResult>('/v1/direct-messages/broadcast', {
      method: 'POST',
      body: JSON.stringify(body),
      suppressForbiddenToast: true,
    }),

  postDirectMessage: (threadId: string, body: PostMessageBody): Promise<PostMessageResult> =>
    callJson<PostMessageResult>(
      `/v1/direct-messages/threads/${encodeURIComponent(threadId)}/messages`,
      { method: 'POST', body: JSON.stringify(body), suppressForbiddenToast: true },
    ),

  markDirectThreadRead: (threadId: string): Promise<MarkReadResult> =>
    callJson<MarkReadResult>(`/v1/direct-messages/threads/${encodeURIComponent(threadId)}/read`, {
      method: 'POST',
      suppressForbiddenToast: true,
    }),

  // ── Mensageria Condensada (PR B4 / F1a) — Pins / favoritos ──────────────
  // Gate `direct-message-participate`. CRUD idempotente no backend (POST repetido
  // não duplica; DELETE de pin inexistente é no-op de sucesso). Respostas JSON CRU:
  // GET → array puro de PinSummary; POST/DELETE → `{ pinned }`. Os 403 de
  // acessibilidade do alvo (DM_PIN_TARGET_NOT_ACCESSIBLE) são tratados inline pela
  // mensageria → `suppressForbiddenToast`.
  listPins: (): Promise<PinSummary[]> =>
    callJson<PinSummary[]>('/v1/direct-messages/pins', {
      method: 'GET',
      suppressForbiddenToast: true,
    }),

  createPin: (body: PinTargetBody): Promise<PinResult> =>
    callJson<PinResult>('/v1/direct-messages/pins', {
      method: 'POST',
      body: JSON.stringify(body),
      suppressForbiddenToast: true,
    }),

  deletePin: (body: PinTargetBody): Promise<UnpinResult> =>
    callJson<UnpinResult>('/v1/direct-messages/pins', {
      method: 'DELETE',
      body: JSON.stringify(body),
      suppressForbiddenToast: true,
    }),

  // ── Mensageria Condensada (PR B3 / F1b) — Context-peek (espiada vinculada) ──
  // GET /v1/conversations/:id/context-peek (?viaThreadId=). Gate
  // `direct-message-participate` + escopo de leitura da conversa no service.
  // 200 → ContextPeekResponse; 403 → corpo MÍNIMO ContextPeekDenied (sem conteúdo).
  // `suppressForbiddenToast` mantém o throw do ApiError (o hook deriva o estado
  // `denied` do 403 + lê `err.body` para a etiqueta de sensibilidade) sem disparar
  // o toast genérico — o "sem permissão" é estado honesto inline, não erro.
  getContextPeek: (conversationId: string, viaThreadId?: string): Promise<ContextPeekResponse> => {
    const qs = viaThreadId !== undefined ? `?viaThreadId=${encodeURIComponent(viaThreadId)}` : '';
    return callJson<ContextPeekResponse>(
      `/v1/conversations/${encodeURIComponent(conversationId)}/context-peek${qs}`,
      { method: 'GET', suppressForbiddenToast: true },
    );
  },

  // ── Respostas Prontas do Operador (spec 2026-07-16 §3.1, Task 6/8) ──────
  // Biblioteca de respostas prontas. GET é `canned-response-use` (leitura pelo
  // operador comum); escrita é `canned-response-manage`. PATCH/DELETE/PUT-playbooks
  // exigem If-Match (ADR-071 — a `version` do recurso).
  // `suppressForbiddenToast` no GET (B1 residual): o 403 é tratado INLINE pelo
  // gate-and-split de `QuickRepliesPage` (fallback-on-denied — cap opt-in que o
  // auth/me não filtra); sem a flag, o toast global "Sem acesso" empilhava num
  // cenário que não é erro de produto. O ApiError continua sendo lançado.
  getCannedResponses: (params?: {
    playbookId?: string;
    category?: string;
    q?: string;
  }): Promise<{ data: CannedResponseWire[] }> => {
    const search = new URLSearchParams();
    if (params?.playbookId !== undefined) search.set('playbookId', params.playbookId);
    if (params?.category !== undefined) search.set('category', params.category);
    if (params?.q !== undefined) search.set('q', params.q);
    const qs = search.toString();
    return callJson(`/v1/canned-responses${qs !== '' ? `?${qs}` : ''}`, {
      method: 'GET',
      suppressForbiddenToast: true,
    });
  },
  createCannedResponse: (body: {
    title: string;
    items: CannedResponseItem[];
    category?: string;
    shortcut?: string;
  }): Promise<CannedResponseWire> =>
    callJson('/v1/canned-responses', { method: 'POST', body: JSON.stringify(body) }),
  updateCannedResponse: (
    id: string,
    version: number,
    body: { title?: string; category?: string; shortcut?: string; items?: CannedResponseItem[] },
  ): Promise<CannedResponseWire> =>
    callJson(`/v1/canned-responses/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'If-Match': version.toString() },
    }),
  archiveCannedResponse: (id: string, version: number): Promise<CannedResponseWire> =>
    callJson(`/v1/canned-responses/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: { 'If-Match': version.toString() },
    }),
  setCannedResponsePlaybooks: (
    id: string,
    version: number,
    playbookIds: string[],
  ): Promise<CannedResponseWire> =>
    callJson(`/v1/canned-responses/${encodeURIComponent(id)}/playbooks`, {
      method: 'PUT',
      body: JSON.stringify({ playbookIds }),
      headers: { 'If-Match': version.toString() },
    }),

  // ── Biblioteca de MÍDIA do tenant (spec 2026-07-16 §3.2, Task 6/8) ──────
  // Upload/archive exigem `media-library-manage`; leitura (list + content) usa
  // `canned-response-use`. `mediaAssetContentUrl` NÃO faz fetch — é a URL direta
  // para <img>/<video> consumirem (o binário nunca passa por callJson).
  // `suppressForbiddenToast` no GET: mesmo racional do `getCannedResponses` —
  // a LibraryManagerView monta esta query junto; no cenário denied os DOIS
  // GETs recebiam 403 e cada um disparava o toast global.
  getMediaAssets: (params?: {
    kind?: 'image' | 'video' | 'audio';
  }): Promise<{ data: MediaAssetWire[] }> => {
    const search = new URLSearchParams();
    if (params?.kind !== undefined) search.set('kind', params.kind);
    const qs = search.toString();
    return callJson(`/v1/media-assets${qs !== '' ? `?${qs}` : ''}`, {
      method: 'GET',
      suppressForbiddenToast: true,
    });
  },
  uploadMediaAsset: (file: File): Promise<MediaAssetWire> => {
    const formData = new FormData();
    formData.append('file', file);
    return callFormData('/v1/media-assets', formData);
  },
  archiveMediaAsset: (id: string, version: number): Promise<MediaAssetWire> =>
    callJson(`/v1/media-assets/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: { 'If-Match': version.toString() },
    }),
  /**
   * URL direta do binário — NÃO passa por `callJson`/fetch (nenhum método de
   * fetch para `content`, por design do brief). Consumida diretamente por
   * `<img src=…>`/`<video src=…>`.
   */
  mediaAssetContentUrl: (id: string): string =>
    `${ENGINE_URL}/v1/media-assets/${encodeURIComponent(id)}/content`,
  /**
   * Fetch AUTENTICADO do binário (Task 9 — `mediaAssetContentUrl` cru num
   * `<img src>` dá 401: o `AuthGuard` exige `Authorization: Bearer`, sem
   * cookie de sessão). Mesmo molde de `exportAuditEvents`/`exportPendencies*`
   * (`rawFetch` + `res.blob()`); o caller monta um object URL (`useMediaAssetObjectUrl`).
   */
  fetchMediaAssetBlob: async (id: string): Promise<Blob> => {
    const res = await rawFetch(`/v1/media-assets/${encodeURIComponent(id)}/content`, {
      method: 'GET',
    });
    if (!res.ok) throw await parseError(res);
    return res.blob();
  },

  // ── Respostas Prontas do Operador — uso na conversa (Task 6, S2) ───────
  // Ambos gated `canned-response-use`, server-authoritative. `suppressForbiddenToast`:
  // mesmo racional B1 residual do bloco de biblioteca acima — o 403 é tratado
  // inline pelo caller (picker/fila de revisão), não pelo toast global.
  getCannedVariables: (conversationId: string): Promise<CannedVariablesWire> =>
    callJson<CannedVariablesWire>(
      `/v1/conversations/${encodeURIComponent(conversationId)}/canned-variables`,
      { method: 'GET', suppressForbiddenToast: true },
    ),
  sendMessageBurst: (
    conversationId: string,
    body: { cannedResponseId: string; burstKey: string; items: BurstItemRequestWire[] },
  ): Promise<BurstResultWire> =>
    callJson<BurstResultWire>(
      `/v1/conversations/${encodeURIComponent(conversationId)}/messages/burst`,
      { method: 'POST', body: JSON.stringify(body), suppressForbiddenToast: true },
    ),

  // ── Respostas Prontas do Operador — supervisor (S4, Task C) ────────────
  // Métricas de uso + config do alerta de conduta. Gated `canned-response-analytics`
  // (cap opt-in; o /auth/me NÃO filtra optInRequired — flag sistêmico). Os 3 GET
  // usam `suppressForbiddenToast` porque o 403 é tratado INLINE pelo gate-and-split
  // da `RespostasProntasSupervisorPage` (fallback = estado "indisponível", nunca
  // toast global). O PATCH deixa o toast (ação de um supervisor entitled — um 403
  // ali é um erro de produto legítimo). Números vêm como `number` (o Engine computa
  // via `computePct`) — NÃO há string-decimal aqui (difere da convenção `Raw`).
  getCannedResponseMetricsOverview: (
    params: { periodFrom?: string; periodTo?: string } = {},
  ): Promise<CannedResponseMetricsOverview> => {
    const s = new URLSearchParams();
    if (params.periodFrom !== undefined) s.set('periodFrom', params.periodFrom);
    if (params.periodTo !== undefined) s.set('periodTo', params.periodTo);
    const qs = s.toString();
    return callJson(`/v1/canned-responses/analytics/overview${qs !== '' ? `?${qs}` : ''}`, {
      method: 'GET',
      suppressForbiddenToast: true,
    });
  },
  getCannedResponseMetricsByOperator: (
    params: { periodFrom?: string; periodTo?: string } = {},
  ): Promise<{ data: CannedResponseOperatorMetric[] }> => {
    const s = new URLSearchParams();
    if (params.periodFrom !== undefined) s.set('periodFrom', params.periodFrom);
    if (params.periodTo !== undefined) s.set('periodTo', params.periodTo);
    const qs = s.toString();
    return callJson(`/v1/canned-responses/analytics/by-operator${qs !== '' ? `?${qs}` : ''}`, {
      method: 'GET',
      suppressForbiddenToast: true,
    });
  },
  getConductAlertConfig: (): Promise<ConductAlertConfigWire> =>
    callJson<ConductAlertConfigWire>('/v1/canned-responses/analytics/conduct-alert-config', {
      method: 'GET',
      suppressForbiddenToast: true,
    }),
  updateConductAlertConfig: (
    version: number,
    body: Partial<ConductAlertConfig>,
  ): Promise<ConductAlertConfigWire> =>
    callJson<ConductAlertConfigWire>('/v1/canned-responses/analytics/conduct-alert-config', {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'If-Match': version.toString() },
    }),
};

// ─── Sub-fatia 10.0 PR 3 — Customer Context types (ADR-142) ──────────────
export interface CustomerContextResponse {
  rows: {
    /** 'internal' | 'rag' | connectorSlug arbitrário do tenant. */
    source: string;
    sourceDisplayName: string;
    label: string;
    value: string;
  }[];
  externalSources?: {
    slug: string;
    displayName: string;
    status:
      | 'normal'
      | 'degraded'
      | 'unreachable'
      | 'not-found'
      | 'invalid-response'
      | 'business-error'
      | 'unconfigured';
    cachedAt: string | null;
    lastError?: { code: string; details?: Record<string, unknown> };
  }[];
  lastRefreshedAt: string | null;
}

// Sprint Backend Operacional Brief 2A r7b — Appointment wire type (ADR-115).
//
// Wire type feature-scoped local (pattern Fatia G + Wave 2 PR #46) — não vive
// em @lume/shared para evitar coupling cross-app. Atendimento adapta para
// AppointmentFixture (kebab-case) em use-panel-fixtures.ts.

export interface AppointmentWireItem {
  id: string;
  tenantId: string;
  conversationId: string | null;
  bondId: string | null;
  personId: string | null;
  /** Brief 2B Tela 7 §4 — nome da Pessoa vinculada (`Person.displayName`); null quando avulso. */
  personName: string | null;
  /** Brief 2B Tela 7 §4 — rótulo PT-BR canônico do tipo do Bond; null quando sem Bond. */
  bondLabel: string | null;
  title: string;
  type:
    | 'technical_visit'
    | 'in_person_meeting'
    | 'video_call'
    | 'phone_call'
    | 'delivery'
    | 'other';
  scheduledAt: string;
  durationMinutes: number;
  addressOrLocation: string | null;
  status:
    | 'scheduled'
    | 'confirmed'
    | 'imminent'
    | 'completed'
    | 'cancelled'
    | 'no_show'
    | 'rescheduled';
  notes: string | null;
  createdByUserId: string | null;
  assignedToUserId: string | null;
  source: 'ai' | 'attendant' | 'customer';
  aiAgentId: string | null;
  cancellationReason: string | null;
  rescheduledFromId: string | null;
  /** Brief 2B B4 — Pendency de follow-up gerada ao marcar no-show. */
  noShowGeneratedPendencyId: string | null;
  /** Frente B Bloco II — série de recorrência que gerou este appointment (null = avulso). */
  seriesId: string | null;
  /**
   * Frente B Sessão 1 (UI recurso-cêntrica) — recursos ocupados por este appointment.
   * `[]` para legados/avulsos. Junte `resourceId` ao catálogo de `SchedulingResourceWire`
   * (nome/capacidade) que a página já carrega. OPCIONAL no wire: um backend ainda não
   * atualizado (rollout) pode omitir o campo → o FE trata ausente como `[]` (defensivo).
   */
  appointmentResources?: {
    resourceId: string;
    /**
     * Frente B Sessão 2 — nome do recurso denormalizado pelo backend (a Conversa não
     * carrega o catálogo de recursos como a /agenda). `null` quando o backend não deu
     * include → o caller junta pelo catálogo (defensivo, forward-compat de rollout).
     */
    resourceName: string | null;
    startsAt: string;
    endsAt: string;
  }[];
  version: number;
  createdAt: string;
  updatedAt: string;
  /**
   * ADR-155 Fase 4 (E1) — envelope episódico coletado (D3/D5), passthrough
   * do `Appointment.collectedData`. `null` sem coleta episódica ainda.
   * Sai em TODOS os endpoints que já serializam appointment (mesmo raio da
   * cap `appointments` que já mostra este dado na Coleta).
   */
  collectedData: Record<string, CollectedFieldEnvelope> | null;
}

/** Frente B Bloco II — recurso agendável nativo (profissional/cadeira/sala/equip.). */
export interface SchedulingResourceWire {
  id: string;
  name: string;
  resourceType: 'professional' | 'chair' | 'room' | 'equipment' | 'other';
  capacity: number;
  timezone: string;
  userId: string | null;
  /** Plano 2 (setup) — soft-delete: recurso aposentado some do catálogo. Sempre true na lista. */
  active: boolean;
  /** Plano 2 (setup) — optimistic concurrency (If-Match no PATCH/DELETE de gestão). */
  version: number;
}
/** Frente B Bloco II — serviço agendável (duração + buffers + recursos exigidos). */
export interface SchedulingServiceWire {
  id: string;
  name: string;
  defaultDurationMinutes: number;
  bufferBeforeMin: number;
  bufferAfterMin: number;
  requiredResourceTypes: string[];
  minLeadTimeMin: number | null;
  cancellationWindowMin: number | null;
  /** Plano 2 (setup) — pré-requisito a qualificar antes de ofertar slot (Eixo B leve). */
  intakeNote: string | null;
  /** Plano 2 (setup) — fila de espera habilitada p/ este serviço. */
  waitlistEnabled: boolean;
  /** Plano 2 (setup) — soft-delete: serviço aposentado some do catálogo. Sempre true na lista. */
  active: boolean;
  /** Plano 2 (setup) — optimistic concurrency (If-Match no PATCH/DELETE de gestão). */
  version: number;
}
/** Frente B Bloco II — slot livre computado (free/busy). `resourceIds` = recursos que o satisfazem. */
export interface SchedulingSlotWire {
  startsAt: string;
  endsAt: string;
  remainingCapacity: number;
  resourceIds: string[];
}

/** Frente B Sessão 1 — período fechado de um recurso (AvailabilityBlock; férias/feriado/almoço). */
export interface SchedulingBlockWire {
  id: string;
  resourceId: string;
  startsAt: string;
  endsAt: string;
  reason: string | null;
}

/** Frente B Plano 2 (setup) — janela recorrente de trabalho de um recurso (WorkingHours). */
export interface SchedulingWorkingHoursWire {
  id: string;
  resourceId: string;
  /** 0 = domingo … 6 = sábado. */
  dayOfWeek: number;
  /** Minutos desde a meia-noite, local ao timezone do recurso. */
  startMinute: number;
  endMinute: number;
  /** Validade opcional (período — "abre set/out"); null = perpétua. */
  effectiveFrom: string | null;
  effectiveUntil: string | null;
}

/**
 * Frente N N3a-2 — um turno de plantão do operador (`OperatorOnCallShift`).
 * Espelha CAMPO-A-CAMPO o `SerializedShift` do `operator-on-call-shift.controller.ts`.
 * `startsAt`/`endsAt` são instantes absolutos (ISO/UTC) — a vigência é comparada em
 * instantes, não em minuto-local (diferente de WorkingHours/shiftWindows).
 */
export interface OperatorOnCallShiftWire {
  id: string;
  /** O operador de plantão (User). */
  userId: string;
  startsAt: string;
  endsAt: string;
  note: string | null;
  /** Quem escalou (null se removido). */
  createdByUserId: string | null;
  version: number;
  createdAt: string;
}

/** Frente B Bloco IV — tier de prioridade da fila de espera (espelha o enum do engine). */
export type WaitlistPriorityTierWire =
  | 'bumped'
  | 'urgent_high'
  | 'urgent_medium'
  | 'urgent_low'
  | 'preferred'
  | 'normal';
/** Frente B Bloco IV — entry da fila de espera (controle do operador, gated `scheduling-waitlist`). */
export interface WaitlistEntryWire {
  id: string;
  personId: string | null;
  /** Frente B Sessão 1 — nome exibível da pessoa; null quando avulso ou backend sem o campo. */
  personName?: string | null;
  bondId: string | null;
  serviceOfferingId: string;
  /** Frente B Sessão 2 — nome do serviço da fila (mini-card da Conversa); null sem include. */
  serviceName?: string | null;
  resourcePreference: 'specific' | 'any_eligible';
  preferredResourceId: string | null;
  priorityTier: WaitlistPriorityTierWire;
  status: 'pending_approval' | 'active' | 'offered' | 'booked' | 'expired' | 'cancelled';
  alternativeOffersOptIn: boolean;
  offeredResourceId: string | null;
  offerExpiresAt: string | null;
  version: number;
  createdAt: string;
}

/**
 * Frente B (reconciliação da agenda, Sessão 5) — sinais da FILA no painel do Supervisor.
 * Agregação read-only (gated `scheduling-waitlist`). A garantia mora no backend; a UI expõe.
 */
export interface WaitlistSignalsWire {
  waiting: number;
  pendingApproval: number;
  byTier: { tier: WaitlistPriorityTierWire; count: number }[];
  byService: { serviceOfferingId: string; serviceName: string; count: number }[];
  fillRate: {
    windowDays: number;
    booked: number;
    expired: number;
    cancelled: number;
    resolved: number;
    /** booked / resolved * 100; null se nada resolveu na janela. */
    ratePct: number | null;
  };
}

/**
 * Frente B (reconciliação da agenda, Sessão 5) — sinais de CAPACIDADE no painel do
 * Supervisor. Agregação read-only (gated `scheduling-native`). Pico de ocupação por
 * recurso vs capacity no horizonte.
 */
export interface CapacitySignalsWire {
  horizonDays: number;
  resources: {
    resourceId: string;
    resourceName: string;
    resourceType: 'professional' | 'chair' | 'room' | 'equipment' | 'other';
    capacity: number;
    peakConcurrent: number;
    upcomingCount: number;
    utilizationPct: number;
    /**
     * Frente B Plano 3 (C1) — ocupação-de-TEMPO (min ocupados ÷ grade disponível) p/ recursos
     * EXCLUSIVOS (capacity 1); `null` p/ GRUPO (nesses o limite é capacidade — vê `utilizationPct`).
     */
    timeUtilizationPct: number | null;
    nearLimit: boolean;
  }[];
}

/** Status canonical do Appointment (snake_case, 7 valores — ADR-115). */
export type AppointmentWireStatus =
  | 'scheduled'
  | 'confirmed'
  | 'imminent'
  | 'completed'
  | 'cancelled'
  | 'no_show'
  | 'rescheduled';

// Brief 2B B5 — Agent dashboard (Painel Pessoal, Tela 5) wire types.
// Espelha `AgentDashboardState` do engine (módulo agent-dashboard). Composição
// read-time, caller-scoped. 6 blocos REAIS (null se a query degradar) + 7
// honest-defer (SEMPRE null, Item 341) + layout (P3.6) + computedAt.
export interface AgentDashboardStateWire {
  activeConversations: { total: number; byMode: { h_a: number; h_b: number; h_c: number } } | null;
  overduePendencies: { count: number } | null;
  appointmentsToday: {
    count: number;
    items: { id: string; title: string; scheduledAt: string; status: string }[];
  } | null;
  metaSessionsToday: { byType: Record<string, number> } | null;
  conflictAlerts: { count: number } | null;
  slaShift: {
    violationCount: number;
    trend: 'up' | 'down' | 'stable' | null;
    compliancePercent: number | null;
  } | null;
  // Honest-defer — sempre null (sem fonte/cron — Item 341).
  weekMetrics: null;
  topCategories: null;
  csat: null;
  activityFeed: null;
  peers: null;
  correlations: null;
  sequences: null;
  // Layout P3.6 + meta.
  layout: { visible: string[]; order: string[] };
  computedAt: string;
}

// S3 Abertura e Fechamento do dia (Corrente de Compromissos, P2 da tese, absorve
// o Item 371) — wire types. Espelha `DayRitualState` do engine (módulo
// agent-dashboard). Composição read-time, caller-scoped (exceto `aiCovering`,
// tenant-scoped por design). Cada bloco REAL pode ser `null` (degradação
// isolada) — nunca derruba o rito inteiro.
export interface DayRitualPendencyItemWire {
  pendencyId: string;
  title: string;
  personName: string | null;
  deadline: string | null;
  conversationId: string | null;
  resolvedAt?: string | null;
}

export interface DayRitualAppointmentItemWire {
  id: string;
  title: string;
  scheduledAt: string;
  personName: string | null;
}

export interface DueTodayBlockWire {
  commitments: DayRitualPendencyItemWire[];
  others: DayRitualPendencyItemWire[];
  appointments: DayRitualAppointmentItemWire[];
  count: number;
}

export interface AiResolvedItemWire {
  conversationId: string;
  personName: string | null;
  summary: string;
  resolvedAt: string;
}

export interface AiResolvedBlockWire {
  items: AiResolvedItemWire[];
  count: number;
}

export type NeedsYouItemKindWire = 'escalation' | 'proposal';

export interface NeedsYouItemWire {
  kind: NeedsYouItemKindWire;
  conversationId: string;
  personName: string | null;
  reason: string | null;
}

export interface NeedsYouBlockWire {
  items: NeedsYouItemWire[];
  count: number;
}

export interface OpeningRitualStateWire {
  kind: 'opening';
  dueToday: DueTodayBlockWire | null;
  aiResolved: AiResolvedBlockWire | null;
  needsYou: NeedsYouBlockWire | null;
  computedAt: string;
}

export interface ProvedTodayBlockWire {
  commitments: DayRitualPendencyItemWire[];
  others: DayRitualPendencyItemWire[];
  conversationsClosedCount: number;
  count: number;
}

export interface AiCoveringBlockWire {
  count: number;
}

// Inc 3 «wow» — métrica-herói «promessa cumprida no prazo» (espelha PromiseMetricBlock).
export interface PromiseMetricBlockWire {
  onTime: number;
  total: number;
  /**
   * Dívida acumulada — promessas vencidas e ainda abertas, de qualquer dia
   * (fatia «promessa quebrada»). Sempre 0 sob a flag OFF do Engine.
   */
  openBreached: number;
}

export interface ClosingRitualStateWire {
  kind: 'closing';
  provedToday: ProvedTodayBlockWire | null;
  carryOver: DueTodayBlockWire | null;
  aiCovering: AiCoveringBlockWire | null;
  promiseMetric: PromiseMetricBlockWire | null;
  computedAt: string;
}

export type DayRitualStateWire = OpeningRitualStateWire | ClosingRitualStateWire;

// S5 Corrente de Compromissos (maré baixa produtiva, Item 405) —
// GET /v1/agent-dashboard/low-tide-suggestions. Molde `SuggestionInput`
// (`@lume/shared/worklist`) — o adapter consome este shape direto.
export interface LowTideSuggestionItemWire {
  id: string;
  mina: 'M6' | 'M7';
  why: string;
  valueLabel: string;
  conversationId?: string;
  personId: string;
  personName: string;
  paidTemplateRisk: boolean;
}

export interface LowTideSuggestionsStateWire {
  suggestions: LowTideSuggestionItemWire[];
  computedAt: string;
}

// Brief 2B T6 — Auxiliar (Tela 6). Notification preferences (módulo notifications).
/** Canais de notificação (enum Engine `NotificationChannel`). */
export type NotificationChannel = 'in_app' | 'email' | 'push';

/**
 * Espelha `NotificationPreference` do Prisma. O GET retorna apenas opt-out/in
 * EXPLÍCITOS — ausência de row = enabled (default opt-in, convenção do
 * `NotificationPreferencesService`). `templateId` é o slug do `NotificationTemplate`.
 */
export interface NotificationPreferenceWire {
  id: string;
  userId: string;
  tenantId: string;
  channel: NotificationChannel;
  templateId: string;
  enabled: boolean;
  updatedAt: string;
}

// Sprint Backend Operacional 2A r7b Agent Z — Pendency wire types (ADR-109).
/**
 * Status canonical Engine (snake_case) — 7 valores (Brief 2B B1 promoveu
 * `postponed`/`archived` a estados reais). Frontend remapeia label no adapter.
 */
export type PendencyWireStatus =
  | 'open'
  | 'in_progress'
  | 'resolved'
  | 'cancelled'
  | 'expired'
  | 'postponed'
  | 'archived';

/** Desfecho canônico ao resolver (ADR-109 Brief 2B B1 — 6 valores snake_case). */
export type PendencyResolutionOutcome =
  | 'success'
  | 'with_restrictions'
  | 'not_possible'
  | 'customer_gave_up'
  | 'replaced'
  | 'other';

export interface PendencyWireItem {
  id: string;
  tenantId: string;
  conversationId: string | null;
  bondId: string | null;
  title: string;
  description: string | null;
  status: PendencyWireStatus;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  ownerUserId: string;
  delegatedToUserId: string | null;
  deadline: string | null;
  resolvedAt: string | null;
  resolvedByUserId: string | null;
  resolutionNote: string | null;
  /** Desfecho canônico (Brief 2B B1) — null quando ainda não resolvida. */
  resolutionOutcome: PendencyResolutionOutcome | null;
  source: string;
  aiAgentId: string | null;
  /** Âncora da captura: de qual mensagem a promessa nasceu (null fora de captura). */
  sourceMessageId: string | null;
  /** Categoria free-form (ADR-109) — 7 valores conhecidos + degradação honesta. */
  category: string | null;
  /** Nº de adiamentos (0..3; ilimitado Regulated). */
  postponeCount: number;
  /** Flag derivada query-time (D109.2 ADR-109). */
  overdue: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Métricas da PRÓPRIA caixa do atendente (Item 346 D3, caller-scoped) — espelha
 * `PersonalPendencyMetrics` do engine. Só o que o pipeline já dá; honest-defer
 * de SLA/gauge/histórico mantido no FE (Item 346 parcial).
 */
export interface PersonalPendencyMetricsRaw {
  open: number;
  overdue: number;
  dueToday: number;
  next7d: number;
  noDue: number;
  oldestOverdueMinutes: number | null;
  resolvedLast30d: number;
  byCategory: { category: string | null; open: number; overdue: number }[];
}

// ADR-105 — CustomerCommitment wire type (espelha SerializedCustomerCommitment do Engine).
export interface CustomerCommitmentWireItem {
  id: string;
  tenantId: string;
  conversationId: string;
  bondId: string | null;
  personId: string | null;
  title: string;
  quote: string | null;
  type: 'financial' | 'document' | 'action' | 'other';
  status: 'pending' | 'fulfilled' | 'broken' | 'cancelled' | 'unclear';
  detectedAt: string;
  dueDate: string | null;
  source: 'ai' | 'attendant' | 'customer' | 'external_event';
  aiAgentId: string | null;
  aiConfidence: number | null;
  fulfilledAt: string | null;
  fulfilledByUserId: string | null;
  cancellationReason: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

// Fatia G — wire types peer-coordination.

export interface HandoffTargetWireItem {
  userId: string;
  name: string;
  primaryRole: string;
}

export interface HandoffTargetTeamWireItem {
  teamId: string;
  name: string;
}

export interface HandoffWireItem {
  id: string;
  conversationId: string;
  mode: 'push' | 'pull';
  initiatedByUserId: string;
  /** A-50 — nome de quem transferiu; opcional até o engine deployar. */
  initiatedByName?: string | null;
  targetUserId: string | null;
  targetQueue: string | null;
  reason: string;
  contextSummary: string;
  priority: 'low' | 'normal' | 'urgent';
  status: 'pending' | 'rejected' | 'cancelled' | 'completed';
  version: number;
  respondedByUserId: string | null;
  responseReason: string | null;
  createdAt: string;
  respondedAt: string | null;
  completedAt: string | null;
}

export interface PresenceWireItem {
  userId: string;
  /** Nome real do User (Engine resolve no /team; null quando não resolvido). */
  name: string | null;
  status: 'online' | 'away' | 'busy' | 'offline' | 'on_call';
  lastActivityAt: string;
  currentConversationsCount: number;
  capacity: number;
  customStatusMessage: string | null;
  updatedAt: string;
}

// Painel V3 Inc 4 — «respaldo do supervisor» (espelha SupervisorRespaldoWire do engine).
// Peer-safe: identidade + presença binária (online/away), NUNCA métrica de carga.
export interface SupervisorRespaldoWire {
  supervisor: { userId: string; name: string; presence: 'online' | 'away' } | null;
}

export interface InternalNoteWireItem {
  id: string;
  conversationId: string;
  authorUserId: string;
  content: string;
  visibility: 'public_team' | 'private_author' | 'private_supervisors';
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface CoAttendantWireItem {
  id: string;
  conversationId: string;
  userId: string;
  role: 'shadow_observer' | 'collaborative_observer' | 'supervisor_monitor';
  addedByUserId: string;
  addedAt: string;
  removedAt: string | null;
  removedByUserId: string | null;
}

// Fatia B + B-bis — wire types Bond.
// Reflete `SerializedBond` / `SerializedBondParticipant` / etc. do Engine
// sem importar diretamente (mantém atendimento desacoplado dos tipos
// Prisma do engine). Enums declarados em strings para evitar acoplamento.

export interface BondWireItem {
  id: string;
  tenantId: string;
  type: string;
  subjectId: string | null;
  status: 'active' | 'paused' | 'closed' | 'archived';
  lifecycle: string;
  parentBondId: string | null;
  crossReferences: unknown[];
  adversarialMode: 'none' | 'competitive' | 'partial' | 'strict';
  side: 'seller' | 'buyer' | 'neutral' | 'mediator' | null;
  metadata: Record<string, unknown>;
  // Item 363 F1 — congelamento cadastral (titular negou pedido de alteração).
  cadastralFrozenAt: string | null;
  cadastralFrozenReason: string | null;
  createdAt: string;
  updatedAt: string | null;
  closedAt: string | null;
  archivedAt: string | null;
}

export interface BondParticipantWireItem {
  id: string;
  bondId: string;
  personId: string | null;
  userId: string | null;
  role: string;
  side: 'seller' | 'buyer' | 'neutral' | 'mediator' | null;
  permissions: string[];
  permissionScope: string;
  temporal: { startsAt?: string; endsAt?: string } | null;
  grantedByUserId: string;
  grantedAt: string;
  revokedAt: string | null;
  revokedReason: string | null;
  reason: string | null;
  // Optimistic concurrency (ADR-071) — If-Match: <version> no PATCH/DELETE.
  version: number;
}

export interface BondRelatedPersonWireItem {
  id: string;
  tenantId: string;
  bondId: string;
  personId: string;
  relationshipType: string;
  status: 'active' | 'terminated';
  startedAt: string | null;
  endedAt: string | null;
  source: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string | null;
  version: number;
}

export interface BondAuthorizedContactWireItem {
  id: string;
  tenantId: string;
  bondId: string;
  personId: string;
  scopes: string[];
  status: 'active' | 'revoked' | 'expired';
  expiresAt: string | null;
  createdAt: string;
  createdByUserId: string | null;
  revokedAt: string | null;
  revokedByUserId: string | null;
  revocationReason: string | null;
  metadata: Record<string, unknown>;
  version: number;
}

// Linha enriquecida da visão tenant-wide (DPO oversight): base + nome do
// titular (person.displayName) e tipo do bond. Espelha SerializedTenantAuthorizedContact.
export interface TenantAuthorizedContactWireItem extends BondAuthorizedContactWireItem {
  personName: string;
  bondType: string;
}

// Requests dos 3 mutadores de authorized-contacts — espelham os DTOs reais
// (AddAuthorizedContactDto / UpdateAuthorizedContactDto / RevokeAuthorizedContactDto).
export interface AddAuthorizedContactRequest {
  personId: string;
  scopes: string[];
  expiresAt?: string;
  metadata?: Record<string, unknown>;
}

export interface UpdateAuthorizedContactRequest {
  scopes?: string[];
  expiresAt?: string;
  metadata?: Record<string, unknown>;
}

export interface RevokeAuthorizedContactRequest {
  revocationReason: string;
}

// Requests dos 3 mutadores de related-persons — espelham os DTOs reais
// (AddRelatedPersonDto / UpdateRelatedPersonDto / TerminateRelatedPersonDto).
export interface AddRelatedPersonRequest {
  personId: string;
  relationshipType: string;
  startedAt?: string;
  source?: string;
  metadata?: Record<string, unknown>;
}

export interface UpdateRelatedPersonRequest {
  relationshipType?: string;
  metadata?: Record<string, unknown>;
}

// terminate (DELETE) — endedAt e reason ambos opcionais.
export interface TerminateRelatedPersonRequest {
  endedAt?: string;
  reason?: string;
}

// Requests dos mutadores de participants — espelham AddParticipantDto / UpdateParticipantDto.
// (revoke não tem body: reason vai via query param.)
export interface AddParticipantRequest {
  // XOR — exatamente um de personId/userId (validado no form).
  personId?: string;
  userId?: string;
  role: string;
  side?: string;
  isPrimary?: boolean;
  permissions?: string[];
  permissionScope?: string;
  externalReferenceId?: string;
  reason?: string;
}

export interface UpdateParticipantRequest {
  role?: string;
  permissions?: string[];
  permissionScope?: string;
  reason?: string;
  /** Frente B Plano 2 (setup) peça 4 — autonomia de agenda por-cliente. `null` limpa o override. */
  nativeSchedulingMode?: string | null;
  waitlistPreferred?: boolean | null;
  /**
   * S4 PR-b (Corrente de Compromissos, Item 404 faceta b) — política de
   * ATUAÇÃO da IA por-cliente. `null` limpa o override (= herda `'engage'`).
   * Molde `nativeSchedulingMode`. `'welcome_only'` NÃO é aceito ainda (reservado
   * — pendente-de-choke, design §3-bis): só `'no_engage' | 'engage' | null`.
   */
  aiEngagementMode?: string | null;
}

// Item 377 (S4 PR-b) — Carteira de Vendedor (customer-portfolio). Espelha
// `PortfolioEntryView` do Engine (customer-portfolio.service.ts).
export interface PortfolioEntryWireItem {
  id: string;
  tenantId: string;
  personId: string;
  personName: string | null;
  operatorUserId: string;
  operatorName: string | null;
  active: boolean;
  version: number;
  assignedAt: string;
  updatedAt: string | null;
}

// W3.3 — wire types Tenant Lifecycle (ADR-111).

export interface TenantLifecycleSnapshotWire {
  id: string;
  name: string;
  slug: string;
  packageType: string;
  status: string;
  version: number;
  onboardingStartedAt: string | null;
  onboardingCompletedAt: string | null;
  activatedAt: string | null;
  paymentOverdueDetectedAt: string | null;
  paymentOverdueGracePeriodEndsAt: string | null;
  suspendedAt: string | null;
  suspendedSource: string | null;
  churningStartedAt: string | null;
  churningCancellableUntil: string | null;
  churningProgressJson: Record<string, unknown> | null;
  churningWorkerLastHeartbeat: string | null;
  churnedAt: string | null;
  archivedAt: string | null;
  churningCancelledAt: string | null;
  createdAt: string;
  updatedAt: string | null;
}

// S6 (Condução Governada, Q-J6/Q-J8) — espelho de `PlaybookTransitionProposalDto`
// (`playbook-transitions.service.ts:32`) e `PlaybookManualSwitchResultDto`
// (`playbook-manual-switch.service.ts:42`). IDs, não nomes — resolução de nome
// honesta fica a cargo do caller via `api.listPlaybooks` (fallback = mostrar o ID).
export type PlaybookTransitionStatusWire =
  | 'proposed'
  | 'executed'
  | 'awaiting_confirmation'
  | 'confirmed'
  | 'rejected'
  | 'blocked';

export interface PlaybookTransitionProposalWire {
  id: string;
  conversationId: string;
  fromPlaybookId: string | null;
  toPlaybookId: string;
  policy: 'automatic_silent' | 'requires_confirmation' | 'blocked';
  status: PlaybookTransitionStatusWire;
  confidence: number;
  reason: string;
  triggeredByAgentId: string;
  triggeredByAgentVersion: string;
  proposedAt: string;
  executedAt: string | null;
  confirmedByUserId: string | null;
  blockReason: string | null;
  metadata: unknown;
}

export interface PlaybookManualSwitchResultWire {
  id: string;
  conversationId: string;
  fromPlaybookId: string | null;
  toPlaybookId: string;
  changedByUserId: string | null;
  changedByAgentId: string | null;
  reason: string;
  autoOrManual: string;
  changedAt: string;
  operationId: string | null;
}

export interface TenantLifecycleHistoryEventWire {
  id: string;
  fromStatus: string;
  toStatus: string;
  transitionedAt: string;
  actorUserId: string | null;
  actorSystem: string | null;
  reason: string | null;
}

// Item 368 F2 Fatia D — Shape real de ProposalForOperator (conduction-proposal.service.ts).
// R15: backend serializa Date como ISO string; confidence = Number(Decimal).
export interface ConductionProposalWire {
  proposalId: string;
  conversationId: string;
  playbookId: string;
  confidence: number;
  summary: string;
  expiresAt: string;
  createdAt: string;
  personDisplayName: string | null;
}

// Item 368 F2 Fatia C — métricas de aceitação por playbook (espelha ProposalMetrics do backend).
export interface ProposalMetricsWire {
  playbookId: string;
  windowDays: number;
  total: number;
  pending: number;
  granted: number;
  declined: number;
  expired: number;
  resolvedCount: number;
  acceptanceRate: number | null;
  band: 'none' | 'insufficient' | 'high' | 'medium' | 'low';
}

/**
 * Alias de re-exportação — Task 9 (UI) importa o tipo de item pelo mesmo
 * módulo `api-client` que já consome (`CannedResponseItem` é a fonte única
 * em `@lume/shared`; NUNCA redefinir, só apelidar).
 */
export type CannedResponseItemWire = CannedResponseItem;

/**
 * Respostas Prontas do Operador (Task 6/8) — mirror de
 * `SerializedCannedResponse` (`canned-responses.serialization.ts`).
 * `items` é `CannedResponseItem[]` do `@lume/shared` (NUNCA redefinir aqui).
 */
export interface CannedResponseWire {
  id: string;
  title: string;
  category: string | null;
  shortcut: string | null;
  items: CannedResponseItem[];
  status: string;
  version: number;
  playbookIds: string[];
  createdAt: string;
  updatedAt: string;
}

/** Biblioteca de mídia do tenant (Task 6/8) — mirror de `SerializedMediaAsset`. */
export interface MediaAssetWire {
  id: string;
  kind: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  status: string;
  version: number;
  createdAt: string;
}

/**
 * Respostas Prontas do Operador — uso na conversa (Task 6, S2, spec
 * 2026-07-16 §3.1). Variáveis camada 1 resolvidas pelo Engine a partir do
 * contexto da conversa; `null` = não-resolvível (o caller decide o fallback).
 */
export interface CannedVariablesWire {
  values: Record<string, string | null>;
}

/**
 * Config do alerta de conduta (S4 supervisor) — `ConductAlertConfig` do
 * `@lume/shared` (fonte única dos 4 campos) + a `version` (If-Match, ADR-071)
 * e `updatedAt` que o serializer do Engine (Task C) acrescenta. NÃO redefinir
 * os 4 campos aqui — só estender o tipo canônico.
 */
export type ConductAlertConfigWire = ConductAlertConfig & {
  version: number;
  updatedAt: string;
};

/** Item de rajada enviado ao Engine — mirror do DTO de `POST .../messages/burst`. */
export interface BurstItemRequestWire {
  itemId: string;
  kind: 'text' | 'media';
  text?: string;
  mediaAssetId?: string;
  caption?: string;
}

/** Resultado por-item da rajada — o Engine reporta o desfecho individual. */
export interface BurstResultItemWire {
  itemId: string;
  index: number;
  status: 'sent' | 'failed' | 'skipped';
  messageId?: string;
  failureReason?: string;
}

/** Envelope do resultado da rajada. */
export interface BurstResultWire {
  burstId: string;
  items: BurstResultItemWire[];
}
